import {
  Application,
  Assets,
  CanvasSource,
  ColorMatrixFilter,
  Container,
  FillGradient,
  Graphics,
  NineSliceSprite,
  Point,
  Sprite,
  Text,
  TextStyle,
  Texture,
  type TextureSource,
  TilingSprite,
} from "pixi.js";

import {
  DIALOG_FRAME_URL,
  SHOWITEM_PHOTO_FRAME_URL,
  SLIDE_MASK_TEXTURE_URL,
} from "../../assets";
import {
  resolveAssetUrl,
  resolveStoryAssetByKey,
  resolveStoryCharacterAssetByKey,
  type StoryAssetFamily,
} from "../asset";
import { DIALOG_FONT_FAMILY, DIALOG_FONT_WEIGHT } from "../font";
import {
  buildTagStyles,
  colorTagName,
  parseRichChars,
  richCharsToTaggedText,
  type RichChar,
  type RichTagStyles,
} from "../richtext";
import { computeShowItemLayout, SHOWITEM_FRAME_BORDER } from "../showitem";
import {
  STORY_HEIGHT,
  STORY_WIDTH,
  type AnimTextInput,
  type AvgDisplayInput,
  type BackgroundInput,
  type BackgroundTweenInput,
  type BlockerInput,
  type CameraShakeInput,
  type CgItemInput,
  type CharacterActionInput,
  type CharacterCutinInput,
  type CharacterSlotInput,
  type CurtainInput,
  type DecisionSelection,
  type DialogueLayout,
  type FocusOutInput,
  type FocusParamInput,
  type GridBackgroundInput,
  type ImageRotateInput,
  type ImageTweenInput,
  type InterludeInput,
  type LargeBackgroundTweenInput,
  type ShowItemFadeStyle,
  type ShowItemInput,
  type SpellStickerInput,
  type StickerInput,
  type StickerTweenInput,
  type StoryRenderer,
  type SubtitleInput,
  type TimerClearInput,
  type TimerStickerInput,
} from "../types";

import { guardClaimedClicks } from "./core/ClickClaim";
import { buildColorEffectMatrix } from "./core/ColorEffectMatrix";
import {
  dotweenDefaultEase,
  dotweenEaseCurve,
  type EaseCurve,
} from "./core/DotweenEase";
import { LayerGraph } from "./core/LayerGraph";
import {
  applyCenteredTransform as applyCenteredTransformToRoot,
  buildGridBackgroundRoot as buildGridRoot,
  readCenteredTransform as readRootTransform,
  rotateTweenDelta,
} from "./core/SceneGeometry";
import { buildShakePath, sampleShakePath } from "./core/ShakePath";
import { SlideMaskFilter } from "./core/SlideMaskFilter";
import { TweenRunner, type TweenRunOptions } from "./core/TweenRunner";
import { AnimTextPanel } from "./panels/AnimTextPanel";
import { AvgDisplayPanel } from "./panels/AvgDisplayPanel";
import { CgItemPanel } from "./panels/CgItemPanel";
import {
  CharacterCutinPanel,
  type CutinCharacterArt,
} from "./panels/CharacterCutinPanel";
import { DecisionPanel } from "./panels/DecisionPanel";
import { DialogPanel } from "./panels/DialogPanel";
import { FocusEffectPanel } from "./panels/FocusEffectPanel";
import { InterludePanel } from "./panels/InterludePanel";
import { SpellStickerPanel } from "./panels/SpellStickerPanel";
import { VideoPanel } from "./panels/VideoPanel";

import type { Context } from "../../context";

function isFiniteNumber(value: number | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * `TweenerOptions.posFrom` / `posTo` / `zoomPos` all default to `Vector2.one`
 * (`_ExecuteCharslot` 0x183e4d752-0x183e4d7ad). The plain-move branch uses
 * posFrom == (1,1) as its "not given" sentinel; the action branches pass the
 * values through verbatim.
 */
const CHARSLOT_POINT_DEFAULT = { x: 1, y: 1 } as const;

interface CharacterSlotPoint {
  x: number;
  y: number;
}

interface CharacterSlotZoomState {
  scaleX: number;
  scaleY: number;
  shiftX: number;
  shiftY: number;
}

/**
 * Which `charslot` branches feed posfrom/posto into `SlotMoveChar`, i.e. the
 * `_offset.localPosition` move. `_UpdateSeqWithParam` (0x183e53940) picks
 * `_GenSlotActionTw` XOR `_GenCharslotMove`: the plain move only runs when
 * `action` is empty and posFrom != (1,1), and `_GenSlotActionTw`
 * (0x183e4f3b0) re-enters SlotMoveChar for `shakemove` and for `jump` when
 * `NeedSkipAnimation(duration)` holds -- without the sentinel gate. `zoom`
 * and `shake` never touch the offset. `move`/`setpos` are Web extensions kept
 * on the plain-move path. Returns null when the command moves nothing.
 */
function characterSlotMove(
  state: { actionX: number; actionY: number },
  input: CharacterSlotInput,
): { from: CharacterSlotPoint; to: CharacterSlotPoint } | null {
  const posTo = input.positionTo ?? CHARSLOT_POINT_DEFAULT;
  switch (input.action) {
    case undefined:
    case "move":
    case "setpos": {
      // posFrom == (1,1) never reaches SlotMoveChar: a bare `posto` is a
      // no-op rather than a relative move.
      if (!input.positionFrom) return null;
      return { from: input.positionFrom, to: posTo };
    }
    case "shakemove": {
      // SlotMoveChar(posFrom, posTo, duration) verbatim, sentinel included.
      return { from: input.positionFrom ?? CHARSLOT_POINT_DEFAULT, to: posTo };
    }
    case "jump": {
      if ((input.durationMs ?? 0) > 0) {
        // CharJump (0x183eb2690): DOLocalJump to `localPosition + posTo`.
        // An omitted posto stays at the (1,1) default -- a bare jump drifts
        // one unit -- while a posto that is present but not an "x,y" pair
        // becomes Vector2.zero (0x183e4e2db-0x183e4e322); the runtime hands
        // that case in as (0,0).
        return {
          from: { x: state.actionX, y: state.actionY },
          to: { x: state.actionX + posTo.x, y: state.actionY + posTo.y },
        };
      }
      // NeedSkipAnimation(duration) => _GenCharslotMove => SlotMoveChar(...,
      // 0) => `localPosition = posTo` outright.
      return { from: input.positionFrom ?? CHARSLOT_POINT_DEFAULT, to: posTo };
    }
    default: {
      return null;
    }
  }
}

/**
 * The zoom half of a `charslot`: CharZoom (0x183eb2b50) tweens the fore
 * Image's `rectTransform.pivot` to `zoomPos` and its `localScale` to `scale`
 * (setter `<CharZoom>b__1` 0x183ed95a0 lerp-drives both from one 0→1 float,
 * Ease.Linear), both absolute -- re-issuing the same zoom lands on the same
 * transform instead of stacking. Returns null when the command is not a zoom
 * or CharZoom would reject the pivot.
 */
function characterSlotZoom(
  state: CharacterRenderState,
  input: CharacterSlotInput,
): { from: CharacterSlotZoomState; to: CharacterSlotZoomState } | null {
  if (input.action !== "zoom") return null;
  // CharZoom validates the pivot first: x/y outside [0,1] => return null,
  // skipping the whole zoom (scale change included). An absent poszoom
  // defaults to (1,1), which is inside the valid range; the Web port keeps
  // treating an absent pivot as (0.5,0.5) ("no pivot move"). Every zoom in
  // the 2.7.61 corpus writes poszoom, so the native (1,1) default is
  // unobservable there.
  const zoom = input.posZoom;
  const pivotValid =
    !zoom || (zoom.x >= 0 && zoom.x <= 1 && zoom.y >= 0 && zoom.y <= 1);
  if (!pivotValid) return null;

  // `CharZoom(zoomPos.x, zoomPos.y, options.scale, duration)` always tweens
  // to `options.scale`, whose GetOrDefault falls back to 1.0 (0x183e4dcea)
  // -- a zoom without `scale` resets it.
  const toScaleX = isFiniteNumber(input.scaleX) ? input.scaleX : 1;
  const toScaleY = isFiniteNumber(input.scaleY) ? input.scaleY : 1;
  // anchoredPosition is not tweened, and every character prefab ships pivot
  // (0.5,0.5) / localScale 1 / sizeDelta = character.json size (verified on
  // avg_npc_2227_1, avg_4225_tanya_1, avg_npc_820_1), so the pivot point
  // stays where the resting rect was centred: the rect grows `scale`x
  // around the fixed point at zoomPos, and its centre lands at
  // `T + scale * (0.5 - zoomPos) * size` (Unity axes). The motion layer
  // instead scales around the rect's top-left corner, so the shift must
  // both move the scaled centre there and cancel that corner-origin growth:
  //   (0.5 - z) * scale * size - (scale - 1) * size / 2
  // (shiftY is stored in Unity's y-up and negated on apply). Tweening the
  // shift linearly makes the endpoints exact and leaves a small mid-tween
  // gap versus native's lerp(scale)*lerp(0.5-pivot) product curve.
  const pivotX = zoom?.x ?? 0.5;
  const pivotY = zoom?.y ?? 0.5;
  return {
    from: {
      scaleX: state.scaleX,
      scaleY: state.scaleY,
      shiftX: state.zoomShiftX,
      shiftY: state.zoomShiftY,
    },
    to: {
      scaleX: toScaleX,
      scaleY: toScaleY,
      shiftX:
        (0.5 - pivotX) * toScaleX * state.width -
        ((toScaleX - 1) * state.width) / 2,
      shiftY:
        (0.5 - pivotY) * toScaleY * state.height +
        ((toScaleY - 1) * state.height) / 2,
    },
  };
}

interface FaceOverlayCacheEntry {
  /** Live visuals drawing this bake; only unreferenced bakes are evicted. */
  refs: number;
  texture: Texture;
}

/**
 * Soft cap on cached face-overlay bakes. Each one is a full base-sized
 * canvas plus its GPU upload (CDN bases run 1024^2-1484^2, ~4-9 MB RGBA), so
 * the cache cannot grow with the number of expressions a story shows. Three
 * slots with an incoming and an outgoing visual each pin at most six.
 */
const FACE_OVERLAY_CACHE_LIMIT = 8;

function faceOverlayCacheKey(baseKey: string, faceKey: string): string {
  return `${baseKey}|${faceKey}`;
}

/** Raw float color channel to the 8-bit tint write (the only clamp point). */
function tintChannel(value: number): number {
  return Math.round(clamp01(value) * 255);
}

/**
 * `_GenPosition`'s per-slot term: it cancels each slot's own offset from the
 * panel centre so a horizontal enter starts at the same absolute x everywhere.
 */
const SLOT_ENTER_COMPENSATION: Record<string, number> = { l: 200, r: -200 };

/**
 * DOTween `Ease.OutCubic`, used by `AVGCharacterSlot.SetCharPos` and
 * `AVGCharacterSlot.MoveChar` (both `DOLocalMove(...).SetEase(Ease.OutCubic)`;
 * 2.7.81 MoveChar @ 0x184029e30, SetCharPos @ 0x18402a1e0), and the sequence
 * ease `AVGCharacterSlot.CharJump` chains on DOLocalJump. `easeOutQuad` below
 * is DOLocalJump's arc and y-displacement ease (2.7.81 DOLocalJump @
 * 0x184257a20; same formula as `dotweenDefaultEase` in core/DotweenEase).
 */
function easeOutCubic(progress: number): number {
  const remaining = 1 - progress;
  return 1 - remaining * remaining * remaining;
}

function easeOutQuad(progress: number): number {
  return 1 - (1 - progress) * (1 - progress);
}

/**
 * Native `Torappu.AVG.AVGTypeWriterText.BeginText` hides the unrevealed tail
 * behind a fully transparent `<color=#00000000>` span so the whole message is
 * laid out (and wrapped) from t0 -- revealing characters never re-wraps the
 * lines already on screen.
 */
const SUBTITLE_HIDDEN_TAIL_COLOR = "#00000000";
const SUBTITLE_HIDDEN_TAIL_TAG = colorTagName(SUBTITLE_HIDDEN_TAIL_COLOR);

/**
 * Web-only counterpart to the transparent tail: PIXI's tagged-text drop shadow
 * pass forces an opaque fill (`CanvasTextGenerator._setupDropShadow`) and never
 * reads the run's own fill, so an `alpha = 0` run still casts the style's
 * shadow -- the "hidden" tail would read as legible grey ghost text. Unity
 * modulates its shadow by vertex alpha instead, so native has nothing to turn
 * off here. Disabling the shadow per tag hits PIXI's own skip branch and leaves
 * the measured layout byte-identical.
 */
const SUBTITLE_HIDDEN_TAIL_STYLE = {
  dropShadow: false,
  fill: SUBTITLE_HIDDEN_TAIL_COLOR,
} as const;

// Keeps each char's bold/italic so the hidden tail wraps like the revealed
// text will: native appends the unrevealed rest with its own tags.
function subtitleHiddenTail(chars: RichChar[]): RichChar[] {
  return chars.map((char) => ({ ...char, color: SUBTITLE_HIDDEN_TAIL_COLOR }));
}

/**
 * Canvas 渲染分辨率：逻辑坐标系固定 1280x720，实际像素数按宿主 CSS 尺寸
 * × devicePixelRatio 反推，避免画布被 CSS 拉伸发糊。取宽高比例的较大值，
 * 宿主短暂偏离 16:9 时也不会欠采样。
 */
function computeDisplayResolution(host: HTMLElement): number {
  const rect = host.getBoundingClientRect();
  const cssWidth = rect.width || STORY_WIDTH;
  const cssHeight = rect.height || STORY_HEIGHT;
  const devicePixelRatio = window.devicePixelRatio || 1;
  return Math.max(
    (cssWidth / STORY_WIDTH) * devicePixelRatio,
    (cssHeight / STORY_HEIGHT) * devicePixelRatio,
  );
}

/** Width of AVGCurtain's `_gradientImg` feather strip, in logical pixels. */
const CURTAIN_GRADIENT_PX = 20;

const CURTAIN_STAGE_CORNERS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 0, y: 0 },
  { x: STORY_WIDTH, y: 0 },
  { x: STORY_WIDTH, y: STORY_HEIGHT },
  { x: 0, y: STORY_HEIGHT },
];

/**
 * Native corner curtains (direction 1/3/5/7): `_GenSizeDeltaWithMultiplior`
 * scales only the local Y axis of a 1600x1600 AVGCurtain rect, but each
 * corner rect carries a 45° `m_LocalRotation` (z = ±0.3827) around a pivot
 * that sits off-stage past its corner. The inner edge therefore sweeps along
 * the same 45° normal `resolveCurtainVector` returns, at projection
 * `hinge · vector + 1600 * fill` -- a diagonal wipe, offset by the hinge.
 * Hinges are the `panel_curtains` pivots (sharedassets1, 2.7.81) in stage
 * space (y down). They sit 98.8-118.3px past the nearest stage corner, so
 * fills under ~0.062-0.074 never reach the stage, and the stage is fully
 * covered from ~0.946-0.958. The ±800px lateral span covers the whole stage
 * for every corner, so only the inner edge clips.
 */
const CURTAIN_CORNER_SIDE = 1600;
const CURTAIN_CORNER_HINGES: Readonly<
  Record<number, { x: number; y: number }>
> = {
  1: { x: 1204.69, y: -240.69 },
  3: { x: 1201.49, y: 938.18 },
  5: { x: 70.11, y: 938.18 },
  7: { x: 73.31, y: -240.69 },
};

/**
 * 对话框顶部/底部黑条用到的同一张渐变纹理（APK 中 `sprite_avg_cutscene`，
 * 4×310 RGBA，自上而下 alpha 0→1）。放在 `public/` 下由 Vite 直接服务，
 * 预加载由 preload.ts 把该 URL 加入预加载集来预热 pixi Assets 缓存。
 */

interface CharacterBuiltVisual {
  sourceHeight: number;
  sourceWidth: number;
  visual: Container;
}

/**
 * One accumulated SequenceBuilder.Push step of a sticker tween, in native
 * push order (position, rotation, alpha). `join` and the per-kind payload
 * mirror Torappu.AVG's AnimStep { spec, join, seqEnd } composition.
 */
type StickerTweenStepInput =
  | {
      durationMs: number;
      from: { x: number; y: number };
      join: boolean;
      kind: "position";
      to: { x: number; y: number };
    }
  | {
      durationMs: number;
      from: number;
      join: boolean;
      kind: "alpha";
      to: number;
    }
  | {
      durationMs: number;
      from: number;
      join: boolean;
      kind: "rotation";
      to: number;
    };

interface CharacterRenderState {
  actionX: number;
  actionY: number;
  baseScaleX: number;
  baseScaleY: number;
  baseX: number;
  baseY: number;
  characterKey?: string;
  contentAlpha: number;
  focusBrightness: number;
  expression?: string;
  fadeIdentity?: string;
  height: number;
  /**
   * The previous visual while it cross-fades out. Native keeps exactly one
   * back Image: the next swap replaces it outright, so a swap during a
   * crossfade discards this one instead of leaving it half faded.
   */
  outgoingVisual: Container | null;
  jumpOffsetY: number;
  jumpSessionId: number;
  motionLayer: Container;
  /**
   * `AVGCharacterSlot.m_currentKey`: the raw `name` ref the slot was last set
   * with, index and alias included. `Set` compares it verbatim, so it cannot
   * be reconstructed from the resolved characterKey/expression pair.
   */
  nativeKey?: string;
  opacitySessionId: number;
  replaceFadeSessionId: number;
  root: Container;
  rotationDeg: number;
  rotationLayer: Container;
  rotateSessionId: number;
  rotateTimeout: ReturnType<typeof setTimeout> | null;
  scaleX: number;
  scaleY: number;
  shakeOffsetX: number;
  shakeOffsetY: number;
  shakeSessionId: number;
  slot: string;
  sourceHeight: number;
  sourceWidth: number;
  transformSessionId: number;
  visual: Container;
  width: number;
  zoomSessionId: number;
  /**
   * CharZoom's pivot move, as a translation in stage pixels. Kept apart from
   * `actionX/Y` (the `_offset` position) because native stores it on the
   * fore Image, which every successful image load resets.
   */
  zoomShiftX: number;
  zoomShiftY: number;
}

interface CurtainRenderState {
  alpha: number;
  direction: number;
  fill: number;
  grad: boolean;
  graphic: Graphics;
  tweenSessionId: number;
}

/** A loaded `image_item` sprite at its native size (`SetNativeSize`). */
function createShowItemSprite(texture: Texture): Sprite {
  const sprite = new Sprite(texture);
  sprite.anchor.set(0.5);
  return sprite;
}

/**
 * `image_item` after a failed load: `AVGShowItemSlot.Show` skips both the
 * sprite assignment and `SetNativeSize`, and a Unity Image without a sprite
 * still renders its white color as a solid quad at the serialized 100x100.
 */
function createShowItemPlaceholder(): Graphics {
  return new Graphics().rect(-50, -50, 100, 100).fill(0xff_ff_ff);
}

/**
 * Live state of the cutin show-item slot: the RectMask2D window (`offset` →
 * `mask`, targetW x 720 in canvas units) and the plate/header that stretch
 * inside it. `maskW`/`maskH` are the live window size the expand styles
 * tween in both directions.
 */
interface ShowItemCutinState {
  /** Full-canvas black backdrop; the expand styles tween its alpha. */
  backdrop: Graphics;
  /** Slot center in root space; the mask rect is drawn around it. */
  cx: number;
  cy: number;
  /** `_showFadeStyle` as stored by the cutin Show and reused by Hide. */
  fadeStyle: ShowItemFadeStyle;
  /**
   * `header` strip (`sprite_avg_cutscene` tinted black at 0.706): a 200px
   * child of the plate pinned to the window's top edge, rotated 180°, so the
   * window clips its top 50px and shows 150px fading downward.
   */
  header: Sprite | null;
  /** Mask window graphics; siblings with the slot container, root coords. */
  mask: Graphics;
  maskH: number;
  maskW: number;
  /** `background` plate (0.192 gray) stretched to the live window. */
  plate: Graphics;
  targetH: number;
  targetW: number;
}

/**
 * Web/PIXI renderer for the AVG command surfaces. Each command method ports
 * the documented observable state and blocking boundaries, while containers,
 * filters, and browser timing are adaptations rather than a Unity scene port.
 * Command-specific native provenance is recorded at the behavior boundaries.
 */
export class PixiStoryRenderer implements StoryRenderer {
  private app: Application | null = null;
  private releaseClickGuard: (() => void) | null = null;
  private resizeHost: HTMLElement | null = null;
  private resizeListener: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private readonly layers = new LayerGraph();
  private readonly backgroundLayer = this.layers.background;
  private backgroundRoot: Container | null = null;
  /** Current background visual; exposed for renderer diagnostics and tests. */
  backgroundSprite: Sprite | TilingSprite | null = null;
  private backgroundTweenSessionId = 0;
  private readonly context: Context;
  private readonly charLayer = this.layers.characters;
  private readonly characterSlots = new Map<string, CharacterRenderState>();
  /**
   * 每槽缓存 `DOTween.Sequence` 的完成时刻（`performance.now()` 毫秒）。
   * `_GetCachedSlotSeq`（0x183e4f830）在 Sequence 未被 `end` 挂的
   * OnComplete 标记 played 前一直复用同一条，而 `_UpdateSeqWithTween`
   * （0x183e53fc0）把每条 charslot tween `Insert` 到各自 `delay` 上——
   * 并行、从不 Append——所以 Sequence 在"全部已插入 tween 的最大结束
   * 时刻"完成。`isblock` 等的就是这一刻（OnComplete → FinishCommand，
   * 0x183e4e501）：紧跟 `end=false` 入场之后的 shake 要等两者同时结束，
   * 但各自仍从自己命令的发出时刻起播。
   *
   * 复用只在同一帧内成立：`TweenManager.Update(Tween, …)`（0x1841495c0）
   * 在 tween 首次更新时置 `creationLocked`，此后 `Insert`（0x18411da10）
   * 只打 `LogAddToLockedSequence` 就返回；播完的 Sequence 又因
   * `defaultAutoKill`（cctor 0x18410b0e0）失活，而 `end=false` 的从不被标
   * played，`_GetCachedSlotSeq` 会继续交出这条死 Sequence，Insert 与
   * OnComplete 都成 no-op。AVG 的 `_DoExecuteCommands`（0x183e2b9c0）把
   * 连续非阻塞命令在同一次 MoveNext 里跑完，所以相邻命令共享 Sequence；
   * 隔了对白或阻塞命令之后，native 只等旧 Sequence 的剩余时间。全语料
   * `end=false` 后接同槽 isblock 的只有相邻两处（act23side_02_beg 425→426、
   * 555→556），这里按 max(旧 deadline, 自身 duration) 近似，不建模上锁。
   */
  private readonly characterSlotSeqDeadlines = new Map<string, number>();
  private readonly faceOverlayTextures = new Map<
    string,
    FaceOverlayCacheEntry
  >();
  /** Which cached bake each character visual draws, for refcounting. */
  private readonly faceOverlayVisualKeys = new WeakMap<Container, string>();
  private readonly cutinLayer = this.layers.cutins;
  private readonly cutinPanel: CharacterCutinPanel;

  private readonly curtainLayer = this.layers.curtains;
  // Skip semantics: 2.7.61's AVGCurtainPanel.ShouldResetOnSkip returns false
  // (0x183e54960, literal `xor al, al`), so skipping a segment must NOT clear
  // curtains -- they only go away via curtain commands or full destroy().
  private readonly curtains = new Map<number, CurtainRenderState>();
  private readonly gridBackgroundLayer = this.layers.gridBackground;
  private gridBackgroundSessionId = 0;
  private largeBackgroundRoot: Container | null = null;
  private largeBackgroundTweenSessionId = 0;
  private readonly imageLayer = this.layers.images;
  private largeImageRoot: Container | null = null;
  private readonly largeImageRoots = new Set<Container>();
  private largeImageSessionId = 0;
  private largeImageTweenSessionId = 0;
  private imageRoot: Container | null = null;
  private imageRotateSessionId = 0;
  private readonly itemLayer = this.layers.items;
  /**
   * Native port: the renderer-side handle on `Torappu.AVG.AVGShowItemPanel`'s
   * single `_slotInUse` gameObject. Keeping an explicit reference (instead of
   * fading every child of itemLayer) matters because the web AnimTextPanel
   * mounts its stamps on the same layer while native animtext is a separate
   * panel that hideitem never touches.
   */
  private showItemRoot: Container | null = null;
  /**
   * Live cutin slot handle (mask window + stretch plate/header), mirroring
   * the `_showFadeStyle` field and the mask subtree its Show/Hide animate.
   * Null while a photo slot (or nothing) is on stage.
   */
  private showItemCutin: ShowItemCutinState | null = null;
  /**
   * Bundled nine-slice texture of the serialized `sprite_avg_showItem_photo`
   * photo frame (native scene asset; CDN and the PRTS mirror do not carry
   * it). Cached once like the blocker slide mask; on load failure the item
   * renders without the frame instead of blocking playback.
   */
  private showItemFrameTexture: Texture | null = null;
  private showItemFrameTextureFailed = false;
  /**
   * Dialog gradient texture, shared with DialogPanel and reused by the cutin
   * slot's `header` strip (`sprite_avg_cutscene` tinted black at 0.706).
   */
  private dialogFrameTexture: Texture | null = null;
  private dialogFrameTextureFailed = false;
  private readonly onWarning?: (detail: string) => void;
  private readonly sceneLayer = this.layers.scene;
  private readonly uiLayer = this.layers.ui;
  private readonly worldLayer = this.layers.world;
  private readonly tweenRunner = new TweenRunner(() => Boolean(this.app));
  private readonly videoPanel: VideoPanel;
  private readonly dialogPanel: DialogPanel;
  private readonly decisionPanel: DecisionPanel;
  private readonly interludePanel: InterludePanel;
  private readonly animTextPanel: AnimTextPanel;
  private readonly avgDisplayPanel: AvgDisplayPanel;
  private readonly spellStickerPanel: SpellStickerPanel;
  private readonly focusEffectPanel: FocusEffectPanel;
  private readonly cgItemPanel: CgItemPanel;

  private blockerSprite: Sprite | null = null;
  /**
   * Raw float mirror of the native `_blocker.color` (prefab initial
   * (0,0,0,0)). Unity `Color` channels are float32 and may exceed 1 -- scripts
   * write 0-255 endpoints (r=255 white flashes, 7.5k+ occurrences) whose
   * mid-tween values saturate only at the GPU. Readback and interpolation stay
   * in raw scale; only the 8-bit tint write clamps.
   */
  private blockerColor: { a: number; b: number; g: number; r: number } = {
    a: 0,
    b: 0,
    g: 0,
    r: 0,
  };
  private blockerTweenSessionId = 0;
  /**
   * localScale sign persistence for the slider wipe (native `_ExecuteBlocker`
   * assigns literal -1 to the flipped axis; `inverse = false` never resets it,
   * and only OnReset/destroy restores +1). The sign is tracked separately from
   * `sprite.scale` because every `blocker.width`/`height` write recomputes the
   * magnitude from the new texture, so the sign is the only part worth owning;
   * the visible mirror is the shader's (SlideMaskFilter uFlip), since the quad
   * itself is untextured white.
   */
  private blockerScaleSign = { x: 1, y: 1 };
  /**
   * `_blocker.material` equivalent: non-null only while the slide wipe is
   * mounted, so `writeBlockerColor` knows whether alpha belongs to the shader
   * or to `sprite.alpha`.
   */
  private blockerSlideFilter: SlideMaskFilter | null = null;
  /**
   * The filter instance itself, mirroring the single `slide_mask` Material
   * `_SetMaterial` re-fetches from the asset loader cache (2.7.61 VA
   * 0x183e30b60): mounting and unmounting reuses it rather than rebuilding.
   */
  private blockerSlideMaterial: SlideMaskFilter | null = null;
  private blockerMaskSource: TextureSource | null = null;
  private blockerMaskSourceFailed = false;
  private cameraShakeSessionId = 0;
  private cameraShakeWaitResolve: (() => void) | null = null;
  private grayscaleAmount = 0;
  private inverseAmount = 0;
  private grayscaleFilter: ColorMatrixFilter | null = null;
  private readonly stickerFadeSessionIds = new Map<string, number>();
  private readonly stickerTexts = new Map<string, Text>();
  private readonly stickerTypingTargets = new Map<
    string,
    {
      alignment: StickerInput["alignment"];
      baseX: number;
      fullText: string;
      widthPx: number;
    }
  >();
  private readonly stickerTypingSessionIds = new Map<string, number>();
  /**
   * Pending sticker-tween steps per id. Native: SequenceBuilder accumulates
   * Push steps until a seqEnd=true step calls BuildSequence (see stickerTween).
   */
  private readonly stickerTweenQueues = new Map<
    string,
    StickerTweenStepInput[]
  >();
  private readonly stickerTweenSessionIds = new Map<string, number>();
  private subtitleFadeSessionId = 0;
  /**
   * Native port: `SubtitlePanel._SetHiddenInternal`'s `m_hidden`. Flips the
   * moment `set_isHidden` is called, while the alpha tween catches up. A
   * `setSubtitle` on an already-shown panel must not replay the fade-in.
   */
  private subtitleHidden = true;
  private subtitleText: Text | null = null;
  private subtitleTypingTarget: {
    alignment: SubtitleInput["alignment"];
    baseX: number;
    fullText: string;
    widthPx: number;
  } | null = null;
  private subtitleTypingSessionId = 0;
  private readonly stickerRichChars = new Map<string, RichChar[]>();
  private timerFadeSessionId = 0;
  /**
   * Mirrors `AVGTimerView.m_countTimerTask != null`. It gates the inline
   * `_TimerTick(0)` that `_StartCountTimer` only fires when it has to build a
   * task, and it survives a `StopTimer` fade -- native nulls the field from
   * that fade's OnComplete, not when the fade starts.
   */
  private timerTaskActive = false;
  /**
   * The pending `StopTimer` OnComplete (`<StopTimer>b__7_0`, VA 0x183ed54f0).
   * `RenderTimer`'s `DOKill(_canvas, complete: true)` runs it; `StopTimer`'s own
   * `DOKill(_canvas, complete: false)` discards it.
   */
  private timerStopFadeComplete: (() => void) | null = null;
  private timerStickerInterval: ReturnType<typeof setInterval> | null = null;
  private timerStickerText: Text | null = null;

  constructor(context: Context, onWarning?: (detail: string) => void) {
    this.context = context;
    this.onWarning = onWarning;
    // Native port: `AVGImagePanel` executors transform the panel's own
    // RectTransform (the scene `panel_image` node), whose pivot is serialized
    // at (0.5, 0.5) — the panel center, i.e. the 1280x720 screen center.
    // Anchor `imageLayer` the same way so `imagerotate` tilts the picture in
    // place instead of orbiting the stage origin. `pivot == position` keeps
    // the child-space translation identity, so sprites keep their
    // (STORY_WIDTH/2, STORY_HEIGHT/2) placement semantics.
    this.imageLayer.pivot.set(STORY_WIDTH / 2, STORY_HEIGHT / 2);
    this.imageLayer.position.set(STORY_WIDTH / 2, STORY_HEIGHT / 2);
    this.videoPanel = new VideoPanel(this.uiLayer, onWarning);
    this.dialogPanel = new DialogPanel(
      this.uiLayer,
      onWarning,
      (durationMs, update, complete) =>
        this.tween(durationMs, update, complete),
    );
    this.decisionPanel = new DecisionPanel(this.uiLayer);
    this.interludePanel = new InterludePanel(
      this.cutinLayer,
      (input) => this.textureForInterlude(input),
      (durationMs, update, complete) =>
        this.tween(durationMs, update, complete),
      onWarning,
    );
    this.cutinPanel = new CharacterCutinPanel(
      this.cutinLayer,
      (input) => this.textureForCutinCharacter(input),
      (durationMs, update, complete) =>
        this.tween(durationMs, update, complete),
    );
    this.animTextPanel = new AnimTextPanel(
      this.itemLayer,
      (durationMs, update, complete) =>
        this.tween(durationMs, update, complete),
      onWarning,
    );
    this.avgDisplayPanel = new AvgDisplayPanel(
      {
        bgover: this.layers.avgDisplayBackground,
        cgover: this.layers.avgDisplayCg,
        charover: this.layers.avgDisplayCharacter,
      },
      (name) => this.textureForImageKey(name, "background"),
      (durationMs, update, complete) =>
        this.tween(durationMs, update, complete),
      onWarning,
    );
    this.spellStickerPanel = new SpellStickerPanel(this.uiLayer, onWarning);
    this.focusEffectPanel = new FocusEffectPanel(
      (type, id) => this.resolveFocusTargets(type, id),
      (durationMs, update, complete) =>
        this.tween(durationMs, update, complete),
    );
    this.cgItemPanel = new CgItemPanel(
      this.layers.cgItems,
      (key) => this.textureForImageKey(key, "image"),
      (duration, update, complete) => this.tween(duration, update, complete),
      onWarning,
    );
  }

  async mount(host: HTMLElement): Promise<void> {
    if (this.app) return;

    const app = new Application();
    await app.init({
      antialias: true,
      autoDensity: true,
      background: "#000000",
      height: STORY_HEIGHT,
      preference: "webgpu",
      resolution: computeDisplayResolution(host),
      width: STORY_WIDTH,
    });

    host.innerHTML = "";
    host.style.position = "relative";
    app.canvas.style.width = "100%";
    app.canvas.style.height = "100%";
    app.canvas.style.display = "block";
    host.append(app.canvas);
    this.videoPanel.mount(host);

    this.app = app;
    // Decision options claim their own press (see ClickClaim): only a click
    // pressed on no interactive canvas object reaches the host's advance.
    this.releaseClickGuard = guardClaimedClicks(
      app.canvas,
      (clientX, clientY) => {
        const events = app.renderer.events;
        const point = new Point();
        events.mapPositionToPoint(point, clientX, clientY);
        return Boolean(events.rootBoundary.hitTest(point.x, point.y));
      },
    );
    this.watchDisplayResolution(host);

    await this.createUi();
    this.layers.attach(app.stage);
  }

  /**
   * 跟随宿主尺寸同步画布分辨率。ResizeObserver 覆盖布局变化（窗口缩放、
   * 全屏切换）；window resize 兜底 devicePixelRatio 变化（页面缩放、跨屏
   * 拖动），这类变化不一定伴随宿主尺寸变化。
   */
  private watchDisplayResolution(host: HTMLElement): void {
    this.resizeHost = host;

    const listener = () => this.syncDisplayResolution();
    window.addEventListener("resize", listener);
    this.resizeListener = () => window.removeEventListener("resize", listener);

    if (typeof ResizeObserver === "undefined") return;
    this.resizeObserver = new ResizeObserver(listener);
    this.resizeObserver.observe(host);
  }

  private syncDisplayResolution(): void {
    const app = this.app;
    const host = this.resizeHost;
    if (!app || !host) return;

    const resolution = computeDisplayResolution(host);
    if (Math.abs(app.renderer.resolution - resolution) < 0.01) return;
    app.renderer.resize(STORY_WIDTH, STORY_HEIGHT, resolution);
    // autoDensity 在 resize 时会把 style 写回 1280x720 逻辑像素，恢复撑满宿主。
    app.canvas.style.width = "100%";
    app.canvas.style.height = "100%";
  }

  destroy(): void {
    this.stopVideo();
    this.stopCameraShake();
    this.decisionPanel.destroy();
    this.interludePanel.destroy();
    this.animTextPanel.destroy();
    this.avgDisplayPanel.destroy();
    this.spellStickerPanel.destroy();
    this.focusEffectPanel.destroy();
    this.cgItemPanel.destroy();
    this.backgroundRoot = null;
    this.backgroundSprite = null;
    this.backgroundTweenSessionId += 1;
    this.backgroundLayer.removeChildren();
    // panel_large_background is a permanent scene-level sibling in front of
    // panel_background (LayerGraph.attach), so the grid layer keeps its parent
    // and only its tile roots need clearing here.
    this.gridBackgroundLayer.removeChildren();
    this.blockerSprite = null;
    // Blocker's closest OnReset equivalent on destroy: drop in-flight tween
    // callbacks and restore the prefab color (0,0,0,0). OnReset also clears
    // the slide_mask material and restores localScale = one.
    this.blockerTweenSessionId += 1;
    this.blockerScaleSign = { x: 1, y: 1 };
    this.blockerSlideFilter = null;
    this.blockerSlideMaterial = null;
    this.blockerColor = { a: 0, b: 0, g: 0, r: 0 };
    this.gridBackgroundSessionId += 1;
    this.largeBackgroundRoot = null;
    this.largeBackgroundTweenSessionId += 1;
    this.largeImageRoot = null;
    this.largeImageSessionId += 1;
    this.largeImageTweenSessionId += 1;
    this.largeImageRoots.clear();
    this.imageRoot = null;
    this.imageLayer.removeChildren();
    this.itemLayer.removeChildren();
    this.showItemRoot = null;
    this.showItemCutin = null;
    this.cutinPanel.destroy();
    for (const state of this.characterSlots.values())
      this.disposeCharacterState(state);
    this.characterSlots.clear();
    this.characterSlotSeqDeadlines.clear();
    for (const entry of this.faceOverlayTextures.values())
      entry.texture.destroy(true);
    this.faceOverlayTextures.clear();
    for (const state of this.curtains.values()) this.disposeCurtainState(state);
    this.curtains.clear();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.resizeListener?.();
    this.resizeListener = null;
    this.resizeHost = null;
    this.releaseClickGuard?.();
    this.releaseClickGuard = null;
    this.app?.destroy(true, { children: true });
    this.app = null;
    this.dialogPanel.destroy();
    this.subtitleText = null;
    this.timerStickerText = null;
    this.clearTimerInterval();
    this.timerFadeSessionId += 1;
    this.timerStopFadeComplete = null;
    this.timerTaskActive = false;
    this.stickerTexts.clear();
    this.stickerFadeSessionIds.clear();
    this.stickerTypingTargets.clear();
    this.stickerTypingSessionIds.clear();
    this.stickerTweenQueues.clear();
    this.stickerTweenSessionIds.clear();
    this.subtitleFadeSessionId += 1;
    this.subtitleHidden = true;
    this.subtitleTypingTarget = null;
    this.subtitleTypingSessionId += 1;
    this.stickerRichChars.clear();
    this.videoPanel.destroy();
  }

  async playVideo(url: string): Promise<void> {
    await this.videoPanel.play(url);
  }

  async setAnimText(input: AnimTextInput): Promise<void> {
    await this.animTextPanel.show(input);
  }

  async setAvgDisplay(input: AvgDisplayInput): Promise<void> {
    await this.avgDisplayPanel.display(input);
  }

  clearAvgDisplays(): void {
    this.avgDisplayPanel.clear();
  }

  clearAnimTexts(): void {
    this.animTextPanel.clear();
  }

  stopVideo(): void {
    this.videoPanel.stop();
  }

  finishTextTyping(): boolean {
    if (this.subtitleText && this.subtitleTypingTarget) {
      this.subtitleTypingSessionId += 1;
      this.subtitleText.text = this.subtitleTypingTarget.fullText;
      this.layoutSubtitle(
        this.subtitleText,
        this.subtitleTypingTarget.baseX,
        this.subtitleTypingTarget.widthPx,
        this.subtitleTypingTarget.alignment,
      );
      this.subtitleTypingTarget = null;
      return true;
    }

    for (const [id, target] of this.stickerTypingTargets.entries()) {
      const sticker = this.stickerTexts.get(id);
      if (!sticker) continue;
      this.stickerTypingSessionIds.set(
        id,
        (this.stickerTypingSessionIds.get(id) ?? 0) + 1,
      );
      sticker.text = target.fullText;
      this.layoutSubtitle(
        sticker,
        target.baseX,
        target.widthPx,
        target.alignment,
      );
      this.stickerTypingTargets.delete(id);
      return true;
    }

    return false;
  }

  setDialogue(
    speaker: string,
    text: string,
    tagStyles?: RichTagStyles,
    layout?: DialogueLayout,
  ): void {
    this.dialogPanel.setDialogue(speaker, text, tagStyles, layout);
  }

  hideDialogue(): void {
    this.dialogPanel.hide();
  }

  finishDialogueCommand(): void {
    this.dialogPanel.restoreMessageWidth();
  }

  async showDecision(
    options: string[],
    values: number[],
    disabled?: boolean[],
  ): Promise<DecisionSelection> {
    return this.decisionPanel.show(options, values, disabled);
  }

  /**
   * 按「未点击」结算挂起中的 decision，让 runtime 挂在 showDecision 上的
   * 命令循环得以继续。Web 适配：native skip 只清空 m_blockingExecutors 放行
   * 命令循环，`DecisionPanel.ForceCommandEnd`（2.7.81 VA 0x183FE0410）是空
   * 函数，面板本身留在屏幕上（见 runtime.skipNode）。
   */
  settleDecision(): void {
    this.decisionPanel.clear();
  }

  /**
   * Port scope: `Torappu.AVG.AVGImagePanel._ExecuteImage` / `_LoadImage` for
   * the `background` path: replacement, initial transform, cross-fade, and
   * block boundary. PIXI roots replace the two Unity Image widgets.
   */
  async setBackground(key: string, input?: BackgroundInput): Promise<void> {
    const texture = await this.textureForImageKey(key, "background");
    if (!texture) {
      // Native `_LoadImage` on a failed sprite load logs "Failed to load
      // image" and falls into the clear branch: DOFade(_backImage -> 0) with
      // the same scaled duration and block gate, so the old background fades
      // out instead of staying visible.
      await this.clearBackground(input?.fadeMs ?? 0, input?.block ?? false);
      return;
    }

    // Simplification vs native: `_ExecuteImage` only DOKills the outgoing
    // image's transform tween when the cross-fade finishes (OnKill ->
    // `_ResetImage(_backImage)`), so an in-flight backgroundtween keeps
    // animating the old image during the fade. Bumping the session id here
    // freezes that tween one fade early; accepted deviation because both
    // endpoints (new image cross-faded in, old root removed) still agree.
    this.backgroundTweenSessionId += 1;
    const root = new Container();
    // `_LoadImage`: TryGetParam("tiled", false) switches Image.type between
    // Tiled and Simple, tiling the sprite inside the final sizeDelta rect; a
    // repeat-addressed TilingSprite is the PIXI equivalent. The address mode
    // is set on the Assets-cached texture, which is harmless for the 0..1-UV
    // simple sprites sharing it.
    let sprite: Sprite | TilingSprite;
    if (input?.tiled) {
      texture.source.style.addressMode = "repeat";
      sprite = new TilingSprite({ texture });
    } else {
      sprite = new Sprite(texture);
    }
    sprite.anchor.set(0.5);
    const nativeRect = this.nativeBackgroundRect(key, texture);
    this.layoutImageForScreenAdapt(
      sprite,
      input?.screenAdapt,
      {
        height: input?.height ?? 1,
        width: input?.width ?? 1,
      },
      nativeRect,
    );
    if (sprite instanceof TilingSprite) {
      // Image.type = Tiled repeats the sprite at its native (ppu-scaled) size
      // inside the final sizeDelta rect, so each tile spans nativeRect while
      // the TilingSprite's own width/height stay at that final rect.
      sprite.tileScale.set(
        nativeRect[0] / Math.max(1, texture.width),
        nativeRect[1] / Math.max(1, texture.height),
      );
      this.alignTilesToLowerLeft(sprite);
    }
    // `_ExecuteImage` reads `xScale`/`yScale` with a 1.0 fallback, so an
    // omitted scale must leave the screen-adapted size alone.
    this.applyCenteredTransform(root, {
      scaleX: input?.scaleX ?? 1,
      scaleY: input?.scaleY ?? 1,
      x: input?.x ?? 0,
      y: input?.y ?? 0,
    });
    root.addChild(sprite);

    const previous = this.backgroundRoot;
    this.backgroundRoot = root;
    this.backgroundSprite = sprite;
    this.backgroundLayer.addChild(root);
    const fadeMs = input?.fadeMs ?? 0;
    root.alpha = fadeMs > 0 ? 0 : 1;
    if (fadeMs <= 0) {
      previous?.removeFromParent();
      return;
    }
    const run = this.tween(
      fadeMs,
      (progress) => (root.alpha = progress),
      () => previous?.removeFromParent(),
    );
    if (input?.block) await run;
    else void run;
  }

  async clearBackground(fadeMs = 0, block = false): Promise<void> {
    this.backgroundTweenSessionId += 1;
    const root = this.backgroundRoot;
    this.backgroundRoot = null;
    this.backgroundSprite = null;
    if (!root) return;
    if (fadeMs <= 0) {
      root.removeFromParent();
      return;
    }
    const startAlpha = root.alpha;
    const run = this.tween(
      fadeMs,
      (progress) => (root.alpha = startAlpha * (1 - progress)),
      () => root.removeFromParent(),
    );
    if (block) await run;
    else void run;
  }

  /**
   * Port of `Torappu.AVG.AVGImagePanel._ExecuteImageTween`'s foreground
   * transform semantics (2.7.71 VA 0x183f055b0): From/To defaults read the
   * current transform, `duration <= 0` snaps to To, and the move + scale
   * DOTween pair shares one ease and loop count, so a single merged
   * interpolation is frame-equivalent. `ease` resolves the command's
   * GetEnum<Ease> value and `loop` maps SetLoops(2*!loop-1) to an infinite
   * Restart loop that replays from the From pose each cycle and never
   * completes (block is dropped upstream for loops). Browser interpolation
   * replaces the DOTween sequence.
   *
   * The zero-duration snap holds for loops too, though not via Complete:
   * `TweenManager.Complete` @ 0x1841f4830 refuses `loops == -1`, but
   * `Tweener.DoStartup` @ 0x184893ce0 forces `Ease.INTERNAL_Zero` on any
   * `duration <= 0` tween, so every update evaluates to the To pose.
   */
  async setBackgroundTween(input: BackgroundTweenInput): Promise<void> {
    // Native's `_foreImage` is a serialized panel field that always exists,
    // so the tween (and a blocking one's full-duration wait) would run even
    // with no background loaded; real scripts always precede this command
    // with `background`, so returning early here is a Web adaptation.
    const root = this.backgroundRoot;
    if (!root || root.parent !== this.backgroundLayer) return;

    const current = this.readCenteredTransform(root);
    const from = {
      scaleX: isFiniteNumber(input.xScaleFrom)
        ? input.xScaleFrom
        : current.scaleX,
      scaleY: isFiniteNumber(input.yScaleFrom)
        ? input.yScaleFrom
        : current.scaleY,
      x: isFiniteNumber(input.xFrom) ? input.xFrom : current.x,
      y: isFiniteNumber(input.yFrom) ? input.yFrom : current.y,
    };
    const to = {
      scaleX: isFiniteNumber(input.xScaleTo) ? input.xScaleTo : current.scaleX,
      scaleY: isFiniteNumber(input.yScaleTo) ? input.yScaleTo : current.scaleY,
      x: isFiniteNumber(input.xTo) ? input.xTo : current.x,
      y: isFiniteNumber(input.yTo) ? input.yTo : current.y,
    };

    const sessionId = ++this.backgroundTweenSessionId;
    this.applyCenteredTransform(root, from);

    if (input.durationMs <= 0) {
      this.applyCenteredTransform(root, to);
      return;
    }

    const run = this.tween(
      input.durationMs,
      (progress) => {
        if (!this.isActiveBackground(root, sessionId)) return;
        this.applyCenteredTransform(root, {
          scaleX: from.scaleX + (to.scaleX - from.scaleX) * progress,
          scaleY: from.scaleY + (to.scaleY - from.scaleY) * progress,
          x: from.x + (to.x - from.x) * progress,
          y: from.y + (to.y - from.y) * progress,
        });
      },
      () => {
        if (!this.isActiveBackground(root, sessionId)) return;
        this.applyCenteredTransform(root, to);
      },
      {
        ease: dotweenEaseCurve(input.ease),
        // The step/done guards already mute a retired session; this also
        // stops its frames, which an infinite loop would otherwise request
        // for as long as the renderer lives.
        isAlive: () => this.isActiveBackground(root, sessionId),
        loops: input.loop ? -1 : 1,
      },
    );

    if (input.block) await run;
    else void run;
  }

  /**
   * Port scope: `Torappu.AVG.LargeBackgroundPanel._ExecuteGridBG` /
   * `_ExecuteVerticalBG` / `_ExecuteImage` (selected via the `layout` input)
   * and the family `_LoadImage`, including all-or-nothing asset loading and
   * replacement. `Container` composition is the Web adaptation of Unity
   * RectTransforms. Load failures empty the panel like native `_ResetPanel()`.
   */
  async setGridBackground(input: GridBackgroundInput): Promise<void> {
    const sessionId = ++this.gridBackgroundSessionId;
    this.largeBackgroundTweenSessionId += 1;
    // Native port: every family executor — `_ExecuteImage` (largebg, VA
    // 0x183e77ee0), `_ExecuteGridBG` (0x183e76290) and `_ExecuteVerticalBG`
    // (0x183e79030) — calls `_ResetImages()` before `_LoadImage`, so the
    // previous tiles vanish the moment the command executes and the new
    // puzzle fades in over an emptied panel — a blank gap, never a
    // cross-fade.
    this.largeBackgroundRoot = null;
    const previous = [...this.gridBackgroundLayer.children];
    for (const child of previous) child.removeFromParent();
    const textures = await Promise.all(
      input.imageKeys.map((key) =>
        this.textureForImageKey(key, input.assetKind ?? "background"),
      ),
    );
    if (!this.app || sessionId !== this.gridBackgroundSessionId) return;

    if (textures.some((texture) => !texture)) {
      // Native `_LoadImage` failure logs "Failed to load background: [key]"
      // per tile plus the command-level "An error occurred when load image
      // {n}." error, then `_ResetPanel()` drops the whole puzzle instead of
      // keeping the previous one on screen (all-or-nothing).
      const missingKeys = input.imageKeys.filter(
        (_, index) => !textures[index],
      );
      this.onWarning?.(`missing grid background: ${missingKeys.join("/")}`);
      await this.clearGridBackground(0);
      return;
    }

    const root = this.buildGridBackgroundRoot(input, textures as Texture[]);
    // The whole family (largebg / verticalbg / gridbg) shares one `_offset`
    // container in native (`_ExecuteImageTween` drives it regardless of which
    // executor filled it, 2.7.61: 0x183e77a94), so every layout must register
    // itself as the largebgtween target.
    this.largeBackgroundRoot = root;

    root.alpha = input.fadeMs > 0 ? 0 : 1;
    this.gridBackgroundLayer.addChild(root);

    if (input.fadeMs <= 0) return;

    const run = this.tween(
      input.fadeMs,
      (progress) => {
        if (!this.app || sessionId !== this.gridBackgroundSessionId) return;
        root.alpha = progress;
      },
      () => {
        if (!this.app || sessionId !== this.gridBackgroundSessionId) return;
        root.alpha = 1;
      },
    );

    if (input.block) await run;
    else void run;
  }

  /**
   * Native port note: `_ExecuteImage`'s clear branch (imagegroup and cggroup
   * both empty) runs `DOFade(0, fadetime)` on the panel's CanvasGroup even
   * when it is already empty, so `block=true` still waits out the fade. The
   * Web adaptation returns early on an empty panel instead of animating
   * nothing — an intentional simplification (no shipped script issues a
   * parameterized clear on an empty panel).
   */
  async clearGridBackground(fadeMs = 0, block = false): Promise<void> {
    const sessionId = ++this.gridBackgroundSessionId;
    this.largeBackgroundRoot = null;
    this.largeBackgroundTweenSessionId += 1;
    const roots = [...this.gridBackgroundLayer.children];
    if (roots.length === 0) return;

    if (fadeMs <= 0) {
      for (const root of roots) root.removeFromParent();
      return;
    }

    const startAlphas = roots.map((root) => root.alpha);
    const run = this.tween(
      fadeMs,
      (progress) => {
        if (!this.app || sessionId !== this.gridBackgroundSessionId) return;
        const nextAlpha = 1 - progress;
        for (const [index, root] of roots.entries()) {
          root.alpha = startAlphas[index]! * nextAlpha;
        }
      },
      () => {
        if (!this.app || sessionId !== this.gridBackgroundSessionId) return;
        for (const root of roots) root.removeFromParent();
      },
    );

    if (block) await run;
    else void run;
  }

  /**
   * Web-only legacy compatibility surface. The investigated client has no
   * `largeimg` command or corresponding native executor, so this must not be
   * represented as a port; it remains isolated from the real `image` path.
   */
  async setLargeImage(input: GridBackgroundInput): Promise<void> {
    const sessionId = ++this.largeImageSessionId;
    this.largeImageTweenSessionId += 1;
    const textures = await Promise.all(
      input.imageKeys.map((key) =>
        this.textureForImageKey(key, input.assetKind ?? "image"),
      ),
    );
    if (!this.app || sessionId !== this.largeImageSessionId) return;

    if (textures.some((texture) => !texture)) return;

    const root = this.buildGridBackgroundRoot(input, textures as Texture[]);
    // largeimg is a legacy-only command and retains its existing transform.
    root.scale.set(1.2);
    const previous = [...this.largeImageRoots];
    this.largeImageRoot = root;

    root.alpha = input.fadeMs > 0 ? 0 : 1;
    this.largeImageRoots.add(root);
    this.imageLayer.addChild(root);

    if (input.fadeMs <= 0) {
      for (const child of previous) {
        child.removeFromParent();
        this.largeImageRoots.delete(child);
      }
      return;
    }

    const run = this.tween(
      input.fadeMs,
      (progress) => {
        if (!this.app || sessionId !== this.largeImageSessionId) return;
        root.alpha = progress;
      },
      () => {
        if (!this.app || sessionId !== this.largeImageSessionId) return;
        root.alpha = 1;
        for (const child of previous) {
          child.removeFromParent();
          this.largeImageRoots.delete(child);
        }
      },
    );

    if (input.block) await run;
    else void run;
  }

  async clearLargeImage(fadeMs = 0, block = false): Promise<void> {
    const sessionId = ++this.largeImageSessionId;
    this.largeImageRoot = null;
    this.largeImageTweenSessionId += 1;
    const roots = [...this.largeImageRoots];
    if (roots.length === 0) return;

    if (fadeMs <= 0) {
      for (const root of roots) {
        root.removeFromParent();
        this.largeImageRoots.delete(root);
      }
      return;
    }

    const startAlphas = roots.map((root) => root.alpha);
    const run = this.tween(
      fadeMs,
      (progress) => {
        if (!this.app || sessionId !== this.largeImageSessionId) return;
        const nextAlpha = 1 - progress;
        for (const [index, root] of roots.entries()) {
          root.alpha = startAlphas[index]! * nextAlpha;
        }
      },
      () => {
        if (!this.app || sessionId !== this.largeImageSessionId) return;
        for (const root of roots) {
          root.removeFromParent();
          this.largeImageRoots.delete(root);
        }
      },
    );

    if (block) await run;
    else void run;
  }

  /**
   * Port scope: `Torappu.AVG.AVGImagePanel._ExecuteImage` / `_LoadImage`
   * (2.7.61 @0x183e57cf0 / @0x183e58390) for the `image` path. Sprite
   * replacement, cross-fade and the block boundary are preserved; the
   * width/height sizeDelta multipliers and the `tiled` Image type are applied
   * like `_LoadImage` steps 5-9, and a failed texture load falls back to the
   * clear branch (LogError + fade the old image out) instead of keeping it.
   *
   * Native `_foreImage` is an Image whose RectTransform sizeDelta carries the
   * screenadapt-fit size while a parent transform carries `xScale`/`yScale` as
   * localScale. The root/sprite split reproduces that separation: the adapt
   * ratio stays on the sprite, `xScale`/`yScale` land on the root, so
   * `imagetween` scale values live in the native localScale space instead of
   * being folded into the adapt ratio.
   */
  async setImage(key: string, input?: BackgroundInput): Promise<void> {
    const texture = await this.textureForImageKey(key, "image");
    if (!texture) {
      // `_LoadImage` on a null sprite logs "Failed to load image: [{0}]"
      // (textureForUrl already reports it) and takes the clear branch:
      // DOFade(_backImage → 0), waiting for the fade when block is set.
      await this.clearImage(input?.fadeMs ?? 0, input?.block ?? false);
      return;
    }

    // `_ExecuteImage` only DOKills `_backImage` and its transform; the
    // panel-level rotation tween from `imagerotate` keeps running across
    // image swaps and the panel's current angle is preserved. Do NOT bump
    // imageRotateSessionId here (that would freeze a rotation mid-flight).
    const root = new Container();
    // `_LoadImage`: TryGetParam("tiled", false) switches Image.type between
    // Tiled and Simple, tiling the sprite inside the final sizeDelta rect;
    // a repeat-addressed TilingSprite is the PIXI equivalent (same
    // construction as the background path -- see setBackground).
    let sprite: Sprite | TilingSprite;
    if (input?.tiled) {
      texture.source.style.addressMode = "repeat";
      sprite = new TilingSprite({ texture });
    } else {
      sprite = new Sprite(texture);
    }
    sprite.anchor.set(0.5);
    // Image art keeps the texture's pixel size as the SetNativeSize rect;
    // the ppu sidecar only covers AVG/Backgrounds keys.
    this.layoutImageForScreenAdapt(sprite, input?.screenAdapt, {
      height: input?.height ?? 1,
      width: input?.width ?? 1,
    });
    if (sprite instanceof TilingSprite) this.alignTilesToLowerLeft(sprite);
    // `_ExecuteImage` reads `xScale`/`yScale` with a 1.0 fallback, so an
    // omitted scale must leave the screen-adapted size alone.
    this.applyCenteredTransform(root, {
      scaleX: input?.scaleX ?? 1,
      scaleY: input?.scaleY ?? 1,
      x: input?.x ?? 0,
      y: input?.y ?? 0,
    });
    root.addChild(sprite);

    const previous = this.imageRoot;
    this.imageRoot = root;
    this.imageLayer.addChild(root);
    const fadeMs = input?.fadeMs ?? 0;
    root.alpha = fadeMs > 0 ? 0 : 1;
    if (fadeMs <= 0) {
      previous?.removeFromParent();
      return;
    }
    const run = this.tween(
      fadeMs,
      (progress) => (root.alpha = progress),
      () => previous?.removeFromParent(),
    );
    if (input?.block) await run;
    else void run;
  }

  async clearImage(fadeMs = 0, block = false): Promise<void> {
    // Same as setImage: the clear branch fades `_backImage` out and never
    // touches the panel rotation tween, so imagerotate keeps its angle and
    // any in-flight rotation keeps playing (see setImageRotate).
    const root = this.imageRoot;
    this.imageRoot = null;
    if (!root) return;
    if (fadeMs <= 0) {
      root.removeFromParent();
      return;
    }
    const startAlpha = root.alpha;
    const run = this.tween(
      fadeMs,
      (progress) => (root.alpha = startAlpha * (1 - progress)),
      () => root.removeFromParent(),
    );
    if (block) await run;
    else void run;
  }

  async clearCharacterCutin(widgetId?: string): Promise<void> {
    this.cutinPanel.clear(widgetId);
  }

  async clearInterludes(): Promise<void> {
    await this.interludePanel.clearAll();
  }

  async setInterlude(input: InterludeInput): Promise<void> {
    await this.interludePanel.run(input);
  }

  /**
   * Native port: `Torappu.AVG.AVGCharacterCutinPanel` (build 2761 IDA review):
   * `GetExecutors` @ 0x183e48f70 registers `charactercutin` ->
   * `_ExecuteCharacterCutin` @ 0x183e49440, whose five widgetID/name branches
   * (show / SlotUpdate / hide) live in CharacterCutinPanel together with the
   * `AVGCharacterCutinSlot` Show/SlotUpdate/Hide geometry.
   */
  async setCharacterCutin(input: CharacterCutinInput): Promise<void> {
    if (!this.app) return;
    await this.cutinPanel.run(input);
  }

  private async textureForCutinCharacter(
    input: CharacterCutinInput,
  ): Promise<CutinCharacterArt | null> {
    const base = input.characterKey;
    const expression = input.expression;
    if (!base || !expression) return null;

    const link = this.context.linkMap[base];
    if (!link) {
      this.onWarning?.(`missing character base: ${base}`);
      return null;
    }

    const item = link.array.find((entry) => entry.name === expression);
    if (!item) {
      this.onWarning?.(`missing character expression: ${base}#${expression}`);
      return null;
    }

    let assetKey: string | null = null;
    if (item.group === -1 && "image" in item && item.image) {
      assetKey = item.image;
    } else if (item.group >= 0 && "face" in item && item.face) {
      const g = link.groups[item.group];
      assetKey = g && g.mode === "face_overlay" ? g.base : null;
    }

    if (!assetKey) {
      this.onWarning?.(`invalid cutin character: ${base}#${expression}`);
      return null;
    }

    const texture = await this.textureForCharacterKey(assetKey);
    if (!texture) return null;
    // AVGCharacterSpriteHub.SetImage resizes the character Image to the hub's
    // serialized size and shifts it by pos; the scene character path applies
    // the same layout (see buildCharacterSlotState), so the cutin must too.
    const sizeX = link.size.x || texture.width;
    const sizeY = link.size.y || texture.height;
    return {
      offsetX: link.pos.x || 0,
      offsetY: link.pos.y || 0,
      scaleX: sizeX / Math.max(1, texture.width),
      scaleY: sizeY / Math.max(1, texture.height),
      texture,
    };
  }

  /** Loads the bundled photo-frame texture once; null after a failure. */
  private async loadShowItemFrameTexture(): Promise<Texture | null> {
    if (!this.showItemFrameTexture && !this.showItemFrameTextureFailed) {
      try {
        this.showItemFrameTexture = await Assets.load<Texture>(
          SHOWITEM_PHOTO_FRAME_URL,
        );
      } catch {
        this.showItemFrameTextureFailed = true;
        this.onWarning?.("missing show item frame texture");
      }
    }
    return this.showItemFrameTexture;
  }

  /** Loads the shared dialog gradient once; null after a failure. */
  private async loadDialogFrameTexture(): Promise<Texture | null> {
    if (!this.dialogFrameTexture && !this.dialogFrameTextureFailed) {
      try {
        this.dialogFrameTexture = await Assets.load<Texture>(DIALOG_FRAME_URL);
      } catch {
        this.dialogFrameTextureFailed = true;
        this.onWarning?.("missing show item header texture");
      }
    }
    return this.dialogFrameTexture;
  }

  /**
   * Port scope: `Torappu.AVG.AVGShowItemPanel._ExecuteShowItem` / `_ShowItem`
   * (2.7.61 VAs 0x183E5BE10 / 0x183E5C290). Native owns a single slot
   * (`_slotInUse`): while a slot is on stage the executor hands the NEW
   * command to the old slot's `Hide` (vtable slot 5), so the old picture
   * fades out with the NEW command's fadetime, then the `b__0` closure
   * (`_Reset` + `_ShowItem`, 0x183E62FB0) destroys the slot before the next
   * picture fades in — both stages block the executor. `style` picks the
   * serialized prefab: photo renders the item at native size over the bundled
   * nine-slice `sprite_avg_showItem_photo` frame (item + 32px per side, see
   * engine/showitem.ts); cutin renders the RectMask2D window (0.192-gray
   * stretch plate + header strip + item) that `fadestyle` expands from one
   * edge while the black backdrop cross-fades.
   */
  async showItem(input: ShowItemInput): Promise<void> {
    const chain = async (): Promise<void> => {
      // Replacement branch of `_ExecuteShowItem`: the old slot's `Hide` runs
      // with the NEW command's fadetime (per-style, see hideShowItem), then
      // the slot is recycled (`_Reset`). A root already detached from the
      // layer (hideitem / scene reset) mirrors a cleared `_slotInUse`, so it
      // takes the direct-show branch instead.
      if (this.showItemRoot?.parent) {
        await this.hideShowItem(input.fadeMs);
      } else {
        this.showItemRoot = null;
        this.showItemCutin = null;
      }

      const texture = input.key
        ? await this.textureForImageKey(input.key, "image")
        : null;
      // `AVGShowItemSlot.Show` (0x183ED3E80) skips only the sprite assignment
      // when the asset fails to load (an empty `image` included); the slot
      // still plays its fade and blocks for fadetime. A failed load also
      // leaves native `_image` at its serialized 100x100 placeholder
      // (`SetNativeSize` is skipped), which is also the fallback behind the
      // cutin `width` key.
      const image = texture
        ? createShowItemSprite(texture)
        : createShowItemPlaceholder();
      const cutinWidth = Number.isFinite(input.width)
        ? Math.round(input.width!)
        : (texture?.width ?? 100);
      // Unity saturates out-of-range graphic alpha at blend time; clamping
      // here reproduces the rendered result of the unclamped
      // `GetFloat("black", 0.4)` the native slots pass straight to SetAlpha.
      const blackAlpha = Math.max(0, Math.min(1, input.blackAlpha));
      const frameTexture =
        input.style === "photo" ? await this.loadShowItemFrameTexture() : null;
      const headerTexture =
        input.style === "cutin" ? await this.loadDialogFrameTexture() : null;

      const root = new Container();
      // Cutin expand styles keep the canvas group opaque (only the mask
      // window and black alpha animate); photo and cutin `fade` fade the
      // whole canvas group from 0.
      const fadeStyle = input.fadeStyle ?? "fade";
      const cutinExpand = input.style === "cutin" && fadeStyle !== "fade";
      root.alpha = input.fadeMs > 0 && !cutinExpand ? 0 : 1;
      // Full-canvas `black` child; the cutin expand styles animate its own
      // alpha (Show: 0 -> black, Hide: -> 0) while photo/cutin-fade fade it
      // together with everything else through the root alpha.
      const backdrop = new Graphics();
      backdrop.rect(0, 0, STORY_WIDTH, STORY_HEIGHT);
      backdrop.fill({ color: 0x00_00_00 });
      backdrop.alpha = blackAlpha;
      root.addChild(backdrop);

      // Both slots write `offsetx/offsety` into a Unity `localPosition` (photo:
      // `_image`, cutin: `_offsetTransform`), whose y axis points up; PIXI's
      // points down.
      const contentX = STORY_WIDTH / 2 + input.offsetX;
      const contentY = STORY_HEIGHT / 2 - input.offsetY;

      let cutin: ShowItemCutinState | null = null;
      if (input.style === "cutin") {
        cutin = this.buildShowItemCutin({
          backdrop,
          contentX,
          contentY,
          fadeStyle,
          headerTexture,
          image,
          root,
          targetW: cutinWidth,
        });
        if (cutinExpand) {
          // Expand Show: the canvas group stays opaque, the mask window grows
          // from the style's edge and the black backdrop cross-fades 0 ->
          // black. Native runs DOSizeDelta + DOFade as two tweens of the same
          // fadetime under DOTween's default OutQuad ease (neither SetEase),
          // so one eased tween drives both here. fadeMs 0 still opens the
          // window full: the runner settles a 0-duration run at progress 1,
          // like DOSizeDelta(_, _, 0) completing on the next native frame.
          backdrop.alpha = 0;
          const horizontal = cutin.fadeStyle.startsWith("horiz");
          const state = cutin;
          const expand = (progress: number): void => {
            state.maskW = horizontal ? state.targetW * progress : state.targetW;
            state.maskH = horizontal ? state.targetH : state.targetH * progress;
            backdrop.alpha = blackAlpha * progress;
            this.drawShowItemCutinWindow(state);
          };
          this.showItemRoot = root;
          this.showItemCutin = cutin;
          this.itemLayer.addChild(root);
          await this.tween(input.fadeMs, expand, undefined, {
            ease: dotweenDefaultEase,
          });
          return;
        }
      } else {
        // Prefab child order: `black` (backdrop) < `background` (frame) <
        // `image_item`, all centered. `offsetx/offsety` move `_image` only;
        // the sibling frame's `UICustomAnchor` re-fits it to `image_item`
        // plus 32px every frame, so the web port moves both as one group.
        const layout = computeShowItemLayout(
          texture ? texture.width : 100,
          texture ? texture.height : 100,
        );
        const content = new Container();
        content.position.set(contentX, contentY);
        if (frameTexture) {
          const frame = new NineSliceSprite({
            bottomHeight: SHOWITEM_FRAME_BORDER,
            leftWidth: SHOWITEM_FRAME_BORDER,
            rightWidth: SHOWITEM_FRAME_BORDER,
            texture: frameTexture,
            topHeight: SHOWITEM_FRAME_BORDER,
          });
          frame.anchor.set(0.5);
          frame.width = layout.frameWidth;
          frame.height = layout.frameHeight;
          content.addChild(frame);
        }
        content.addChild(image);
        root.addChild(content);
      }

      // Only the tracked slot root is added here: `animtext` renders into the
      // same layer but is an independent native panel (`AnimTextPanel`), so it
      // must survive item swaps instead of being swept by removeChildren().
      this.showItemRoot = root;
      this.showItemCutin = cutin;
      this.itemLayer.addChild(root);

      if (input.fadeMs <= 0) return;

      // Photo Show and cutin `fade`: the canvas group fades 0 -> 1 while the
      // backdrop is already at `black` (SetAlpha applies it immediately).
      // DOFade carries no SetEase, so DOTween's default OutQuad applies.
      await this.tween(
        input.fadeMs,
        (progress) => {
          root.alpha = progress;
        },
        undefined,
        { ease: dotweenDefaultEase },
      );
    };

    if (input.block) await chain();
    else void chain();
  }

  /**
   * Builds the cutin slot subtree: the full-canvas `black` sits outside the
   * mask, while `offset` -> `mask` (RectMask2D) clips the 0.192-gray stretch
   * plate, its `sprite_avg_cutscene` header strip (tinted black at 0.706)
   * and the centered item. The window starts at full extent for
   * `fade` and zero extent along the expansion axis otherwise, matching the
   * state Show seeds before its tween starts.
   */
  private buildShowItemCutin(args: {
    backdrop: Graphics;
    contentX: number;
    contentY: number;
    fadeStyle: ShowItemFadeStyle;
    headerTexture: Texture | null;
    image: Container;
    root: Container;
    targetW: number;
  }): ShowItemCutinState {
    // `offset` stretches over the fixed 1280x720 `panel_avg` and Show sizes
    // the mask to its rect height, so the window is 720 tall.
    const targetH = STORY_HEIGHT;
    const slot = new Container();
    slot.position.set(args.contentX, args.contentY);
    const plate = new Graphics();
    slot.addChild(plate);
    let header: Sprite | null = null;
    if (args.headerTexture) {
      // Pivot bottom-center at 50px above the window top, rotated 180°
      // around Z: the strip hangs 150px into the window upside down.
      header = new Sprite(args.headerTexture);
      header.anchor.set(0.5, 1);
      header.rotation = Math.PI;
      header.tint = 0x00_00_00;
      header.alpha = 0.706;
      header.height = 200;
      slot.addChild(header);
    }
    slot.addChild(args.image);
    const mask = new Graphics();
    args.root.addChild(mask);
    slot.mask = mask;
    args.root.addChild(slot);

    const horizontal = args.fadeStyle.startsWith("horiz");
    // Show seeds the mask window at full extent for `fade` and zero extent
    // along the expansion axis otherwise (canvas group alpha covers `fade`).
    const state: ShowItemCutinState = {
      backdrop: args.backdrop,
      cx: args.contentX,
      cy: args.contentY,
      fadeStyle: args.fadeStyle,
      header,
      mask,
      maskH: horizontal || args.fadeStyle === "fade" ? targetH : 0,
      maskW: horizontal ? 0 : args.targetW,
      plate,
      targetH,
      targetW: args.targetW,
    };
    this.drawShowItemCutinWindow(state);
    return state;
  }

  /**
   * Redraws the cutin mask window plus the plate/header that stretch inside
   * it. Pivot table from Show/Hide: left2right pins the left edge, right2left
   * the right edge, top2bottom the top edge, bottom2top the bottom edge; the
   * rest stay centered. PIXI's y axis is inverted relative to Unity, which
   * swaps which coordinate stays pinned for the vertical styles.
   */
  private drawShowItemCutinWindow(state: ShowItemCutinState): void {
    const { maskH: h, maskW: w, targetH, targetW } = state;
    let x: number;
    switch (state.fadeStyle) {
      case "horiz_expand_left2right": {
        x = -targetW / 2;
        break;
      }
      case "horiz_expand_right2left": {
        x = targetW / 2 - w;
        break;
      }
      default: {
        x = -w / 2;
        break;
      }
    }
    let y: number;
    switch (state.fadeStyle) {
      case "vert_expand_top2bottom": {
        y = -targetH / 2;
        break;
      }
      case "vert_expand_bottom2top": {
        y = targetH / 2 - h;
        break;
      }
      default: {
        y = -h / 2;
        break;
      }
    }
    state.mask
      .clear()
      .rect(state.cx + x, state.cy + y, w, h)
      .fill(0xff_ff_ff);
    state.plate.clear().rect(x, y, w, h).fill(0x31_31_31);
    if (state.header) {
      state.header.position.set(x + w / 2, y - 50);
      state.header.width = w;
    }
  }

  /**
   * Port of the slot `Hide` both executors dispatch to (vtable slot 5). Photo
   * unconditionally snaps the canvas group to alpha 1 before fading to 0;
   * cutin reuses the `_showFadeStyle` stored by its Show — a plain canvas
   * fade from the current alpha for `fade` (no snap), or the mask window
   * collapsing along its expansion axis with a parallel black fade for the
   * expand styles (root alpha stays 1). No native Hide tween calls SetEase,
   * so every fade runs on DOTween's default OutQuad. `fadeMs <= 0` is an
   * immediate destroy (`DOFade(_, _, 0)` completes on the next native frame).
   */
  private async hideShowItem(fadeMs: number): Promise<void> {
    const root = this.showItemRoot;
    const cutin = this.showItemCutin;
    this.showItemRoot = null;
    this.showItemCutin = null;
    if (!root?.parent) return;
    if (fadeMs <= 0) {
      root.removeFromParent();
      return;
    }

    if (cutin && cutin.fadeStyle !== "fade") {
      const horizontal = cutin.fadeStyle.startsWith("horiz");
      const startW = cutin.maskW;
      const startH = cutin.maskH;
      const startBlack = cutin.backdrop.alpha;
      const state = cutin;
      const collapse = (progress: number): void => {
        state.maskW = horizontal ? startW * (1 - progress) : startW;
        state.maskH = horizontal ? startH : startH * (1 - progress);
        state.backdrop.alpha = startBlack * (1 - progress);
        this.drawShowItemCutinWindow(state);
      };
      await this.tween(
        fadeMs,
        collapse,
        () => {
          root.removeFromParent();
        },
        { ease: dotweenDefaultEase },
      );
      return;
    }

    if (!cutin) {
      // `AVGShowItemPhotoSlot.Hide` resets the canvas group to alpha 1
      // before the DOFade; only an interrupted Show ever notices the snap.
      root.alpha = 1;
      await this.tween(
        fadeMs,
        (progress) => {
          root.alpha = 1 - progress;
        },
        () => {
          root.removeFromParent();
        },
        { ease: dotweenDefaultEase },
      );
      return;
    }

    // Cutin `fade` Hide: canvas fade from the current alpha; the backdrop
    // under the same canvas group fades with it.
    const startAlpha = root.alpha;
    await this.tween(
      fadeMs,
      (progress) => {
        root.alpha = startAlpha * (1 - progress);
      },
      () => {
        root.removeFromParent();
      },
      { ease: dotweenDefaultEase },
    );
  }

  async showCgItem(input: CgItemInput): Promise<void> {
    await this.cgItemPanel.show(input);
  }

  async clearCgItems(
    key?: string,
    fadeMs = 130,
    ease = "Linear",
    block = false,
  ): Promise<void> {
    await this.cgItemPanel.hide(key, fadeMs, ease, block);
  }

  /**
   * Port of `Torappu.AVG.AVGShowItemPanel._ExecuteHideItem` dispatching to the
   * slot's `Hide` (GameAssembly 2.7.61: panel @ 0x183E5BCE0, photo slot
   * `AVGShowItemPhotoSlot.Hide` @ 0x183ED35E0, cutin slot
   * `AVGShowItemCutinSlot.Hide` @ 0x183ED2A60). Native photo Hide snaps the
   * canvas group to alpha 1 and DOTween-fades it to 0; cutin Hide reuses the
   * stored `_showFadeStyle` — alpha fade for `fade`, or the mask window
   * collapsing along its expansion axis plus a parallel black fade. Only the
   * show-item root is touched: native animtext is a separate panel, and the
   * stamps sharing this layer must survive a hideitem.
   */
  async clearItems(fadeMs = 0, hasSlot = false): Promise<void> {
    const hide = this.hideShowItem(fadeMs);
    if (hasSlot) await hide;
    else void hide;
  }

  /**
   * Port of `Torappu.AVG.AVGImagePanel._ExecuteImageTween` for the foreground
   * visual. Native tweens `_foreImage.rectTransform` localPosition/localScale
   * with DOTween; both are reproduced on the image root, whose scale is the
   * localScale space (`setImage` keeps the screenadapt ratio on the inner
   * sprite). `ease` goes through the runner's `SetEase` option as a single
   * eased progress shared by the position and scale interpolation, matching
   * `SetEase` on both native tweens, and `loop` maps SetLoops(2*!loop-1) to
   * the same infinite Restart loop as `setBackgroundTween` (block is dropped
   * upstream for loops). Consecutive `imagetween`s are not cancelled (native
   * never calls DOKill either; both DOTween instances keep writing the same
   * transform); only an infinite loop is retired once its root stops being
   * the foreground image, since it would otherwise request frames for as long
   * as the renderer lives.
   *
   * Without a foreground image native dereferences `_foreImage` and throws;
   * story scripts always precede the tween with `[Image]`, so the silent
   * return only avoids the equivalent crash.
   */
  async setImageTween(input: ImageTweenInput): Promise<void> {
    const root = this.imageRoot;
    if (!root || root.parent !== this.imageLayer) return;

    // From/To default to the same current transform values (native reads the
    // current localPosition/localScale once for both fallbacks).
    const current = this.readCenteredTransform(root);
    const from = {
      scaleX: isFiniteNumber(input.xScaleFrom)
        ? input.xScaleFrom
        : current.scaleX,
      scaleY: isFiniteNumber(input.yScaleFrom)
        ? input.yScaleFrom
        : current.scaleY,
      x: isFiniteNumber(input.xFrom) ? input.xFrom : current.x,
      y: isFiniteNumber(input.yFrom) ? input.yFrom : current.y,
    };
    const to = {
      scaleX: isFiniteNumber(input.xScaleTo) ? input.xScaleTo : current.scaleX,
      scaleY: isFiniteNumber(input.yScaleTo) ? input.yScaleTo : current.scaleY,
      x: isFiniteNumber(input.xTo) ? input.xTo : current.x,
      y: isFiniteNumber(input.yTo) ? input.yTo : current.y,
    };

    // Native jumps to the From transform before starting the tweens, and a
    // non-positive duration Completes both tweens so the To state lands
    // immediately.
    this.applyCenteredTransform(root, from);

    if (input.durationMs <= 0) {
      this.applyCenteredTransform(root, to);
      return;
    }

    const run = this.tween(
      input.durationMs,
      (progress) => {
        this.applyCenteredTransform(root, {
          scaleX: from.scaleX + (to.scaleX - from.scaleX) * progress,
          scaleY: from.scaleY + (to.scaleY - from.scaleY) * progress,
          x: from.x + (to.x - from.x) * progress,
          y: from.y + (to.y - from.y) * progress,
        });
      },
      undefined,
      {
        ease: dotweenEaseCurve(input.ease),
        // Single passes keep writing an outgoing root until they finish, as
        // before; only a never-ending loop has to stop with its root.
        isAlive: input.loop ? () => this.imageRoot === root : undefined,
        loops: input.loop ? -1 : 1,
      },
    );

    if (input.block) await run;
    else void run;
  }

  /**
   * Port of `Torappu.AVG.LargeBackgroundPanel._ExecuteImageTween` for
   * `largebg`; it intentionally uses the panel's direct tween timing,
   * including the `GetEnum<Ease>("ease", 1)` ease curve and the
   * `SetLoops(2*!loop-1)` loop count.
   */
  async setLargeBackgroundTween(
    input: LargeBackgroundTweenInput,
  ): Promise<void> {
    const root = this.largeBackgroundRoot;
    if (!root || root.parent !== this.gridBackgroundLayer) return;

    const current = this.readCenteredTransform(root);
    const from = {
      scaleX: isFiniteNumber(input.xScaleFrom)
        ? input.xScaleFrom
        : current.scaleX,
      scaleY: isFiniteNumber(input.yScaleFrom)
        ? input.yScaleFrom
        : current.scaleY,
      x: isFiniteNumber(input.xFrom) ? input.xFrom : current.x,
      y: isFiniteNumber(input.yFrom) ? input.yFrom : current.y,
    };
    const to = {
      scaleX: isFiniteNumber(input.xScaleTo) ? input.xScaleTo : current.scaleX,
      scaleY: isFiniteNumber(input.yScaleTo) ? input.yScaleTo : current.scaleY,
      x: isFiniteNumber(input.xTo) ? input.xTo : current.x,
      y: isFiniteNumber(input.yTo) ? input.yTo : current.y,
    };

    // Native never kills a running largebgtween before starting a new one:
    // overlapping tweens both write `_offset` every frame (the newer one
    // wins while both run, and a longer older tween resumes writing once the
    // newer completes). Retiring the previous tween via the session id
    // instead is a deliberate Web deviation that avoids competing writers.
    const sessionId = ++this.largeBackgroundTweenSessionId;
    this.applyCenteredTransform(root, from);

    if (input.durationMs <= 0) {
      this.applyCenteredTransform(root, to);
      return;
    }

    // The native move and scale DOTweens share one duration, ease and loop
    // count, so a single merged interpolation is frame-equivalent; loop=true
    // turns the run into an infinite Restart loop that replays the From pose
    // every cycle and never completes (block is dropped upstream for loops).
    const run = this.tween(
      input.durationMs,
      (progress) => {
        if (!this.isActiveLargeBackground(root, sessionId)) return;
        this.applyCenteredTransform(root, {
          scaleX: from.scaleX + (to.scaleX - from.scaleX) * progress,
          scaleY: from.scaleY + (to.scaleY - from.scaleY) * progress,
          x: from.x + (to.x - from.x) * progress,
          y: from.y + (to.y - from.y) * progress,
        });
      },
      () => {
        if (!this.isActiveLargeBackground(root, sessionId)) return;
        this.applyCenteredTransform(root, to);
      },
      {
        ease: dotweenEaseCurve(input.ease),
        loops: input.loop ? -1 : 1,
      },
    );

    if (input.block) await run;
    else void run;
  }

  /**
   * Web-only companion to the legacy `setLargeImage` surface: the investigated
   * client has no `largeimgtween` executor. It is intentionally not a native
   * provenance claim; the tween semantics (ease via `GetEnum<Ease>("ease",
   * 1)`, loops via `SetLoops(2*!loop-1)`) mirror the closest native
   * relative, `LargeBackgroundPanel._ExecuteImageTween` (`largebgtween`).
   */
  async setLargeImageTween(input: LargeBackgroundTweenInput): Promise<void> {
    const root = this.largeImageRoot;
    if (!root || root.parent !== this.imageLayer) return;

    const current = this.readCenteredTransform(root);
    const from = {
      scaleX: isFiniteNumber(input.xScaleFrom)
        ? input.xScaleFrom
        : current.scaleX,
      scaleY: isFiniteNumber(input.yScaleFrom)
        ? input.yScaleFrom
        : current.scaleY,
      x: isFiniteNumber(input.xFrom) ? input.xFrom : current.x,
      y: isFiniteNumber(input.yFrom) ? input.yFrom : current.y,
    };
    const to = {
      scaleX: isFiniteNumber(input.xScaleTo) ? input.xScaleTo : current.scaleX,
      scaleY: isFiniteNumber(input.yScaleTo) ? input.yScaleTo : current.scaleY,
      x: isFiniteNumber(input.xTo) ? input.xTo : current.x,
      y: isFiniteNumber(input.yTo) ? input.yTo : current.y,
    };

    const sessionId = ++this.largeImageTweenSessionId;
    this.applyCenteredTransform(root, from);

    if (input.durationMs <= 0) {
      this.applyCenteredTransform(root, to);
      return;
    }

    // The native move and scale DOTweens share one duration, ease and loop
    // count, so a single merged interpolation is frame-equivalent; loop=true
    // turns the run into an infinite Restart loop that replays the From pose
    // every cycle and never completes (block is dropped upstream for loops).
    const run = this.tween(
      input.durationMs,
      (progress) => {
        if (!this.isActiveLargeImage(root, sessionId)) return;
        this.applyCenteredTransform(root, {
          scaleX: from.scaleX + (to.scaleX - from.scaleX) * progress,
          scaleY: from.scaleY + (to.scaleY - from.scaleY) * progress,
          x: from.x + (to.x - from.x) * progress,
          y: from.y + (to.y - from.y) * progress,
        });
      },
      () => {
        if (!this.isActiveLargeImage(root, sessionId)) return;
        this.applyCenteredTransform(root, to);
      },
      {
        ease: dotweenEaseCurve(input.ease),
        loops: input.loop ? -1 : 1,
      },
    );

    if (input.block) await run;
    else void run;
  }

  /**
   * Port of `Torappu.AVG.AVGImagePanel._ExecuteImageRotate`: rotate the panel
   * transform (`_rectTransform`, the scene `panel_image` node) rather than its
   * foreground Image, preserving angle across image swaps. `imageLayer` is the
   * corresponding PIXI adaptation, with its constructor-time pivot mirroring
   * the RectTransform's serialized (0.5, 0.5) pivot so the rotation orbits the
   * panel center instead of the stage origin. Native never resets the angle
   * except `OnReset` (story start/skip); the widget builds a fresh renderer
   * per story, so no explicit zeroing is needed here.
   *
   * Unity's eulerAngles.z is counter-clockwise on screen (y-up; a positive
   * sweep is what CreateRotateTween's `counterClockwise` flag asks for), while
   * PIXI's `angle` is clockwise (y-down). The sweep is computed in Unity space
   * and negated at the PIXI boundary, the same axis flip applyCenteredTransform
   * does for `y`.
   */
  async setImageRotate(input: ImageRotateInput): Promise<void> {
    const target = this.imageLayer;
    const sessionId = ++this.imageRotateSessionId;
    const startAngle = -target.angle;
    const delta = rotateTweenDelta(
      startAngle,
      input.angleDeg,
      input.circles,
      input.inverse,
    );
    const endAngle = startAngle + delta;

    if (input.durationMs <= 0) {
      target.angle = -endAngle;
      return;
    }

    const run = this.tween(
      input.durationMs,
      (progress) => {
        if (this.imageRotateSessionId !== sessionId) return;
        target.angle = -(startAngle + delta * progress);
      },
      () => {
        if (this.imageRotateSessionId !== sessionId) return;
        target.angle = -endAngle;
      },
    );

    if (input.block) await run;
    else void run;
  }

  /**
   * Port scope: `Torappu.AVG.CharacterPanel._ExecuteCharacter` and its
   * character-slot branch. Slot state and transitions are preserved; display
   * hierarchy and texture composition are Web/PIXI adaptations.
   */
  async setCharacter(input: CharacterSlotInput): Promise<void> {
    if (this.isCharacterSlotCommand(input)) {
      await this.setCharacterSlot(input);
      return;
    }

    if (!input.characterKey || !input.expression) return;

    const link = this.context.linkMap[input.characterKey];
    if (!link) {
      this.onWarning?.(`missing character base: ${input.characterKey}`);
      return;
    }

    const item = link.array.find((entry) => entry.name === input.expression);
    if (!item) {
      this.onWarning?.(
        `missing character expression: ${input.characterKey}#${input.expression}`,
      );
      return;
    }

    const slot = this.normalizeCharacterSlot(input.slot);
    const slotBaseX: Record<string, number> = {
      // Derived from legacy 960-space anchors [330, 480, 630], scaled to 1280.
      l: 440,
      m: 640,
      r: 840,
    };

    const built = await this.buildCharacterVisual(
      input.characterKey,
      input.expression,
      input.blackStart,
      input.blackEnd,
    );
    if (!built) return;

    const { sourceHeight, sourceWidth, visual } = built;

    const sizeX = link.size.x || sourceWidth;
    const sizeY = link.size.y || sourceHeight;
    const offsetX = link.pos.x || 0;
    const offsetY = link.pos.y || 0;

    // Match legacy char layout semantics in 1280x720 space.
    const targetX = (slotBaseX[slot] ?? slotBaseX.m) - sizeX / 2 + offsetX;
    const targetY = STORY_HEIGHT - sizeY / 2 - offsetY;
    const enterOffset = this.enterOffset(
      slot,
      input.enterFrom,
      input.enterPosition,
    );
    const root = new Container();
    const motionLayer = new Container();
    const rotationLayer = new Container();
    rotationLayer.addChild(visual);
    motionLayer.addChild(rotationLayer);
    root.addChild(motionLayer);
    root.x = targetX;
    root.y = targetY;

    const safeWidth = Math.max(1, sourceWidth);
    const safeHeight = Math.max(1, sourceHeight);
    const baseScaleX = sizeX / safeWidth;
    const baseScaleY = sizeY / safeHeight;
    motionLayer.scale.set(baseScaleX, baseScaleY);
    rotationLayer.pivot.set(sourceWidth / 2, sourceHeight / 2);
    rotationLayer.position.set(sourceWidth / 2, sourceHeight / 2);

    const previous = this.characterSlots.get(slot);
    const focusBrightness = input.dimmed ? 0.5 : 1;
    const durationMs = Math.max(0, Math.round(input.durationMs ?? 0));
    // Native `AVGCharacterSlot.Set` receives `dontFadeIfSameChar=true` from
    // `CharacterPanel._ProcessSlot`. It compares `_GetIdWithoutAliasOrIndex`
    // for the old/new refs and forces only the image fade to zero when that id
    // is unchanged. Cross-fading two copies of the same body sprite causes a
    // visible brightness dip, so expression/focus changes must swap at once.
    const fadeIdentity = input.fadeIdentity ?? input.characterKey;
    // Native `_ProcessSlot`: with an `enter`, the image fade duration goes
    // through `_ProcessDurationWithTransType(duration, transtype)`, which
    // returns 0 for ECharTransType.NONE (0) -- i.e. the default enter is a
    // pure slide with no fade; only ALPHA_IN/ALPHA_OUT fade while sliding.
    // Without `enter` the raw duration is used. `dontFadeIfSameChar` can
    // still zero the fade in both paths.
    const fadeMsForNewImage =
      input.enterFrom && !input.transType ? 0 : durationMs;
    const imageFadeMs =
      previous?.fadeIdentity === fadeIdentity ? 0 : fadeMsForNewImage;
    // An explicit enter still moves for the requested duration even when the
    // same character's expression is swapped instantly.
    const moveMs = input.enterFrom ? durationMs : 0;
    const animationMs = Math.max(imageFadeMs, moveMs);
    // `AVGCharacterSlot.Set` @ 0x183eb38a0 guards its entire swap branch --
    // the `_offset` position reset and the localScale reset included -- with
    // `!op_Equality(m_currentKey, key)`. A re-show under the same key, e.g.
    // the `character(focus=-1)` that follows a `characteraction move`, never
    // reaches the reset, so the slot keeps the offset the move left behind.
    // `m_currentKey` is the raw `name` ref, so `nativeKey` is compared rather
    // than the resolved base/expression pair: `avg_x` and `avg_x#1$1` can
    // resolve alike yet are different keys to native.
    //
    // An `enter` is exempt. `CharacterPanel._ProcessSlot` @ 0x183e6c1c0 takes
    // the branch that first calls `SetCharPos(x, y, 0)` -- which writes that
    // very same `_offset` absolutely (@ 0x183eb3290) -- and then passes
    // `resetOffsetPos = 0`; the closing `SetCharPos(0, 0, duration)` tweens it
    // back to the origin. So a slide always overrides an earlier move.
    const keepsOffset =
      !input.enterFrom &&
      previous !== undefined &&
      input.nativeKey !== undefined &&
      previous.nativeKey === input.nativeKey;
    const state: CharacterRenderState = {
      actionX: keepsOffset ? previous.actionX : 0,
      actionY: keepsOffset ? previous.actionY : 0,
      baseScaleX,
      baseScaleY,
      baseX: targetX,
      baseY: targetY,
      characterKey: input.characterKey,
      contentAlpha: 1,
      focusBrightness,
      expression: input.expression,
      fadeIdentity,
      height: sizeY,
      jumpOffsetY: 0,
      jumpSessionId: 0,
      motionLayer,
      nativeKey: input.nativeKey,
      opacitySessionId: 0,
      outgoingVisual: null,
      replaceFadeSessionId: 0,
      root,
      rotationDeg: 0,
      rotationLayer,
      rotateSessionId: 0,
      rotateTimeout: null,
      scaleX: keepsOffset ? previous.scaleX : 1,
      scaleY: keepsOffset ? previous.scaleY : 1,
      shakeOffsetX: 0,
      shakeOffsetY: 0,
      shakeSessionId: 0,
      slot,
      sourceHeight,
      sourceWidth,
      transformSessionId: 0,
      visual,
      width: sizeX,
      zoomSessionId: 0,
      zoomShiftX: keepsOffset ? previous.zoomShiftX : 0,
      zoomShiftY: keepsOffset ? previous.zoomShiftY : 0,
    };
    this.updateCharacterState(state);

    root.alpha = imageFadeMs > 0 ? 0 : 1;
    if (moveMs > 0) {
      root.x = targetX + enterOffset.x;
      root.y = targetY + enterOffset.y;
    }

    if (previous && imageFadeMs <= 0) this.disposeCharacterState(previous);
    this.characterSlots.set(slot, state);
    this.charLayer.addChild(root);
    this.applyCharacterZOrder(input.focus ?? 0);

    if (previous && imageFadeMs > 0)
      void this.fadeOutAndRemove(previous.root, imageFadeMs, () =>
        this.disposeCharacterState(previous),
      );

    if (animationMs > 0) {
      const run = this.tween(
        animationMs,
        (progress) => {
          root.alpha = imageFadeMs > 0 ? progress : 1;
          // Native `SetCharPos` runs the slide as
          // `DOLocalMove(...).SetEase(Ease.OutCubic)`; the shared tween runner
          // is linear, so ease the move -- and only the move -- here.
          const moveProgress = moveMs > 0 ? easeOutCubic(progress) : 1;
          root.x = targetX + enterOffset.x * (1 - moveProgress);
          root.y = targetY + enterOffset.y * (1 - moveProgress);
        },
        () => {
          root.alpha = 1;
          this.updateCharacterOpacity(state);
          root.x = targetX;
          root.y = targetY;
        },
      );

      void run;
    }
  }

  /**
   * Native `_ExecuteCharslot` assembles every tween into the slot's cached
   * Sequence and plays it right away: `_GetCachedSlotSeq` (0x183e4f830) hands
   * back a fresh `DOTween.Sequence()` whenever the previous one already ran,
   * and `DOTween.Sequence()` (0x184109110) starts it playing because
   * `defaultAutoPlay` is `AutoPlay.All` (cctor 0x18410b0e0). `end=false` only
   * skips the `OnComplete(_SetSeqPlayed)` + redundant `Play()` pair at
   * 0x183e4e501, which in turn is what gates `isblock` (0x183e4e56f) -- it
   * never defers the animation, so nothing is queued here. `isblock` instead
   * waits for the whole cached Sequence (see characterSlotSeqDeadlines), not
   * for this command's own tweens.
   */
  private async setCharacterSlot(input: CharacterSlotInput): Promise<void> {
    const slot = this.normalizeCharacterSlot(input.slot);
    let state = this.characterSlots.get(slot);
    const hasCharacter = Boolean(input.characterKey && input.expression);

    const durationMs = Math.max(0, Math.round(input.durationMs ?? 0));

    if (hasCharacter) {
      const built = await this.buildCharacterSlotState(input, slot, state);
      // Only a linkMap miss or a texture load failure lands here; the runtime
      // already turns an unresolvable `name` into a slot clear (native swaps
      // the Images before `_LoadImage`, so the old art fades out as the back
      // Image). Keep whatever the slot shows and still run the action/focus
      // sections, as `_UpdateSeqWithParam` does after a failed load.
      if (built) state = built;
    }

    // The deadline is anchored once the asset is in, i.e. when this command's
    // tweens actually start (native only inserts the crossfade tweens after
    // its synchronous load), so a slow load neither shortens the wait of a
    // later same-slot `isblock` nor this one's. Every action contributes
    // `durationMs`: DOLocalJump's duration spans all `times` jumps
    // (0x183eb2690), and `CharShake`/`CharZoom` are plain duration-length
    // tweens.
    let blockWaitMs = 0;
    if (input.slotSequence) {
      const nowMs = performance.now();
      const cachedDeadlineMs = this.characterSlotSeqDeadlines.get(slot) ?? 0;
      const seqDeadlineMs = Math.max(
        cachedDeadlineMs,
        nowMs,
        nowMs + durationMs,
      );
      this.characterSlotSeqDeadlines.set(slot, seqDeadlineMs);
      if (input.block) blockWaitMs = seqDeadlineMs - nowMs;
    }

    if (!state) {
      if (input.focusMode) this.applyCharacterSlotFocus(input.focusSlots ?? []);
      if (blockWaitMs > 0) await this.tween(blockWaitMs, () => {});
      return;
    }

    if (input.focusMode) this.applyCharacterSlotFocus(input.focusSlots ?? []);

    const move = characterSlotMove(state, input);
    const zoom = characterSlotZoom(state, input);

    // SlotMoveChar writes `localPosition = posFrom` when the tween is
    // created, so the start of a move lands on the spot even when the tween
    // itself has a duration.
    if (move) {
      state.actionX = move.from.x;
      state.actionY = move.from.y;
    }
    this.updateCharacterState(state);

    // `_GenSlotActionTw`: NeedSkipAnimation(duration) makes the shake branch
    // return null -- shake (and shakemove's shake half) only exists with a
    // positive duration.
    if (
      (input.action === "shake" || input.action === "shakemove") &&
      durationMs > 0
    ) {
      this.startShakeAction(state, {
        block: false,
        durationMs,
        power: Math.max(0, input.power ?? 0),
        randomness: input.randomness ?? 10,
        rotationFromDeg: 0,
        rotationLeftDeg: -15,
        rotationRightDeg: 15,
        slot,
        stop: Boolean(input.stop),
        times: input.times ?? 0,
        type: "shake",
        xOffset: 0,
        yOffset: 0,
      });
    }

    if (input.action === "jump")
      this.startJumpAction(
        state,
        Math.max(0, input.power ?? 0),
        input.times ?? 0,
        durationMs,
      );

    // Native drives rotation from the standalone `angle` parameter segment
    // at the tail of `_UpdateSeqWithParam`; `action="rotate"` itself is an
    // unknown action in 2.7.61's `_GenSlotActionTw` (LogError + null).
    if (input.angle !== undefined) {
      const sessionId = ++state.rotateSessionId;
      const fromAngle = state.rotationDeg;
      const direction = input.inverse ? -1 : 1;
      const toAngle = direction * (input.angle + 360 * (input.circles ?? 0));
      void this.tween(durationMs, (progress) => {
        if (
          !this.isActiveCharacterState(state) ||
          state.rotateSessionId !== sessionId
        )
          return;
        state.rotationDeg = fromAngle + (toAngle - fromAngle) * progress;
        this.updateCharacterState(state);
      });
    }

    this.applyCharacterSlotOpacity(state, input, durationMs);

    // The move (`_offset.localPosition`) and the zoom (the fore Image's
    // pivot/localScale) are independent tweens natively: an image swap
    // resets the zoom but leaves a running move alone, and a command whose
    // from == to starts nothing, so an in-flight tween of either kind
    // survives an unrelated command.
    if (move && (move.from.x !== move.to.x || move.from.y !== move.to.y)) {
      const sessionId = ++state.transformSessionId;
      const moveDurationMs = input.action === "setpos" ? 0 : durationMs;
      const { from, to } = move;
      void this.tween(
        moveDurationMs,
        (progress) => {
          if (!this.isActiveCharacterState(state, sessionId)) return;
          state.actionX = from.x + (to.x - from.x) * progress;
          state.actionY = from.y + (to.y - from.y) * progress;
          this.updateCharacterState(state);
        },
        () => {
          if (!this.isActiveCharacterState(state, sessionId)) return;
          state.actionX = to.x;
          state.actionY = to.y;
          this.updateCharacterState(state);
        },
      );
    }

    if (
      zoom &&
      (zoom.from.scaleX !== zoom.to.scaleX ||
        zoom.from.scaleY !== zoom.to.scaleY ||
        zoom.from.shiftX !== zoom.to.shiftX ||
        zoom.from.shiftY !== zoom.to.shiftY)
    ) {
      const sessionId = ++state.zoomSessionId;
      const { from, to } = zoom;
      void this.tween(
        durationMs,
        (progress) => {
          if (
            !this.isActiveCharacterState(state) ||
            state.zoomSessionId !== sessionId
          )
            return;
          state.scaleX = from.scaleX + (to.scaleX - from.scaleX) * progress;
          state.scaleY = from.scaleY + (to.scaleY - from.scaleY) * progress;
          state.zoomShiftX = from.shiftX + (to.shiftX - from.shiftX) * progress;
          state.zoomShiftY = from.shiftY + (to.shiftY - from.shiftY) * progress;
          this.updateCharacterState(state);
        },
        () => {
          if (
            !this.isActiveCharacterState(state) ||
            state.zoomSessionId !== sessionId
          )
            return;
          state.scaleX = to.scaleX;
          state.scaleY = to.scaleY;
          state.zoomShiftX = to.shiftX;
          state.zoomShiftY = to.shiftY;
          this.updateCharacterState(state);
        },
      );
    }

    // `isblock` waits for the slot's whole cached Sequence -- this command's
    // shake/jump/rotate tweens included, plus whatever a still-running
    // `end=false` enter on the same slot left in it -- not just move/zoom.
    if (blockWaitMs > 0) await this.tween(blockWaitMs, () => {});
  }

  /**
   * Port scope: `Torappu.AVG.CharacterPanel._ExecuteCharacterAction` and its
   * move/jump/shake/zoom/exit handlers (build 2761). Browser tween sampling
   * adapts DOTween, but keeps the per-action state and the completion
   * boundary: every branch funnels through `_AddFinishCommand`, so a blocking
   * command awaits the full native tween duration (shake included, and jump's
   * move+bounce sequence, not just the move segment).
   */
  async runCharacterAction(input: CharacterActionInput): Promise<void> {
    const state = this.characterSlots.get(
      this.normalizeCharacterSlot(input.slot),
    );
    if (!state) {
      this.onWarning?.(`missing character slot: ${input.slot}`);
      return;
    }

    switch (input.type) {
      case "move": {
        await this.runMoveAction(
          state,
          input.xOffset,
          input.yOffset,
          input.durationMs,
          input.block,
        );
        return;
      }
      case "jump": {
        // Native CharJump builds one DOLocalJump Sequence of total durationMs
        // and blocks on the whole sequence. Inside it x is Linear, the y
        // displacement is DOVirtual.EasedValue(..., Ease.OutQuad) over the
        // arc tween's elapsed percentage (`<DOLocalJump>b__7` @ 0x184265320),
        // and CharJump's SetEase(OutCubic) warps the whole sequence timeline
        // (`Sequence.DoApplyTween` @ 0x1842525c0 evaluates the sequence's own
        // ease before ApplyInternalCycle), so every axis sees OutCubic time.
        const moveRun = this.startMoveAction(
          state,
          input.xOffset,
          input.yOffset,
          input.durationMs,
          easeOutCubic,
          (progress) => easeOutQuad(easeOutCubic(progress)),
        );
        const jumpRun = this.startJumpAction(
          state,
          input.power,
          input.times,
          input.durationMs,
        );
        if (input.block && input.durationMs > 0) {
          await Promise.all([moveRun, jumpRun]);
        }
        return;
      }
      case "shake": {
        // Native CharShake returns the DOShakePosition waypoint tween of
        // durationMs and _AddFinishCommand blocks on it; the runtime already
        // degenerated durationMs<=0 to a move, so the tween always exists
        // here. `stop` is a web extension with no tween to wait for.
        const shakeRun = this.startShakeAction(state, input);
        if (input.block && !input.stop) await shakeRun;
        return;
      }
      case "zoom": {
        await this.runScaleAction(
          state,
          input.scaleX,
          input.scaleY,
          input.pivot,
          input.durationMs,
          input.block,
        );
        return;
      }
      case "exit": {
        await this.runExitAction(
          state,
          input.durationMs,
          input.direction,
          input.absolutePosition,
          input.block,
        );
      }
    }
  }

  async clearCharacters(slot?: string, fadeMs = 0): Promise<void> {
    if (slot) {
      const normalized = this.normalizeCharacterSlot(slot);
      const state = this.characterSlots.get(normalized);
      if (!state) return;
      this.characterSlots.delete(normalized);
      if (fadeMs > 0)
        await this.fadeOutAndRemove(state.root, fadeMs, () =>
          this.disposeCharacterState(state),
        );
      else this.disposeCharacterState(state);
      return;
    }

    const states = [...this.characterSlots.values()];
    this.characterSlots.clear();
    if (fadeMs > 0) {
      await Promise.all(
        states.map((state) =>
          this.fadeOutAndRemove(state.root, fadeMs, () =>
            this.disposeCharacterState(state),
          ),
        ),
      );
      return;
    }
    for (const state of states) this.disposeCharacterState(state);
  }

  async clearCurtains(fadeMs = 0, block = false): Promise<void> {
    const states = [...this.curtains.values()];
    if (states.length === 0) return;

    if (fadeMs <= 0) {
      for (const state of states)
        this.removeCurtainState(state.direction, state);
      return;
    }

    const run = Promise.all(
      states.map((state) =>
        this.setCurtain({
          alphaFrom: state.alpha,
          alphaTo: state.alpha,
          block,
          direction: state.direction,
          delayMs: 0,
          fadeMs,
          fillFrom: state.fill,
          fillTo: 0,
          // Native `HideCurtain` only tweens sizeDelta toward zero; it never
          // touches alpha or the `_gradientImg` active state, so the 20px
          // feather must stay on while the curtain retracts.
          grad: state.grad,
        }),
      ),
    );

    if (block) await run;
    else void run;
  }

  async clearSubtitle(fadeMs = 0): Promise<void> {
    this.subtitleTypingSessionId += 1;
    this.subtitleFadeSessionId += 1;
    this.subtitleTypingTarget = null;
    // Native `set_isHidden(true)` flips `m_hidden` immediately; the fade only
    // animates the visible alpha towards it.
    this.subtitleHidden = true;

    const subtitle = this.subtitleText;
    if (!subtitle) return;

    if (!subtitle.visible || !subtitle.text) {
      subtitle.text = "";
      subtitle.visible = false;
      subtitle.alpha = 1;
      return;
    }

    const sessionId = this.subtitleFadeSessionId;
    const startAlpha = subtitle.alpha;
    if (fadeMs <= 0) {
      subtitle.text = "";
      subtitle.visible = false;
      subtitle.alpha = 1;
      return;
    }

    await this.tween(
      fadeMs,
      (progress) => {
        if (this.subtitleFadeSessionId !== sessionId) return;
        subtitle.alpha = startAlpha * (1 - progress);
      },
      () => {
        if (this.subtitleFadeSessionId !== sessionId) return;
        subtitle.text = "";
        subtitle.visible = false;
        subtitle.alpha = 1;
      },
    );
  }

  /**
   * Native port: the `_ExecuteSticker` hide branch, which runs
   * `AVGStickerTextView.TryFinishType` before `HideSticker` — in-flight typing
   * completes instantly, so the full text is what fades out, not the
   * partially typed prefix.
   */
  async clearSticker(id?: string, fadeMs = 0): Promise<void> {
    if (!id) {
      await this.clearStickers(fadeMs);
      return;
    }

    this.hideSticker(id, fadeMs, true);
  }

  /**
   * Native port: `StickerPanel._RecycleStickers` (stickerclear and the skip
   * reset) calls `HideSticker(0)` on every view without `TryFinishType`, so
   * typing is not completed before the fade.
   */
  async clearStickers(fadeMs = 0): Promise<void> {
    this.stickerRichChars.clear();
    for (const id of this.stickerTexts.keys())
      this.hideSticker(id, fadeMs, false);
  }

  private hideSticker(id: string, fadeMs: number, finishTyping: boolean): void {
    const sticker = this.stickerTexts.get(id);
    if (!sticker) return;

    this.bumpStickerSessions(id);
    const typingTarget = this.stickerTypingTargets.get(id);
    if (finishTyping && typingTarget) {
      sticker.text = typingTarget.fullText;
      this.layoutSubtitle(
        sticker,
        typingTarget.baseX,
        typingTarget.widthPx,
        typingTarget.alignment,
      );
    }
    this.stickerTypingTargets.delete(id);
    this.stickerRichChars.delete(id);
    if (!sticker.visible || !sticker.text) {
      sticker.text = "";
      sticker.visible = false;
      sticker.alpha = 1;
      return;
    }

    const sessionId = this.stickerFadeSessionIds.get(id) ?? 0;
    const startAlpha = sticker.alpha;
    if (fadeMs <= 0) {
      sticker.text = "";
      sticker.visible = false;
      sticker.alpha = 1;
      return;
    }

    void this.tween(
      fadeMs,
      (progress) => {
        if ((this.stickerFadeSessionIds.get(id) ?? 0) !== sessionId) return;
        sticker.alpha = startAlpha * (1 - progress);
      },
      () => {
        if ((this.stickerFadeSessionIds.get(id) ?? 0) !== sessionId) return;
        sticker.text = "";
        sticker.visible = false;
        sticker.alpha = 1;
      },
    );
  }

  clearSpellStickers(): void {
    this.spellStickerPanel.clear();
  }

  hideSpellSticker(id: string): void {
    this.spellStickerPanel.hide(id);
  }

  /**
   * Port scope: `Torappu.AVG.AVGTimerView.StopTimer` (2.7.61 VA 0x183ed53e0):
   * `DOKill(_canvas, complete: false)` kills any in-flight fade tween without
   * completing it, then fades from the current alpha to a hard-coded 0 and
   * hides (never destroys) the view. The fade session counter is the DOKill
   * analogue: bumping it here retires the previous fade's step/done callbacks
   * so a timersticker fade-in still running when timerclear lands cannot keep
   * writing alpha alongside the fade-out.
   */
  async clearTimerSticker(input?: TimerClearInput): Promise<void> {
    this.clearTimerInterval();
    // `DOKill(_canvas, complete: false)`: an earlier fade is dropped mid-flight,
    // so a StopTimer fade it may have been running never reaches its OnComplete.
    this.timerFadeSessionId += 1;
    this.timerStopFadeComplete = null;

    const timer = this.timerStickerText;
    if (!timer) {
      this.timerTaskActive = false;
      return;
    }

    if (!input) {
      this.timerTaskActive = false;
      timer.text = "";
      timer.visible = false;
      timer.alpha = 1;
      return;
    }

    // `<StopTimer>b__7_0` is what actually retires the clock: it nulls
    // `m_countTimerTask` and deactivates the view. Until this runs the slot is
    // still considered to own a task, which is what makes a timersticker
    // arriving mid-fade reuse it instead of rebuilding it.
    const finishStopFade = (): void => {
      this.timerStopFadeComplete = null;
      this.clearTimerInterval();
      this.timerTaskActive = false;
      timer.alpha = 0;
      timer.visible = false;
    };

    const fromAlpha = timer.alpha;
    if (input.durationMs <= 0) {
      finishStopFade();
      return;
    }

    const fadeSessionId = this.timerFadeSessionId;
    this.timerStopFadeComplete = finishStopFade;
    void this.tween(
      input.durationMs,
      (progress) => {
        if (this.timerFadeSessionId !== fadeSessionId) return;
        timer.alpha = fromAlpha * (1 - progress);
      },
      () => {
        if (this.timerFadeSessionId !== fadeSessionId) return;
        finishStopFade();
      },
    );
  }

  /**
   * Port scope: `Torappu.AVG.AVGBlockerPanel._ExecuteBlocker`, including the
   * zero-duration and idempotent inverse branches. A PIXI sprite stands in for
   * the native blocker view.
   */
  async setBlocker(input: BlockerInput): Promise<void> {
    const blocker = this.ensureBlocker();
    if (input.style === "default" && input.image) {
      const url = resolveStoryAssetByKey(input.image, "image");
      if (url) {
        try {
          blocker.texture = await Assets.load<Texture>(url);
          blocker.width = STORY_WIDTH;
          blocker.height = STORY_HEIGHT;
          this.applyBlockerScaleSign(blocker);
        } catch {
          this.onWarning?.(`missing blocker image: ${input.image}`);
        }
      }
    } else if (input.style !== "default") {
      // Native slider/verticalslider set sprite = null (2.7.61 VA 0x183e30297):
      // the quad stays full-screen and the SlideMask material draws the wipe.
      // Texture.WHITE is the web equivalent of Unity's null-sprite white quad.
      blocker.texture = Texture.WHITE;
      blocker.width = STORY_WIDTH;
      blocker.height = STORY_HEIGHT;
      this.applyBlockerScaleSign(blocker);
      if (input.fadeMs > 0) {
        // _GenTweenerWithParam mounts slide_mask only on the animated path
        // (VA 0x183e30860); the zero-duration branch never touches the
        // material, so it persists from the previous command.
        await this.attachBlockerSlideFilter(blocker, input.style);
      }
    }

    const current = this.readBlockerColor();
    // `DOKill(_blocker, complete: false)` equivalent (2.7.61 VA 0x183e30522):
    // a new command kills the previous DOColor mid-flight, so at most one
    // blocker tween is ever active. Stale step/complete callbacks below are
    // dropped -- a stale complete with to.a ~= 0 would otherwise reset the
    // texture and hide the sprite under the new command.
    const sessionId = ++this.blockerTweenSessionId;
    const from = {
      a: Number.isFinite(input.from.a) ? input.from.a : current.a,
      b: Number.isFinite(input.from.b) ? input.from.b : current.b,
      g: Number.isFinite(input.from.g) ? input.from.g : current.g,
      r: Number.isFinite(input.from.r) ? input.from.r : current.r,
    };
    this.writeBlockerColor(blocker, from);

    if (input.fadeMs <= 0) {
      // The zero-fadetime path returns before the inverse block, so scale is
      // untouched here, and before _GenTweenerWithParam, so the material
      // (slide filter) keeps whatever the previous animated command mounted.
      this.writeBlockerColor(blocker, input.to);
      return;
    }

    // Animated default-style commands call _CleanMaterial (set_material(null))
    // inside _GenTweenerWithParam; slider styles mounted the filter above.
    if (input.style === "default") this.detachBlockerSlideFilter(blocker);

    // `AVGBlockerPanel._ExecuteBlocker` assigns literal -1 rather than negating
    // the current axis: repeated inverse commands are idempotent, and only reset
    // via OnReset. The sign lives in blockerScaleSign (PIXI's width setter
    // rewrites scale magnitudes), and the mask coordinate mirror follows it in
    // SlideMaskFilter's uFlip uniforms.
    if (input.inverse) {
      if (input.style === "slider") this.blockerScaleSign.x = -1;
      else if (input.style === "verticalslider") this.blockerScaleSign.y = -1;
      this.applyBlockerScaleSign(blocker);
    }

    const run = this.tween(
      input.fadeMs,
      (progress) => {
        if (sessionId !== this.blockerTweenSessionId) return;
        this.writeBlockerColor(blocker, {
          a: from.a + (input.to.a - from.a) * progress,
          b: from.b + (input.to.b - from.b) * progress,
          g: from.g + (input.to.g - from.g) * progress,
          r: from.r + (input.to.r - from.r) * progress,
        });
      },
      () => {
        if (sessionId !== this.blockerTweenSessionId) return;
        if (Math.abs(input.to.a) <= 1e-6) {
          // _FinishCommand's _ResetBlockerImg equivalent. Native restores the
          // serialized `_defaultBlocker` sprite (VA 0x183e30af0), not null;
          // the plain white quad is the standing web approximation of it.
          // The size write is required because the 1x1 Texture.WHITE would
          // otherwise inherit the previous texture's scale.
          blocker.texture = Texture.WHITE;
          blocker.width = STORY_WIDTH;
          blocker.height = STORY_HEIGHT;
          this.applyBlockerScaleSign(blocker);
          blocker.visible = false;
        }
      },
    );

    if (input.block) await run;
    else void run;
  }

  /**
   * Port scope: `Torappu.AVG.AVGCurtainPanel._ExecuteCurtain`. It keeps the
   * direction state, delayed size/alpha transition, and zero-duration branch;
   * Graphics rectangles are a Web/PIXI adaptation of `AVGCurtain` widgets.
   */
  async setCurtain(input: CurtainInput): Promise<void> {
    const direction = input.direction;
    const vector = this.resolveCurtainVector(direction);
    if (!vector) {
      this.onWarning?.(`unsupported curtain direction: ${input.direction}`);
      return;
    }

    const state = this.ensureCurtainState(direction);
    const fromFill = clamp01(input.fillFrom ?? state.fill);
    const toFill = clamp01(input.fillTo);
    // Native port: `_ExecuteCurtain` reads `afrom` (default: the alpha
    // captured *before* the snap) and then runs `SetCurtainAlpha(1.0)`
    // unconditionally, before choosing the instant/tween branch. The re-snap
    // to `afrom` only happens inside the a>=0 alpha tween
    // (`SetCurtainAlphaTween` snaps afrom while building the sequence). With
    // `a` absent the curtain therefore presents fully opaque no matter what
    // alpha an earlier command left behind, and a bare `afrom` is ignored.
    const capturedAlpha = clamp01(input.alphaFrom ?? state.alpha);
    const fromAlpha = input.alphaTo === undefined ? 1 : capturedAlpha;
    const toAlpha =
      input.alphaTo === undefined ? fromAlpha : clamp01(input.alphaTo);

    state.fill = fromFill;
    state.alpha = fromAlpha;
    state.grad = input.grad;
    this.updateCurtainState(state, vector);

    if (input.fadeMs <= 0) {
      state.fill = toFill;
      state.alpha = toAlpha;
      this.updateCurtainState(state, vector);
      if (toFill <= 0) this.removeCurtainState(direction, state);
      return;
    }

    // Native never actually cancels the side's previous tween. 2.7.61+ calls
    // DOKill(curtain component, complete: true) (2.7.81 0x183fc3023), whose
    // target matches neither tween's (_curtainRect / _canvasGroup); and even
    // 2.7.51's DOKill(transform, false) could not reach them, because both
    // tweens are nested in a Sequence and AddActiveTweenToSequence drops
    // nested tweens from the active list DOKill filters. A stale Sequence
    // keeps writing sizeDelta/alpha until it ends. The session bump is a
    // deliberate simplification (newest command owns the side), not a port.
    const sessionId = ++state.tweenSessionId;
    // Native SetCurtainSizeTween/SetCurtainAlphaTween carry no SetEase, so
    // both DOSizeDelta and DOFade run on DOTween's default OutQuad (2781
    // re-verified: DOTween cctor defaultEaseType = 6). Without the explicit
    // ease the TweenRunner default would be linear.
    const animate = () =>
      this.tween(
        input.fadeMs,
        (progress) => {
          if (!this.isActiveCurtainState(direction, state, sessionId)) return;
          state.fill = fromFill + (toFill - fromFill) * progress;
          state.alpha = fromAlpha + (toAlpha - fromAlpha) * progress;
          this.updateCurtainState(state, vector);
        },
        () => {
          if (!this.isActiveCurtainState(direction, state, sessionId)) return;
          state.fill = toFill;
          state.alpha = toAlpha;
          this.updateCurtainState(state, vector);
          if (toFill <= 0) this.removeCurtainState(direction, state);
        },
        { ease: dotweenDefaultEase },
      );
    const run =
      input.delayMs > 0
        ? new Promise<void>((resolve) =>
            setTimeout(resolve, input.delayMs),
          ).then(animate)
        : animate();

    if (input.block) await run;
    else void run;
  }

  /**
   * Port scope: `Torappu.AVG.AVGCameraEffect._ExecuteCameraEffect` for the
   * grayscale/inverse branches and their completion behavior. A PIXI color
   * filter substitutes for `AVGSceneEffectManager`'s camera post-process.
   *
   * Deliberately unported (verified on 2.7.61 / build 2761 IDA decompile):
   * - Chaos: dual-material distortion, intentionally skipped upstream.
   * - Reset lifecycle: native `OnReset`/`ShouldResetOnSkip=true` clears the
   *   effect record on story reset/skip; the web player rebuilds the renderer
   *   per playback instead, so no explicit reset exists here.
   * - Colorinverse amount is a shader `_Inverse` float natively (interpolable
   *   0..1, modeled below as `lerp(prev, 1-prev, inverse)` in the matrix).
   */
  async setCameraEffect(
    effect: "Colorinverse" | "Grayscale",
    amount: number,
    durationMs: number,
    block: boolean,
    keep: boolean,
    initialAmount?: number,
  ): Promise<void> {
    if (effect === "Colorinverse") {
      this.inverseAmount = amount;
      this.updateCameraFilter();
      return;
    }
    // Native `_TweenGrayscaleAmount` (2.7.61 VA 0x183E33C60): a negative
    // initamount (not just the omitted key's -1 default) starts from the
    // current grayscale amount via GetEffectAmount("grayscale", "grayscale");
    // other values are the explicit tween start. Native tests it with
    // MathUtil.LT(initAmount, 0), which decompiles to `-1e-5 > initAmount`, so
    // its real threshold sits an epsilon below 0; the plain `>= 0` here only
    // differs over [-1e-5, 0), which no story script hits.
    const from =
      initialAmount !== undefined && initialAmount >= 0
        ? initialAmount
        : this.grayscaleAmount;
    this.grayscaleAmount = from;
    this.updateCameraFilter();
    const run = this.tween(
      durationMs,
      (progress) => {
        // Native: the executor never calls SetEase, so DOTween.To tweens with
        // DOTween's default ease. This build ships DOTween 1.2.760, whose
        // cctor (VA 0x18410B0E0) writes defaultEaseType = 6 = Ease.OutQuad
        // (fast-then-slow), not linear. DOTween.AutoInit does let a
        // Resources/DOTweenSettings asset overwrite that field, and the asset
        // lives in the player's resources.assets rather than a hot-update
        // bundle so it cannot be read here — but OutQuad is also
        // DOTweenSettings' own default, so the compiled value stands.
        // DOTween's OutQuad is `-t*(t-2)`, i.e. the 1-(1-t)^2 below.
        const eased = 1 - (1 - progress) ** 2;
        this.grayscaleAmount = from + (amount - from) * eased;
        this.updateCameraFilter();
      },
      () => {
        this.grayscaleAmount = keep ? amount : 0;
        this.updateCameraFilter();
      },
    );
    if (block) await run;
    else void run;
  }

  async setFocusOut(input: FocusOutInput): Promise<void> {
    await this.focusEffectPanel.setFocus(input);
  }

  setFocusParam(input: FocusParamInput): void {
    this.focusEffectPanel.setParam(input);
  }

  private updateCameraFilter(): void {
    if (this.grayscaleAmount === 0 && this.inverseAmount === 0) {
      this.sceneLayer.filters = [];
      this.grayscaleFilter = null;
      return;
    }

    if (!this.grayscaleFilter) this.grayscaleFilter = new ColorMatrixFilter();

    // Native AVGSceneGrayScale blits the scene once with
    // AVG/[UC]Common/Arts/Materials/mat_grayscale, writing
    // `_Params = (0.299, 0.587, 0.114, grayAmount)` and `_Inverse`. See
    // buildColorEffectMatrix for the shader math and why pixi's
    // grayscale()/negative() helpers cannot express it.
    this.grayscaleFilter.matrix = buildColorEffectMatrix(
      this.grayscaleAmount,
      this.inverseAmount,
    );
    this.sceneLayer.filters = [this.grayscaleFilter];
  }

  private resolveFocusTargets(type: string, id: string): Container[] {
    switch (type) {
      case "bg": {
        return [this.backgroundLayer];
      }
      case "char": {
        return [this.charLayer];
      }
      // `AVGImagePanel._PostDisplayKey` registers ck_cg_1/2 for `image`'s
      // fore/back images, not CG objects.
      case "cg": {
        return [this.imageLayer];
      }
      // `LargeBackgroundPanel._PostDisplayKey` registers ck_lbg_1..4 over the
      // family-shared `_images` list, which `_LoadImage` fills for largebg /
      // verticalbg / gridbg alike. The Web port only tracks the composed
      // root, so focus effects apply to the whole puzzle rather than one tile.
      case "lbg": {
        return this.largeBackgroundRoot ? [this.largeBackgroundRoot] : [];
      }
      case "cgitem": {
        return this.cgItemPanel.targets(id);
      }
      case "customchar": {
        if (!id)
          return [...this.characterSlots.values()].map((state) => state.root);
        const state = this.characterSlots.get(this.normalizeCharacterSlot(id));
        return state ? [state.root] : [];
      }
      default: {
        return [];
      }
    }
  }

  /**
   * Port of `Torappu.AVG.AVGCameraEffect._ExecuteCameraShake`: it moves the
   * scene root, not independent visual layers. Path sampling is a Web/PIXI
   * adaptation of DOTween's shake tween.
   */
  async shakeCamera(input: CameraShakeInput): Promise<void> {
    this.stopCameraShake();

    if (input.stop || input.durationMs <= 0) return;

    const sessionId = ++this.cameraShakeSessionId;
    const path = buildShakePath(input.durationMs, input);
    const runCycle = async (): Promise<void> => {
      await this.tween(input.durationMs, (progress) => {
        if (sessionId !== this.cameraShakeSessionId) return;
        const offset = sampleShakePath(path, progress);
        this.sceneLayer.position.set(offset.x, offset.y);
      });
      if (sessionId !== this.cameraShakeSessionId) return;
      if (input.infinite) {
        void runCycle();
        return;
      }
      this.stopCameraShake(sessionId);
    };

    let wait: Promise<void> | undefined;
    if (input.block) {
      wait = new Promise<void>((resolve) => {
        this.cameraShakeWaitResolve = resolve;
      });
    }

    void runCycle();

    if (wait) {
      await wait;
      this.cameraShakeWaitResolve = null;
    }
  }

  /**
   * Port scope: `Torappu.AVG.SubtitlePanel._ExecuteSubtitle` normal-playback
   * semantics. PIXI text/typewriter scheduling adapts `AVGTypeWriterText`;
   * playback/reader-mode executor variants are intentionally not represented.
   */
  async setSubtitle(input: SubtitleInput): Promise<void> {
    const subtitle = this.subtitleText;
    if (!subtitle) return;

    this.subtitleTypingSessionId += 1;
    this.subtitleTypingTarget = null;

    // Native `SubtitlePanel._SetHiddenInternal` (2.7.61 VA 0x183e94ff0):
    // `set_isHidden(false)` is a no-op while the panel is already visible, so
    // consecutive subtitles swap text seamlessly without replaying the 150ms
    // Linear fade-in. A hidden panel fades in from its current alpha, which
    // also covers interrupting an in-flight fade-out (DOKill + DOFade).
    //
    // The fade session is only bumped when a fade actually starts: native's
    // no-op branch never reaches DOKill either, so a fade-in that is still
    // running when the next subtitle arrives must keep animating to 1. Bumping
    // it unconditionally would orphan that tween and strand the panel at
    // whatever alpha it had reached.
    if (this.subtitleHidden) {
      this.subtitleHidden = false;
      const startAlpha = subtitle.visible ? subtitle.alpha : 0;
      subtitle.visible = true;
      this.subtitleFadeSessionId += 1;
      const fadeSessionId = this.subtitleFadeSessionId;
      void this.tween(
        150,
        (progress) => {
          if (this.subtitleFadeSessionId === fadeSessionId)
            subtitle.alpha = startAlpha + (1 - startAlpha) * progress;
        },
        () => {
          if (this.subtitleFadeSessionId === fadeSessionId) subtitle.alpha = 1;
        },
      );
    }

    const prevChars: RichChar[] = [];
    const newChars = parseRichChars(input.text);
    const allChars = [...prevChars, ...newChars];

    const style = this.createOverlayTextStyle(input.sizePx, input.widthPx);
    // Native maps alignment to TextAnchor.UpperLeft/UpperCenter/UpperRight,
    // which horizontally aligns *every wrapped line* inside the width box;
    // PIXI needs the per-line `align` style in addition to the whole-block
    // offset that layoutSubtitle applies.
    style.align = input.alignment;
    // The transparent hidden tail below needs its tag registered while typing,
    // with the drop shadow disabled so the unrevealed text stays invisible.
    const tagStyles: NonNullable<TextStyle["tagStyles"]> =
      buildTagStyles(allChars);
    if (input.delayMs > 0)
      tagStyles[SUBTITLE_HIDDEN_TAIL_TAG] = { ...SUBTITLE_HIDDEN_TAIL_STYLE };
    if (Object.keys(tagStyles).length > 0) style.tagStyles = tagStyles;
    subtitle.style = style;
    subtitle.y = input.y;

    const fullText = richCharsToTaggedText(allChars);
    if (input.delayMs <= 0) {
      subtitle.text = fullText;
      this.layoutSubtitle(subtitle, input.x, input.widthPx, input.alignment);
      // Nothing to type: the typewriter is done the moment the text is shown.
      input.onTypingComplete?.();
      return;
    }

    const sessionId = this.subtitleTypingSessionId;
    // Native `AVGTypeWriterText.BeginText` lays out the full message from t0
    // by keeping the unrevealed tail in the text wrapped in a fully
    // transparent <color=#00000000> span; revealing chars never re-wraps the
    // lines that are already on screen.
    subtitle.text = richCharsToTaggedText([
      ...prevChars,
      ...subtitleHiddenTail(newChars),
    ]);
    this.layoutSubtitle(subtitle, input.x, input.widthPx, input.alignment);
    this.subtitleTypingTarget = {
      alignment: input.alignment,
      baseX: input.x,
      fullText,
      widthPx: input.widthPx,
    };
    void this.runSubtitleTyping(sessionId, subtitle, {
      alignment: input.alignment,
      baseX: input.x,
      delayMs: input.delayMs,
      newChars,
      onTypingComplete: input.onTypingComplete,
      prevChars,
      widthPx: input.widthPx,
    });
  }

  /**
   * Port scope: `Torappu.AVG.StickerPanel._ExecuteSticker` and its append,
   * fade, and typewriter state. PIXI Text replaces the native sticker prefab.
   * Append must leave the view untouched except for the appended text, so the
   * caller replays the previous show's layout fields (the runtime stores them
   * because the native executor's append path reads no layout parameters).
   */
  async setSticker(input: StickerInput): Promise<void> {
    const sticker = this.ensureStickerText(input.id);
    const wasActive = this.stickerRichChars.has(input.id);
    this.bumpStickerSessions(input.id);
    sticker.alpha = wasActive ? sticker.alpha : 0;
    sticker.visible = true;

    if (!wasActive) {
      const fadeSessionId = this.stickerFadeSessionIds.get(input.id) ?? 0;
      void this.tween(
        input.fadeMs,
        (progress) => {
          if ((this.stickerFadeSessionIds.get(input.id) ?? 0) === fadeSessionId)
            sticker.alpha = progress;
        },
        () => {
          if ((this.stickerFadeSessionIds.get(input.id) ?? 0) === fadeSessionId)
            sticker.alpha = 1;
        },
      );
    }

    const prevChars = input.append
      ? (this.stickerRichChars.get(input.id) ?? [])
      : [];
    const newChars = parseRichChars(input.text);
    const allChars = [...prevChars, ...newChars];
    this.stickerRichChars.set(input.id, allChars);

    const style = this.createOverlayTextStyle(input.sizePx, input.widthPx);
    style.tagStyles = buildTagStyles(allChars);
    sticker.style = style;
    sticker.y = input.y;

    const fullText = richCharsToTaggedText(allChars);
    if (input.delayMs <= 0) {
      sticker.text = fullText;
      this.layoutSubtitle(sticker, input.x, input.widthPx, input.alignment);
      input.onTypingComplete?.();
      return;
    }

    const sessionId = this.stickerTypingSessionIds.get(input.id) ?? 0;
    sticker.text = richCharsToTaggedText(prevChars);
    this.layoutSubtitle(sticker, input.x, input.widthPx, input.alignment);
    this.stickerTypingTargets.set(input.id, {
      alignment: input.alignment,
      baseX: input.x,
      fullText,
      widthPx: input.widthPx,
    });
    void this.runStickerTyping(input.id, sessionId, sticker, {
      alignment: input.alignment,
      baseX: input.x,
      delayMs: input.delayMs,
      prevChars,
      newChars,
      onTypingComplete: input.onTypingComplete,
      widthPx: input.widthPx,
    });
  }

  setSpellSticker(input: SpellStickerInput): void {
    this.spellStickerPanel.show(input);
  }

  /**
   * Native port: Torappu.AVG.StickerPanel._ExecuteStickerTween (2.7.61 build
   * 2761, VA 0x183e92110) driving AVGStickerTextView.ExecuteTween<T>
   * (VA 0x184165be0 / 0x184165940). Each non-empty group is one
   * SequenceBuilder.Push step in native order (position, rotation, alpha);
   * `isend=false` steps only accumulate, and the first `isend=true` command
   * builds and plays the whole sequence -- mirrored here by a per-id pending
   * queue. Web adaptation: instead of a DOTween Sequence, every step gets a
   * time window inside a single tween of the total duration; a `join` step
   * starts when the previous step started, otherwise after it ends (DOTween
   * Join vs Append). Durations were already animateRatio-scaled by the
   * runtime, which is the documented visual equivalent of the native
   * `Sequence.timeScale = animateRatio` assignment. Rotation is degrees in
   * native localEulerAngles and radians in pixi, hence the conversion.
   */
  stickerTween(input: StickerTweenInput): void {
    const queue = this.stickerTweenQueues.get(input.id) ?? [];
    if (input.position)
      queue.push({ ...input.position, join: input.join, kind: "position" });
    if (input.rotation)
      queue.push({ ...input.rotation, join: input.join, kind: "rotation" });
    if (input.alpha)
      queue.push({ ...input.alpha, join: input.join, kind: "alpha" });

    if (!input.isend) {
      this.stickerTweenQueues.set(input.id, queue);
      return;
    }

    this.stickerTweenQueues.delete(input.id);
    const sticker = this.stickerTexts.get(input.id);
    if (!sticker || queue.length === 0) return;

    // Starting a new sequence bumps the session: older in-flight sticker
    // tweens for this id stop applying (native lets two sequences fight over
    // the transform; skipping that glitch is a deliberate web adaptation).
    const sessionId = (this.stickerTweenSessionIds.get(input.id) ?? 0) + 1;
    this.stickerTweenSessionIds.set(input.id, sessionId);
    const isCurrent = () =>
      (this.stickerTweenSessionIds.get(input.id) ?? 0) === sessionId;

    const steps = queue;

    // Append/Join scheduling: `join` shares the previous step's start,
    // everything else starts after the longest step of its segment ends.
    const windows: Array<{ start: number; step: (typeof steps)[number] }> = [];
    let segmentStart = 0;
    let segmentLongest = 0;
    let open = false;
    for (const step of steps) {
      if (!open || !step.join) {
        segmentStart += segmentLongest;
        segmentLongest = 0;
        open = true;
      }
      windows.push({ start: segmentStart, step });
      segmentLongest = Math.max(segmentLongest, step.durationMs);
    }
    const totalMs = segmentStart + segmentLongest;

    const apply = (step: (typeof steps)[number], progress: number): void => {
      if (step.kind === "position") {
        sticker.x = step.from.x + (step.to.x - step.from.x) * progress;
        sticker.y = step.from.y + (step.to.y - step.from.y) * progress;
        return;
      }
      if (step.kind === "alpha") {
        sticker.alpha = step.from + (step.to - step.from) * progress;
        return;
      }
      const deg = step.from + (step.to - step.from) * progress;
      sticker.rotation = (deg * Math.PI) / 180;
    };

    // `BuildSequence` creates every tween up front, and each
    // `AVGTweenFactory._Create*Tween` snaps the transform to that step's
    // `from` right there -- before the sequence plays and regardless of when
    // the step's own window opens. So all the snaps happen now, and the
    // clamped window below keeps a not-yet-started step pinned to `from`.
    for (const { step } of windows) apply(step, 0);

    void this.tween(
      totalMs,
      (progress) => {
        if (!isCurrent()) return;
        const elapsed = progress * totalMs;
        for (const { start, step } of windows) {
          const local =
            step.durationMs <= 0
              ? 1
              : Math.min(1, Math.max(0, (elapsed - start) / step.durationMs));
          apply(step, local);
        }
      },
      () => {
        if (!isCurrent()) return;
        for (const { step } of windows) apply(step, 1);
      },
    );
  }

  /**
   * Port scope: `Torappu.AVG.StickerPanel._ExcuteTimerSticker` (native spelling)
   * and `AVGTimerView.RenderTimer`. Browser intervals and PIXI text adapt the
   * native timer view; this command itself never supplies a block boundary.
   * The step order below is RenderTimer's own: transform and font first, then
   * `_StartCountTimer`, then the `_ShowTimer` fade, and only after RenderTimer
   * returns does `_ExcuteTimerSticker` re-activate the view.
   */
  async setTimerSticker(input: TimerStickerInput): Promise<void> {
    const timer = this.ensureTimerStickerText();

    timer.style = this.createOverlayTextStyle(input.sizePx, input.widthPx);
    timer.x = input.x;
    timer.y = input.y;

    // RenderTimer guards `_StartCountTimer` with `time > 0`; with the parameter
    // absent (native default -1) neither the text nor a running clock is
    // touched.
    if (input.limitSeconds !== undefined && input.limitSeconds > 0)
      this.startTimerCountUp(timer, input.limitSeconds);

    // `DOKill(_canvas, complete: true)`, the asymmetric counterpart of
    // StopTimer's `complete: false`: it *finishes* a timerclear fade still in
    // flight, which runs `<StopTimer>b__7_0` and nulls `m_countTimerTask` --
    // discarding the clock restarted just above, so the text stays frozen at
    // its last value while the view still fades back in.
    this.timerFadeSessionId += 1;
    this.timerStopFadeComplete?.();

    timer.alpha = input.fromAlpha;
    const fadeSessionId = this.timerFadeSessionId;
    void this.tween(
      input.durationMs > 0 ? input.durationMs : 130,
      (progress) => {
        if (this.timerFadeSessionId !== fadeSessionId) return;
        timer.alpha =
          input.fromAlpha + (input.toAlpha - input.fromAlpha) * progress;
      },
      () => {
        if (this.timerFadeSessionId !== fadeSessionId) return;
        timer.alpha = input.toAlpha;
      },
    );

    // `SetActiveIfNecessary(gameObject, true)` trails RenderTimer, so it undoes
    // the deactivation a completed StopTimer fade may have just performed.
    timer.visible = true;
  }

  /**
   * Port scope: `Torappu.AVG.AVGTimerView._StartCountTimer` (2.7.61 VA
   * 0x183ed5830) plus the `CountDownTask` it drives.
   *
   * Native is a stopwatch, not a countdown: `SetCountDown` (0x181cefc50) stores
   * startTime = now, endTime = now + time*1000 (ms) on a wall clock, and
   * `Update` feeds `_OverrideTimerTaskTick` = `Math.Max(curTime - startTime, 0)`
   * -- elapsed milliseconds, verified at the instruction level in build 2761
   * (an earlier reading of `endTime - curTime` had the operands swapped) -- into
   * `_TimerTick` = TimeSpan.FromMilliseconds(...). The text therefore climbs
   * 00:00:00 -> 00:00:01 -> ...; reaching `time` fires `_TimerEnd`, which only
   * clears the task, so the display freezes at the cap (time=9999 -> 02:46:39)
   * and stays visible. Deriving elapsed from the wall clock also avoids interval
   * drift under browser throttling.
   */
  private startTimerCountUp(timer: Text, capSeconds: number): void {
    this.clearTimerInterval();

    if (!this.timerTaskActive) {
      // The inline `_TimerTick(0)` that seeds 00:00:00 lives in the branch that
      // builds the task, so a slot whose clock is still alive is merely re-based
      // by `SetCountDown` and keeps showing its current value.
      this.timerTaskActive = true;
      timer.text = this.formatTimer(0);
    }

    const startMs = Date.now();
    this.timerStickerInterval = setInterval(
      () => {
        const elapsedSeconds = Math.min(
          capSeconds,
          // `Math.Max(..., 0)`: a wall clock can step backwards.
          Math.max(0, Math.floor((Date.now() - startMs) / 1000)),
        );
        const timerText = this.timerStickerText;
        if (!timerText) return;

        const text = this.formatTimer(elapsedSeconds);
        if (timerText.text !== text) timerText.text = text;
        if (elapsedSeconds >= capSeconds) {
          this.clearTimerInterval();
          this.timerTaskActive = false;
        }
      },
      // 200ms mirrors the native CountDownTask internal tick; the wall-clock
      // math makes extra fires harmless (same floor value, no text rewrite).
      200,
    );
  }

  private async createUi(): Promise<void> {
    await this.dialogPanel.mount();
    this.subtitleText = new Text({
      style: this.createOverlayTextStyle(18, 675),
      text: "",
    });
    this.subtitleText.visible = false;
    this.timerStickerText = new Text({
      style: this.createOverlayTextStyle(18, 220),
      text: "",
    });
    this.timerStickerText.visible = false;

    this.uiLayer.addChild(this.subtitleText);
    this.uiLayer.addChild(this.timerStickerText);
  }

  private ensureBlocker(): Sprite {
    if (this.blockerSprite) return this.blockerSprite;

    const blocker = new Sprite(Texture.WHITE);
    // Centered anchor: the persistent inverse flip (localScale = -1) mirrors
    // the quad around its center, exactly like the native stretch-anchored
    // RectTransform, so coverage stays the full 1280x720 either sign.
    blocker.anchor.set(0.5);
    blocker.position.set(STORY_WIDTH / 2, STORY_HEIGHT / 2);
    blocker.width = STORY_WIDTH;
    blocker.height = STORY_HEIGHT;
    blocker.tint = 0x00_00_00;
    blocker.alpha = 0;

    this.blockerSprite = blocker;
    // Native hierarchy: panel_blocker (sibling 1) renders *below*
    // panel_curtains (sibling 3), so curtains cover the blocker when both are
    // up (black-mask pass, 2.7.61 scene layout). Insert just below the
    // curtains container instead of appending on top of it. `indexOf` rather
    // than `getChildIndex`, which throws when curtains has no parent yet
    // (LayerGraph.attach runs in mount, so only an un-mounted harness hits
    // that) -- there, appending is the right fallback.
    const curtainIndex = this.worldLayer.children.indexOf(this.curtainLayer);
    this.worldLayer.addChildAt(
      blocker,
      curtainIndex === -1 ? this.worldLayer.children.length : curtainIndex,
    );

    return blocker;
  }

  /**
   * Push the persistent localScale sign onto the sprite and the wipe shader.
   * Called after every width/height write, which recomputes the scale
   * magnitude from the new texture's local bounds (PIXI's size setters do
   * carry the existing sign across, so this is a no-op re-assert there; it is
   * load-bearing when `blockerScaleSign` itself just changed, and it is the
   * single place SlideMaskFilter's uFlip stays in sync).
   */
  private applyBlockerScaleSign(blocker: Sprite): void {
    blocker.scale.x = this.blockerScaleSign.x * Math.abs(blocker.scale.x);
    blocker.scale.y = this.blockerScaleSign.y * Math.abs(blocker.scale.y);
    this.blockerSlideFilter?.setFlip(
      this.blockerScaleSign.x < 0,
      this.blockerScaleSign.y < 0,
    );
  }

  /**
   * `_SetMaterial(slide_mask)` + ENABLE_VERTICAL keyword equivalent. Loads the
   * bundled `slide_left` mask once; on failure the wipe degrades to the plain
   * whole-surface veil (warning) instead of blocking playback.
   */
  private async attachBlockerSlideFilter(
    blocker: Sprite,
    style: "slider" | "verticalslider",
  ): Promise<void> {
    if (!this.blockerMaskSource && !this.blockerMaskSourceFailed) {
      try {
        const texture = await Assets.load<Texture>(SLIDE_MASK_TEXTURE_URL);
        // Native sampler: bilinear + clamp wrap (Texture2D slide_left
        // settings), which is PIXI's default texture style.
        this.blockerMaskSource = texture.source;
      } catch {
        this.blockerMaskSourceFailed = true;
        this.onWarning?.("missing blocker slide mask texture");
      }
    }
    const source = this.blockerMaskSource;
    if (!source) return;

    this.blockerSlideMaterial ??= new SlideMaskFilter(source);
    this.blockerSlideFilter = this.blockerSlideMaterial;
    this.blockerSlideFilter.setVertical(style === "verticalslider");
    this.applyBlockerScaleSign(blocker);
    blocker.filters = [this.blockerSlideFilter];
  }

  /** `_CleanMaterial` (set_material(null)) equivalent for default styles. */
  private detachBlockerSlideFilter(blocker: Sprite): void {
    if (!this.blockerSlideFilter) return;
    blocker.filters = [];
    // Null the mount so writeBlockerColor returns to sprite.alpha blending;
    // blockerSlideMaterial keeps the instance for the next slider command,
    // the way native re-assigns the same cached Material asset.
    this.blockerSlideFilter = null;
    // While mounted, sprite.alpha was pinned to 1 and the wipe owned the
    // alpha. Hand it straight back here: the caller's next color write is a
    // tween step one frame away, so leaving 1 in place flashes a fully opaque
    // blocker for that frame.
    blocker.alpha = clamp01(this.blockerColor.a);
  }

  private ensureCurtainState(direction: number): CurtainRenderState {
    const existing = this.curtains.get(direction);
    if (existing) return existing;

    const graphic = new Graphics();
    graphic.visible = false;
    this.curtainLayer.addChild(graphic);

    const state: CurtainRenderState = {
      alpha: 1,
      direction,
      fill: 0,
      grad: false,
      graphic,
      tweenSessionId: 0,
    };
    this.curtains.set(direction, state);
    return state;
  }

  private updateCurtainState(
    state: CurtainRenderState,
    vector: { x: number; y: number },
  ): void {
    state.graphic.alpha = clamp01(state.alpha);
    state.graphic.clear();

    if (state.graphic.alpha <= 0 || state.fill <= 0) {
      state.graphic.visible = false;
      return;
    }

    const threshold = this.curtainThreshold(
      state.direction,
      vector,
      state.fill,
    );
    if (threshold === null) {
      state.graphic.visible = false;
      return;
    }

    // The curtain body is a solid black quad (_curtainImg is sprite_white tinted
    // (0,0,0,1)). `grad` adds a separate CURTAIN_GRADIENT_PX strip on the inner
    // edge, opaque on the curtain side and fully transparent toward the screen
    // centre -- it does not fade the whole body.
    //
    // The body length comes from each AVGCurtain's layout group. Straight
    // edges always pad the inner edge by 20px and their gradient ignores
    // layout, sitting in that padding -- so without `grad` the inner 20px of
    // the rect stays empty. Corner layouts have no padding and the gradient
    // is a 20px layout child, so it only takes room from the body while
    // active.
    const inset =
      state.grad || !CURTAIN_CORNER_HINGES[state.direction]
        ? CURTAIN_GRADIENT_PX
        : 0;
    const bodyEnd = threshold - inset;
    const body = this.buildCurtainBand(vector, null, bodyEnd);
    const feather = state.grad
      ? this.buildCurtainBand(vector, bodyEnd, threshold)
      : [];
    // Corner hinges sit off-stage, so small fills clip away entirely.
    state.graphic.visible = body.length >= 6 || feather.length >= 6;

    if (body.length >= 6) state.graphic.poly(body).fill(0x00_00_00);
    if (feather.length < 6) return;

    state.graphic.poly(feather).fill(
      new FillGradient({
        colorStops: [
          { color: "rgba(0, 0, 0, 1)", offset: 0 },
          { color: "rgba(0, 0, 0, 0)", offset: 1 },
        ],
        end: { x: vector.x * threshold, y: vector.y * threshold },
        start: { x: vector.x * bodyEnd, y: vector.y * bodyEnd },
        textureSpace: "global",
        type: "linear",
      }),
    );
  }

  /**
   * Projection along `vector` where the curtain's inner edge sits. Straight
   * edges hinge on the stage edge, so `fill` spans the stage; corners hinge
   * off-stage at CURTAIN_CORNER_HINGES and grow by the 1600px native side.
   */
  private curtainThreshold(
    direction: number,
    vector: { x: number; y: number },
    fill: number,
  ): number | null {
    const clampedFill = clamp01(fill);
    if (clampedFill <= 0) return null;

    const hinge = CURTAIN_CORNER_HINGES[direction];
    if (hinge)
      return (
        hinge.x * vector.x +
        hinge.y * vector.y +
        CURTAIN_CORNER_SIDE * clampedFill
      );

    const projections = CURTAIN_STAGE_CORNERS.map(
      (point) => point.x * vector.x + point.y * vector.y,
    );
    const min = Math.min(...projections);
    const max = Math.max(...projections);
    if (Math.abs(max - min) <= 1e-6) return null;

    return min + (max - min) * clampedFill;
  }

  /** Stage rect clipped to `low <= projection <= high`; `null` low means unbounded. */
  private buildCurtainBand(
    vector: { x: number; y: number },
    low: number | null,
    high: number,
  ): number[] {
    let polygon = this.clipCurtainPolygon(
      [...CURTAIN_STAGE_CORNERS],
      vector,
      high,
    );
    if (low !== null) {
      polygon = this.clipCurtainPolygon(
        polygon,
        { x: -vector.x, y: -vector.y },
        -low,
      );
    }
    if (polygon.length < 3) return [];
    return polygon.flatMap((point) => [point.x, point.y]);
  }

  private clipCurtainPolygon(
    polygon: Array<{ x: number; y: number }>,
    vector: { x: number; y: number },
    threshold: number,
  ): Array<{ x: number; y: number }> {
    const output: Array<{ x: number; y: number }> = [];
    const last = polygon.at(-1);
    if (!last) return output;
    let previous = last;
    let previousInside = this.isCurtainPointInside(previous, vector, threshold);

    for (const current of polygon) {
      const currentInside = this.isCurtainPointInside(
        current,
        vector,
        threshold,
      );
      if (currentInside !== previousInside)
        output.push(
          this.intersectCurtainEdge(previous, current, vector, threshold),
        );
      if (currentInside) output.push(current);
      previous = current;
      previousInside = currentInside;
    }

    return output;
  }

  private isCurtainPointInside(
    point: { x: number; y: number },
    vector: { x: number; y: number },
    threshold: number,
  ): boolean {
    return point.x * vector.x + point.y * vector.y <= threshold + 1e-6;
  }

  private intersectCurtainEdge(
    start: { x: number; y: number },
    end: { x: number; y: number },
    vector: { x: number; y: number },
    threshold: number,
  ): { x: number; y: number } {
    const startProjection = start.x * vector.x + start.y * vector.y;
    const endProjection = end.x * vector.x + end.y * vector.y;
    const delta = endProjection - startProjection;
    if (Math.abs(delta) <= 1e-6) return { ...start };

    const ratio = (threshold - startProjection) / delta;
    return {
      x: start.x + (end.x - start.x) * ratio,
      y: start.y + (end.y - start.y) * ratio,
    };
  }

  /**
   * Inward half-plane normal per direction, following `_curtainWidgets`
   * order (0 top, then clockwise). The corner diagonals are the native 45°
   * rect rotation, not the stage diagonal.
   */
  private resolveCurtainVector(
    direction: number,
  ): { x: number; y: number } | null {
    const mappedAngles: Record<number, number> = {
      0: 180,
      1: 225,
      2: 270,
      3: 315,
      4: 0,
      5: 45,
      6: 90,
      7: 135,
    };

    const angleDeg = mappedAngles[direction];
    if (angleDeg === undefined) return null;

    const radians = (angleDeg * Math.PI) / 180;
    return {
      x: Math.sin(radians),
      y: -Math.cos(radians),
    };
  }

  private async buildCharacterVisual(
    characterKey: string,
    expression: string,
    blackStart?: number,
    blackEnd?: number,
  ): Promise<CharacterBuiltVisual | null> {
    const link = this.context.linkMap[characterKey];
    if (!link) {
      this.onWarning?.(`missing character base: ${characterKey}`);
      return null;
    }

    const item = link.array.find((entry) => entry.name === expression);
    if (!item) {
      this.onWarning?.(
        `missing character expression: ${characterKey}#${expression}`,
      );
      return null;
    }

    const visual = new Container();
    const content = new Container();
    visual.addChild(content);

    let sourceWidth: number;
    let sourceHeight: number;
    // Every sprite that contributes character pixels, in draw order. Native
    // composites the face into the *same* material as the body (`SetSprite`
    // feeds the face through `_HGDynamicTex`), so alpha (`DOColor` on the fore
    // Image's vertex colour) and `_BlackStart`/`_BlackEnd` multiply the
    // already-composited pixels. The web port must bake the face onto the body
    // up front for the same reason: two stacked sprites would each carry the
    // fade alpha separately, making the face region's effective opacity
    // 1-(1-p)^2 instead of p -- the face would fade in ahead of the body.
    const contentSprites: Sprite[] = [];

    if (item.group === -1 && "image" in item && item.image) {
      const texture = await this.textureForCharacterKey(item.image);
      if (!texture) return null;

      const sprite = new Sprite(texture);
      sprite.anchor.set(0, 0);
      content.addChild(sprite);
      contentSprites.push(sprite);
      sourceWidth = texture.width;
      sourceHeight = texture.height;
    } else if (item.group >= 0 && "face" in item && item.face) {
      const group = link.groups[item.group];
      if (!group || group.mode !== "face_overlay") {
        this.onWarning?.(
          `unsupported character group: ${characterKey}#${expression}`,
        );
        return null;
      }

      const [baseTexture, faceTexture] = await Promise.all([
        this.textureForCharacterKey(group.base),
        this.textureForCharacterKey(item.face),
      ]);
      if (!baseTexture || !faceTexture) return null;

      const texture = this.bakeFaceOverlayTexture(
        group.base,
        item.face,
        baseTexture,
        faceTexture,
        group.faceRect,
      );
      if (!texture) return null;

      const sprite = new Sprite(texture);
      sprite.anchor.set(0, 0);
      content.addChild(sprite);
      contentSprites.push(sprite);
      this.retainFaceOverlay(
        visual,
        faceOverlayCacheKey(group.base, item.face),
      );
      sourceWidth = baseTexture.width;
      sourceHeight = baseTexture.height;
    } else {
      this.onWarning?.(`invalid character item: ${characterKey}#${expression}`);
      return null;
    }

    this.applyCharacterBlackGradient(
      content,
      contentSprites,
      sourceWidth,
      sourceHeight,
      blackStart,
      blackEnd,
    );
    return { sourceHeight, sourceWidth, visual };
  }

  private async buildCharacterSlotState(
    input: CharacterSlotInput,
    slot: string,
    current?: CharacterRenderState,
  ): Promise<CharacterRenderState | null> {
    if (!input.characterKey || !input.expression) return current ?? null;

    const link = this.context.linkMap[input.characterKey];
    if (!link) {
      this.onWarning?.(`missing character base: ${input.characterKey}`);
      return null;
    }

    const built = await this.buildCharacterVisual(
      input.characterKey,
      input.expression,
      input.blackStart,
      input.blackEnd,
    );
    if (!built) return null;

    const slotBaseX: Record<string, number> = { l: 440, m: 640, r: 840 };
    const sizeX = link.size.x || built.sourceWidth;
    const sizeY = link.size.y || built.sourceHeight;
    const offsetX = link.pos.x || 0;
    const offsetY = link.pos.y || 0;
    const baseX = (slotBaseX[slot] ?? slotBaseX.m) - sizeX / 2 + offsetX;
    const baseY = STORY_HEIGHT - sizeY / 2 - offsetY;
    const baseScaleX = sizeX / Math.max(1, built.sourceWidth);
    const baseScaleY = sizeY / Math.max(1, built.sourceHeight);

    if (!current) {
      const root = new Container();
      const motionLayer = new Container();
      const rotationLayer = new Container();
      rotationLayer.addChild(built.visual);
      motionLayer.addChild(rotationLayer);
      root.addChild(motionLayer);
      root.x = baseX;
      root.y = baseY;
      rotationLayer.pivot.set(built.sourceWidth / 2, built.sourceHeight / 2);
      rotationLayer.position.set(built.sourceWidth / 2, built.sourceHeight / 2);

      const state: CharacterRenderState = {
        actionX: 0,
        actionY: 0,
        baseScaleX,
        baseScaleY,
        baseX,
        baseY,
        characterKey: input.characterKey,
        contentAlpha: 1,
        focusBrightness: 1,
        expression: input.expression,
        fadeIdentity: input.fadeIdentity ?? input.characterKey,
        height: sizeY,
        jumpOffsetY: 0,
        jumpSessionId: 0,
        motionLayer,
        nativeKey: input.nativeKey,
        opacitySessionId: 0,
        outgoingVisual: null,
        replaceFadeSessionId: 0,
        root,
        rotationDeg: 0,
        rotationLayer,
        rotateSessionId: 0,
        rotateTimeout: null,
        scaleX: 1,
        scaleY: 1,
        shakeOffsetX: 0,
        shakeOffsetY: 0,
        shakeSessionId: 0,
        slot,
        sourceHeight: built.sourceHeight,
        sourceWidth: built.sourceWidth,
        transformSessionId: 0,
        visual: built.visual,
        width: sizeX,
        zoomSessionId: 0,
        zoomShiftX: 0,
        zoomShiftY: 0,
      };
      this.updateCharacterState(state);
      this.updateCharacterOpacity(state);
      this.characterSlots.set(slot, state);
      this.charLayer.addChild(root);
      return state;
    }

    const previousVisual = current.visual;
    // Native keeps one back Image; a swap mid-crossfade replaces it outright.
    if (current.outgoingVisual && current.outgoingVisual !== previousVisual)
      this.discardCharacterVisual(current.outgoingVisual);
    current.outgoingVisual = null;

    // `AVGCharacterSpriteHub._SetImage` (0x183ec8f50) and its HubGroup twin
    // (0x183ec8020) both open with `GUIUtils.AssignLocalSettings(
    // m_image.rectTransform, hub.rectTransform)` (0x181f02c70), which copies
    // the hub prefab's sizeDelta/pivot/localScale/localRotation/localPosition
    // onto the fore Image on every successful load -- exactly the pivot and
    // localScale CharZoom's setter animates. A named swap therefore resets
    // the zoom while the `_offset` move survives (resetOffsetPos=false). The
    // outgoing Image keeps its own transform as it fades, so the previous
    // visual carries the old zoom on itself (scaled back through the base
    // ratio because the motion layer now holds the incoming art's base).
    if (previousVisual !== built.visual) {
      previousVisual.scale.set(
        (current.scaleX * current.baseScaleX) / Math.max(1e-6, baseScaleX),
        (current.scaleY * current.baseScaleY) / Math.max(1e-6, baseScaleY),
      );
      previousVisual.position.set(
        current.zoomShiftX / Math.max(1e-6, baseScaleX),
        -current.zoomShiftY / Math.max(1e-6, baseScaleY),
      );
    }
    current.zoomSessionId += 1;
    current.scaleX = 1;
    current.scaleY = 1;
    current.zoomShiftX = 0;
    current.zoomShiftY = 0;

    current.baseScaleX = baseScaleX;
    current.baseScaleY = baseScaleY;
    current.baseX = baseX;
    current.baseY = baseY;
    current.characterKey = input.characterKey;
    current.expression = input.expression;
    current.fadeIdentity = input.fadeIdentity ?? input.characterKey;
    current.height = sizeY;
    // `_SlotSetCharInternal` writes `m_currentKey = options.charName` too.
    current.nativeKey = input.nativeKey;
    current.root.x = baseX;
    current.root.y = baseY;
    current.sourceHeight = built.sourceHeight;
    current.sourceWidth = built.sourceWidth;
    current.visual = built.visual;
    current.width = sizeX;
    current.rotationLayer.pivot.set(
      built.sourceWidth / 2,
      built.sourceHeight / 2,
    );
    current.rotationLayer.position.set(
      built.sourceWidth / 2,
      built.sourceHeight / 2,
    );
    current.rotationLayer.addChild(built.visual);

    // Native crossfade: `_SlotSetCharInternal` runs the outgoing sprite's
    // fade-out immediately, while the incoming sprite's afrom->ato tween (the
    // same tween `applyCharacterSlotOpacity` drives through contentAlpha) is
    // the crossfade itself. Its length is `duration` -- `fadetime` is not a
    // charslot key. The incoming visual therefore starts at the current
    // contentAlpha (set to `afrom` right after by the caller) and only the
    // outgoing visual gets a dedicated fade here.
    const fadeMs = Math.max(0, Math.round(input.replaceFadeMs ?? 0));
    built.visual.alpha = clamp01(current.contentAlpha);
    if (previousVisual && previousVisual !== built.visual && fadeMs > 0) {
      const sessionId = ++current.replaceFadeSessionId;
      const previousAlpha = previousVisual.alpha;
      current.outgoingVisual = previousVisual;
      void this.tween(
        fadeMs,
        (progress) => {
          if (
            !this.isActiveCharacterState(current) ||
            current.replaceFadeSessionId !== sessionId
          )
            return;
          previousVisual.alpha = previousAlpha * (1 - progress);
        },
        () => {
          if (
            !this.isActiveCharacterState(current) ||
            current.replaceFadeSessionId !== sessionId
          )
            return;
          if (current.outgoingVisual === previousVisual)
            current.outgoingVisual = null;
          this.discardCharacterVisual(previousVisual);
        },
      );
    } else if (previousVisual && previousVisual !== built.visual) {
      this.discardCharacterVisual(previousVisual);
    }

    return current;
  }

  /**
   * Web approximation of the greenscreen black band. Native feeds bstart/bend
   * through `CharacterParam` into `AVGCharacterSlot._LoadImage` (0x183eb4f60)
   * -> `AVGCharacterSpriteHub._SetImage` -> `AlphaSplitImageHolder.SetSprite`
   * (0x183ec5e60), which writes the material floats as
   * `_BlackStart = 1 - bstart` and `_BlackEnd = 1 - bend` -- the same 1-x the
   * dead `charslotmask` channel (`SlotChangeMask`) tweens, so both channels
   * hand the shader identical values and one mapping serves both. The band
   * geometry itself lives in the greenscreen shader, which the DLL cannot
   * show; this static top-down gradient was matched against the client
   * visually and is the approximation.
   */
  private applyCharacterBlackGradient(
    content: Container,
    contentSprites: Sprite[],
    width: number,
    height: number,
    blackStart = Number.NaN,
    blackEnd = Number.NaN,
  ): void {
    if (!Number.isFinite(blackStart) || !Number.isFinite(blackEnd)) return;

    const start = Math.max(0, Math.min(1, blackStart ?? 0));
    const end = Math.max(0, Math.min(1, blackEnd ?? 0));
    if (end <= 0) return;

    const startY = height * Math.min(start, end);
    const endY = height * Math.max(start, end);
    const texture = this.bakeDarkenedCharacterTexture(
      contentSprites,
      width,
      height,
      startY,
      endY,
    );
    if (!texture) return;

    // Native applies the gradient inside the character shader
    // (`avgCharSplitShader` material floats `_BlackStart`/`_BlackEnd` on the
    // sprite's own material, AlphaSplitImageHolder.SetSprite), darkening the
    // texture BEFORE the vertex color multiplies it (fade-in alpha, focus
    // dim tint). The web port must bake the darkening into the texture
    // itself: an overlay/mask sibling cannot reproduce that order, because
    // Pixi multiplies container alpha into each child before the children
    // composite, yielding dst * (1 - a * p) instead of dst * (1 - a) * p --
    // the shading would fade in with the character and only look right at
    // the end of the fade.
    for (const sprite of contentSprites) sprite.removeFromParent();
    const darkened = new Sprite(texture);
    darkened.anchor.set(0, 0);
    content.addChild(darkened);
  }

  /**
   * Flattens a `face_overlay` group (body + expression face) into a single
   * texture. Native renders both through one `avgCharSplitShader` material
   * (`AlphaSplitImageHolder.SetSprite` 0x183ec5e60: the body is the Image's
   * own sprite, the face rides the same material's `_HGDynamicTex` with the
   * `faceOffset` UV transform), so the face is composited *inside* the shader
   * before the vertex colour multiplies the result. Keeping two stacked Pixi
   * sprites instead would fade each layer with its own copy of the alpha
   * (`1-(1-p)^2` over the face) and double-apply `_BlackStart` shading, so the
   * composite is baked once per (base, face) pair and cached (bounded by
   * `FACE_OVERLAY_CACHE_LIMIT`, refcounted by the visuals drawing it).
   *
   * The face texture is stretched to `faceRect` as-is (no aspect-ratio
   * preservation): the CDN face PNG matches what native maps through the
   * `faceOffset` scale/offset, verified against the official client.
   */
  private bakeFaceOverlayTexture(
    baseKey: string,
    faceKey: string,
    baseTexture: Texture,
    faceTexture: Texture,
    faceRect: { x: number; y: number; w: number; h: number },
  ): Texture | null {
    const cacheKey = faceOverlayCacheKey(baseKey, faceKey);
    const cached = this.faceOverlayTextures.get(cacheKey);
    if (cached) {
      // Refresh recency: the Map's insertion order is the eviction order.
      this.faceOverlayTextures.delete(cacheKey);
      this.faceOverlayTextures.set(cacheKey, cached);
      return cached.texture;
    }

    const baseResource = baseTexture.source?.resource as
      CanvasImageSource | undefined;
    const faceResource = faceTexture.source?.resource as
      CanvasImageSource | undefined;
    // Mirrors bakeDarkenedCharacterTexture: a texture we cannot draw has to
    // fail the whole bake rather than silently drop the face or the body.
    if (!baseResource || !faceResource) return null;

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(baseTexture.width));
    canvas.height = Math.max(1, Math.round(baseTexture.height));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const baseFrame = baseTexture.frame;
    ctx.drawImage(
      baseResource,
      baseFrame.x,
      baseFrame.y,
      baseFrame.width,
      baseFrame.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    const faceFrame = faceTexture.frame;
    ctx.drawImage(
      faceResource,
      faceFrame.x,
      faceFrame.y,
      faceFrame.width,
      faceFrame.height,
      faceRect.x,
      faceRect.y,
      faceRect.w,
      faceRect.h,
    );

    const texture = new Texture({
      source: new CanvasSource({ resource: canvas }),
    });
    this.faceOverlayTextures.set(cacheKey, { refs: 0, texture });
    this.trimFaceOverlayCache();
    return texture;
  }

  /** Pins a cached bake for as long as `visual` is on stage. */
  private retainFaceOverlay(visual: Container, cacheKey: string): void {
    const entry = this.faceOverlayTextures.get(cacheKey);
    if (!entry) return;
    entry.refs += 1;
    this.faceOverlayVisualKeys.set(visual, cacheKey);
  }

  private releaseCharacterVisual(visual: Container): void {
    const cacheKey = this.faceOverlayVisualKeys.get(visual);
    if (cacheKey === undefined) return;
    this.faceOverlayVisualKeys.delete(visual);
    const entry = this.faceOverlayTextures.get(cacheKey);
    if (entry) entry.refs = Math.max(0, entry.refs - 1);
    this.trimFaceOverlayCache();
  }

  /** Takes a character visual off stage and drops its hold on its bake. */
  private discardCharacterVisual(visual: Container): void {
    visual.removeFromParent();
    this.releaseCharacterVisual(visual);
  }

  /**
   * Evicts the least recently baked entries nobody draws any more until the
   * cache is back under `FACE_OVERLAY_CACHE_LIMIT`. Entries still on stage
   * are skipped, so the cap is soft.
   */
  private trimFaceOverlayCache(): void {
    for (const [key, entry] of this.faceOverlayTextures) {
      if (this.faceOverlayTextures.size <= FACE_OVERLAY_CACHE_LIMIT) return;
      if (entry.refs > 0) continue;
      this.faceOverlayTextures.delete(key);
      entry.texture.destroy(true);
    }
  }

  private bakeDarkenedCharacterTexture(
    sprites: Sprite[],
    width: number,
    height: number,
    startY: number,
    endY: number,
  ): Texture | null {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    for (const sprite of sprites) {
      const resource = sprite.texture?.source?.resource as
        CanvasImageSource | undefined;
      // The caller drops the originals in favour of the baked texture, so a
      // sprite we cannot draw has to abort the whole bake -- skipping it would
      // silently erase that part of the character instead of just leaving it
      // undarkened.
      if (!resource) return null;
      const frame = sprite.texture.frame;
      ctx.drawImage(
        resource,
        frame.x,
        frame.y,
        frame.width,
        frame.height,
        sprite.x,
        sprite.y,
        sprite.width,
        sprite.height,
      );
    }

    // source-atop lerps the existing pixels toward the gradient color while
    // keeping their alpha: out = black * g + c * (1 - g), identical to the
    // native shader's lerp-to-black.
    ctx.globalCompositeOperation = "source-atop";
    const overlayHeight = Math.max(1, endY);
    const gradient = ctx.createLinearGradient(0, 0, 0, overlayHeight);
    gradient.addColorStop(0, "rgba(0, 0, 0, 1)");
    gradient.addColorStop(
      Math.min(1, startY / overlayHeight),
      "rgba(0, 0, 0, 1)",
    );
    gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    return new Texture({ source: new CanvasSource({ resource: canvas }) });
  }

  private applyCharacterSlotFocus(focusSlots: string[]): void {
    // Native focus is a full re-resolve on every charslot:
    // `_ProcessFocusArray` (0x183e50b30) clears all four flags, then lights
    // only the listed slots; unrecognized values light nothing. Brightness
    // changes are tweens in the Sequence (SetFocus), applied instantly here.
    const active = new Set(
      focusSlots.map((value) => this.normalizeCharacterSlot(value)),
    );

    for (const [slotKey, state] of this.characterSlots.entries()) {
      state.focusBrightness = active.has(slotKey) ? 1 : 0.5;
      this.updateCharacterOpacity(state);
    }
  }

  private applyCharacterSlotOpacity(
    state: CharacterRenderState,
    input: CharacterSlotInput,
    durationMs: number,
  ): void {
    const hasFrom = isFiniteNumber(input.alphaFrom);
    const hasTo = isFiniteNumber(input.alphaTo);
    if (!hasFrom && !hasTo) return;

    // `contentAlpha` mirrors the fore Image's vertex alpha, which native lets
    // overshoot below 0 (a nameless `afrom` fades toward ato = -1); it is only
    // clamped at the write in updateCharacterOpacity, so a resumed fade starts
    // from the visible value.
    const fromAlpha = hasFrom ? input.alphaFrom! : clamp01(state.contentAlpha);
    const toAlpha = hasTo ? input.alphaTo! : fromAlpha;
    state.contentAlpha = fromAlpha;
    this.updateCharacterOpacity(state);

    if (durationMs <= 0 || fromAlpha === toAlpha) {
      state.contentAlpha = toAlpha;
      this.updateCharacterOpacity(state);
      return;
    }

    const sessionId = ++state.opacitySessionId;
    void this.tween(
      durationMs,
      (progress) => {
        if (
          !this.isActiveCharacterState(state) ||
          state.opacitySessionId !== sessionId
        )
          return;
        state.contentAlpha = fromAlpha + (toAlpha - fromAlpha) * progress;
        this.updateCharacterOpacity(state);
      },
      () => {
        if (
          !this.isActiveCharacterState(state) ||
          state.opacitySessionId !== sessionId
        )
          return;
        state.contentAlpha = toAlpha;
        this.updateCharacterOpacity(state);
      },
    );
  }

  /**
   * Port of `CharacterPanel._GenPosition`, which returns the slide-in start as
   * an `_offset.localPosition` -- a slot-local delta that `SetCharPos(0, 0,
   * duration)` then tweens back to zero, not an absolute screen position.
   *
   * ```
   * v = slot == LEFT ? 200 : slot == RIGHT ? -200 : 0
   * "left"  -> (v - 1152, 0)   "right" -> (v + 1152, 0)
   * "up"    -> (0, 1072)       "down"  -> (0, -1072)
   * ```
   *
   * The LEFT +200 / RIGHT -200 term cancels each slot's own resting offset
   * from the panel centre (`slotBaseX` is 440/640/840, i.e. 200 apart), so
   * every slot starts a horizontal enter at the same absolute off-screen x.
   * Unity y is up while the renderer is y-down, hence the flipped vertical
   * constants.
   */
  private enterOffset(
    slot: string,
    direction?: CharacterSlotInput["enterFrom"],
    position?: { x: number; y: number },
  ): {
    x: number;
    y: number;
  } {
    // Explicit xpos/ypos replace `_GenPosition` wholesale -- slot
    // compensation included -- and native only applies the result for a legal
    // `enter`, with both coordinates present.
    if (direction && position) return position;
    const slotCompensation = SLOT_ENTER_COMPENSATION[slot] ?? 0;
    switch (direction) {
      case "left": {
        return { x: slotCompensation - 1152, y: 0 };
      }
      case "right": {
        return { x: slotCompensation + 1152, y: 0 };
      }
      case "up": {
        return { x: 0, y: -1072 };
      }
      case "down": {
        return { x: 0, y: 1072 };
      }
      default: {
        return { x: 0, y: 0 };
      }
    }
  }

  /**
   * Native `_ProcessSlot` raises MIDDLE via SetAsLastSibling, then the focused
   * non-middle slot (ECharSlot LEFT = 1 / RIGHT = 2). With the native
   * processing order MIDDLE -> LEFT -> RIGHT the resulting bottom-to-top
   * sibling order is: focus 1 -> [r, m, l], focus 2 -> [l, m, r],
   * otherwise -> [l, r, m].
   *
   * Raising each tracked root to the top in that order also keeps roots that
   * are mid cross-fade below the live ones: they are no longer tracked slots,
   * so nothing lifts them back up. Computing fixed target indices instead
   * would not -- `setChildIndex` splices, so each move shifts the indices the
   * later moves were computed against, and an outgoing root ends up wedged
   * between two live slots.
   */
  private applyCharacterZOrder(focus: number): void {
    const ufocus = focus >>> 0;
    let order = ["l", "r", "m"];
    if (ufocus === 1) order = ["r", "m", "l"];
    else if (ufocus === 2) order = ["l", "m", "r"];
    for (const slot of order) {
      const root = this.characterSlots.get(slot)?.root;
      if (!root || root.parent !== this.charLayer) continue;
      this.charLayer.setChildIndex(root, this.charLayer.children.length - 1);
    }
  }

  private async fadeOutAndRemove(
    container: Container,
    durationMs: number,
    done?: () => void,
  ): Promise<void> {
    const startAlpha = container.alpha;
    await this.tween(
      durationMs,
      (progress) => {
        container.alpha = startAlpha * (1 - progress);
      },
      () => {
        container.removeFromParent();
        done?.();
      },
    );
  }

  private updateCharacterState(state: CharacterRenderState): void {
    state.motionLayer.x = state.actionX + state.zoomShiftX + state.shakeOffsetX;
    state.motionLayer.y =
      state.shakeOffsetY - state.actionY - state.zoomShiftY - state.jumpOffsetY;
    state.motionLayer.scale.set(
      state.baseScaleX * state.scaleX,
      state.baseScaleY * state.scaleY,
    );
    state.rotationLayer.rotation = (state.rotationDeg * Math.PI) / 180;
    this.updateCharacterOpacity(state);
  }

  private updateCharacterOpacity(state: CharacterRenderState): void {
    const channel = Math.round(0xff * clamp01(state.focusBrightness));
    state.visual.tint = (channel << 16) | (channel << 8) | channel;
    // Unity's Color -> Color32 conversion clamps the vertex alpha, so a tween
    // toward a negative `ato` reads as fully transparent once it crosses 0.
    state.visual.alpha = clamp01(state.contentAlpha);
  }

  private disposeCharacterState(state: CharacterRenderState): void {
    state.opacitySessionId += 1;
    state.replaceFadeSessionId += 1;
    state.transformSessionId += 1;
    state.zoomSessionId += 1;
    state.jumpSessionId += 1;
    this.stopRotateAction(state);
    this.stopShakeAction(state);
    state.root.removeFromParent();
    // The current visual and any still-fading previous one both hold a
    // face-overlay bake.
    for (const child of state.rotationLayer.children)
      this.releaseCharacterVisual(child as Container);
    state.outgoingVisual = null;
  }

  private normalizeCharacterSlot(slot?: string): string {
    switch ((slot ?? "").trim().toLowerCase()) {
      case "char_left":
      case "l":
      case "left": {
        return "l";
      }
      case "c":
      case "center":
      case "char_middle":
      case "m":
      case "middle": {
        return "m";
      }
      case "char_right":
      case "r":
      case "right": {
        return "r";
      }
      default: {
        return "m";
      }
    }
  }

  private isCharacterSlotCommand(input: CharacterSlotInput): boolean {
    return (
      input.focusMode !== undefined ||
      input.focusSlots !== undefined ||
      input.positionFrom !== undefined ||
      input.positionTo !== undefined ||
      input.alphaFrom !== undefined ||
      input.alphaTo !== undefined ||
      input.action !== undefined ||
      input.scaleX !== undefined ||
      input.scaleY !== undefined ||
      input.posZoom !== undefined ||
      input.replaceFadeMs !== undefined ||
      input.power !== undefined ||
      input.randomness !== undefined ||
      input.stop !== undefined ||
      input.times !== undefined ||
      input.characterKey === undefined ||
      input.expression === undefined
    );
  }

  private stopShakeAction(state: CharacterRenderState): void {
    state.shakeSessionId += 1;
    state.shakeOffsetX = 0;
    state.shakeOffsetY = 0;
    this.updateCharacterState(state);
  }

  private stopRotateAction(state: CharacterRenderState): void {
    state.rotateSessionId += 1;
    if (state.rotateTimeout) {
      clearTimeout(state.rotateTimeout);
      state.rotateTimeout = null;
    }
    state.rotationDeg = 0;
    this.updateCharacterState(state);
  }

  private disposeCurtainState(state: CurtainRenderState): void {
    state.tweenSessionId += 1;
    state.graphic.removeFromParent();
    state.graphic.destroy();
  }

  private removeCurtainState(
    direction: number,
    state: CurtainRenderState,
  ): void {
    if (this.curtains.get(direction) !== state) return;
    this.curtains.delete(direction);
    this.disposeCurtainState(state);
  }

  /**
   * Native port: AVGCharacterSlot.MoveChar (0x184029e30 on 2.7.81). Relative
   * displacement (cur + (x, y)); a zero fadeTime applies it instantly and
   * returns a null tween, which is why `block` only waits when there is a
   * tween to wait for. The tween runs `DOLocalMove(...).SetEase(Ease.OutCubic)`,
   * so the move branch eases with OutCubic; the jump branch passes its own
   * per-axis curves (see `runCharacterAction`'s jump case).
   */
  private startMoveAction(
    state: CharacterRenderState,
    xOffset: number,
    yOffset: number,
    durationMs: number,
    ease: EaseCurve,
    easeY: EaseCurve = ease,
  ): Promise<void> {
    const fromX = state.actionX;
    const fromY = state.actionY;
    const toX = fromX + xOffset;
    const toY = fromY + yOffset;
    const sessionId = ++state.transformSessionId;

    return this.tween(
      durationMs,
      (progress) => {
        if (!this.isActiveCharacterState(state, sessionId)) return;
        state.actionX = fromX + (toX - fromX) * ease(progress);
        state.actionY = fromY + (toY - fromY) * easeY(progress);
        this.updateCharacterState(state);
      },
      () => {
        if (!this.isActiveCharacterState(state, sessionId)) return;
        state.actionX = toX;
        state.actionY = toY;
        this.updateCharacterState(state);
      },
    );
  }

  private async runMoveAction(
    state: CharacterRenderState,
    xOffset: number,
    yOffset: number,
    durationMs: number,
    block: boolean,
  ): Promise<void> {
    const run = this.startMoveAction(
      state,
      xOffset,
      yOffset,
      durationMs,
      easeOutCubic,
    );
    if (block && durationMs > 0) await run;
  }

  /**
   * Native port: AVGCharacterSlot.CharZoom (0x183EB2B50) as reached from
   * `_ExecuteCharacterAction`. xpos/ypos are the [0,1] pivot of the character
   * image (runtime already rejected out-of-range values, which native turns
   * into a whole-command no-op); an absent pivot keeps the (0.5,0.5) center.
   * The pivot point stays where the resting rect was centred while localScale
   * tweens, so the motion layer gets the same shift compensation the
   * `charslot` zoom uses (see `characterSlotZoom`):
   *   (0.5 - pivot) * scale * size - (scale - 1) * size / 2
   * (shiftY is stored in Unity's y-up and negated on apply). Zoom runs on its
   * own session so it supersedes a running zoom without touching a move.
   */
  private async runScaleAction(
    state: CharacterRenderState,
    scaleX: number | undefined,
    scaleY: number | undefined,
    pivot: { x: number; y: number } | undefined,
    durationMs: number,
    block: boolean,
  ): Promise<void> {
    const fromScaleX = state.scaleX;
    const fromScaleY = state.scaleY;
    const fromShiftX = state.zoomShiftX;
    const fromShiftY = state.zoomShiftY;
    const toScaleX = isFiniteNumber(scaleX) ? scaleX : state.scaleX;
    const toScaleY = isFiniteNumber(scaleY) ? scaleY : state.scaleY;
    const pivotX = pivot?.x ?? 0.5;
    const pivotY = pivot?.y ?? 0.5;
    const toShiftX =
      (0.5 - pivotX) * toScaleX * state.width -
      ((toScaleX - 1) * state.width) / 2;
    const toShiftY =
      (0.5 - pivotY) * toScaleY * state.height +
      ((toScaleY - 1) * state.height) / 2;
    const sessionId = ++state.zoomSessionId;

    const run = this.tween(
      durationMs,
      (progress) => {
        if (
          !this.isActiveCharacterState(state) ||
          state.zoomSessionId !== sessionId
        )
          return;
        state.scaleX = fromScaleX + (toScaleX - fromScaleX) * progress;
        state.scaleY = fromScaleY + (toScaleY - fromScaleY) * progress;
        state.zoomShiftX = fromShiftX + (toShiftX - fromShiftX) * progress;
        state.zoomShiftY = fromShiftY + (toShiftY - fromShiftY) * progress;
        this.updateCharacterState(state);
      },
      () => {
        if (
          !this.isActiveCharacterState(state) ||
          state.zoomSessionId !== sessionId
        )
          return;
        state.scaleX = toScaleX;
        state.scaleY = toScaleY;
        state.zoomShiftX = toShiftX;
        state.zoomShiftY = toShiftY;
        this.updateCharacterState(state);
      },
    );

    // Native CharZoom has no instant branch: DOTween.To with a zero duration
    // still returns a tween (it completes on the first update), so a blocking
    // zoom stays blocking even at durationMs 0 — unlike move/exit whose
    // instant branch returns a null tween and never blocks.
    if (block) await run;
  }

  /**
   * Native port: CharacterPanel._ExecuteCharacterExit (0x183fd6f20 on 2.7.81)
   * → _GenExitPosition (0x183fd8980) → AVGCharacterSlot.SetCharPos
   * (0x18402a1e0). The exit table below is verbatim native: displacements
   * from each slot's rest anchor (l=440/m=640/r=840 in 1280x720 story space;
   * actionX/actionY are exactly that displacement). Both coordinates are set
   * absolutely, so a horizontal exit also snaps the vertical drift back to 0.
   * An unknown direction resolves to (0,0) — the rest anchor. An explicit
   * xpos/ypos pair replaces the table lookup but lands in the same
   * `_offset.localPosition` space. SetCharPos runs
   * `DOLocalMove(...).SetEase(Ease.OutCubic)` (same tween as the character
   * enter slide), so the exit path eases with OutCubic too.
   */
  private async runExitAction(
    state: CharacterRenderState,
    durationMs: number,
    direction: CharacterActionInput["direction"],
    position: { x: number; y: number } | undefined,
    block: boolean,
  ): Promise<void> {
    const exitTable: Record<string, Record<string, [number, number]>> = {
      l: { left: [-952, 0], right: [1352, 0] },
      m: { left: [-1152, 0], right: [1152, 0] },
      r: { left: [-1352, 0], right: [952, 0] },
    };
    const verticalTable: Record<string, [number, number]> = {
      down: [0, -1072],
      up: [0, 1072],
    };

    let toX: number;
    let toY: number;
    if (position) {
      // Both xpos & ypos present: SetCharPos(xpos, ypos) writes them straight
      // into `_offset.localPosition` (0x18402a1e0), the same slot-anchor
      // displacement the table rows use (actionY is up-positive).
      toX = position.x;
      toY = position.y;
    } else if (direction === "up" || direction === "down") {
      [toX, toY] = verticalTable[direction];
    } else if (direction === "left" || direction === "right") {
      [toX, toY] = (exitTable[state.slot] ?? exitTable.m)[direction] ?? [0, 0];
    } else {
      [toX, toY] = [0, 0];
    }

    const fromX = state.actionX;
    const fromY = state.actionY;
    const sessionId = ++state.transformSessionId;

    const run = this.tween(
      durationMs,
      (progress) => {
        if (!this.isActiveCharacterState(state, sessionId)) return;
        const eased = easeOutCubic(progress);
        state.actionX = fromX + (toX - fromX) * eased;
        state.actionY = fromY + (toY - fromY) * eased;
        this.updateCharacterState(state);
      },
      () => {
        if (!this.isActiveCharacterState(state, sessionId)) return;
        state.actionX = toX;
        state.actionY = toY;
        this.updateCharacterState(state);
      },
    );

    if (block && durationMs > 0) await run;
  }

  /**
   * Native port: AVGCharacterSlot.CharJump (0x1840294f0 on 2.7.81) →
   * DOTween DOLocalJump (0x184257a20), which is a Sequence of three tweens
   * that all start together and last fadeTime in total:
   * - x: `DOLocalMove`-style tween, `SetEase(Ease.Linear)`, cur.x → cur.x+xpos
   *   over the whole duration (the horizontal move of the jump);
   * - y: relative `+jumpPower` tween of duration/(2*times) with
   *   `SetEase(Ease.OutQuad).SetLoops(2*times, LoopType.Yoyo)` — each jump
   *   rises OutQuad to jumpPower in half a jump period and replays the same
   *   eased arc backwards on the way down (`times < 1` clamps to 1);
   * - z: Linear tween to the unchanged z (a no-op here).
   * This method drives only the arc; the x/y displacement is the move tween
   * started next to it. CharJump chains `SetEase(Ease.OutCubic)` on the
   * returned Sequence, and `Sequence.DoApplyTween` (0x1842525c0) evaluates
   * that ease on the sequence position before ApplyInternalCycle hands it to
   * the nested tweens, so the yoyo samples OutCubic-warped time (the bounces
   * bunch up early). `_GenCharslotJump` reaches the same CharJump, so the
   * charslot jump shares this arc.
   */
  private startJumpAction(
    state: CharacterRenderState,
    power: number,
    times: number,
    durationMs: number,
  ): Promise<void> {
    const sessionId = ++state.jumpSessionId;
    state.jumpOffsetY = 0;
    this.updateCharacterState(state);

    // Native DOLocalJump always creates the y arc tween; a power of 0 is a
    // flat arc (visual no-op) and a negative power bounces downward, so only
    // a non-positive duration skips it (the runtime degenerates that case to
    // a move anyway — this is a defensive guard).
    if (durationMs <= 0) return Promise.resolve();

    const cycles = Math.max(1, times);
    const halfCycleProgress = 1 / (cycles * 2);
    return this.tween(
      durationMs,
      (progress) => {
        if (
          !this.isActiveCharacterState(state) ||
          state.jumpSessionId !== sessionId
        )
          return;
        // Position inside the 2*times yoyo cycle list; even cycles play the
        // OutQuad arc forward, odd cycles replay it backwards.
        const cyclePos =
          Math.min(easeOutCubic(progress), 1) / halfCycleProgress;
        const cycle = Math.floor(cyclePos);
        const frac = cyclePos - cycle;
        const arc = cycle % 2 === 0 ? easeOutQuad(frac) : easeOutQuad(1 - frac);
        state.jumpOffsetY = power * arc;
        this.updateCharacterState(state);
      },
      () => {
        if (
          !this.isActiveCharacterState(state) ||
          state.jumpSessionId !== sessionId
        )
          return;
        state.jumpOffsetY = 0;
        this.updateCharacterState(state);
      },
    );
  }

  /**
   * Native port: AVGCharacterSlot.CharShake (0x184029850 on 2.7.81) →
   * ShortcutExtensions.DOShakePosition (0x18425cda0) → DOTween.Shake
   * (0x18424e2d0) with `ignoreZAxis = false`, Full randomness mode and
   * fadeOut — the same waypoint walk `buildShakePath` ports for the camera
   * shake (vibrato = `times` per second, strength = power on both axes).
   * CharShake chains SetEase(OutCubic), but DOShakePosition marks the tween
   * `SpecialStartupMode.SetShake`, whose startup hook
   * (`SpecialPluginsUtils.SetShake` @ 0x18427f590) forces `easeType =
   * Linear` after SetPunch; Vector3ArrayPlugin then eases each segment on its
   * own (0x184282330), so every segment interpolates linearly.
   * X keeps Unity's right-positive axis; Y is stored negated because
   * motionLayer consumes screen-down-positive y.
   */
  private startShakeAction(
    state: CharacterRenderState,
    input: CharacterActionInput,
  ): Promise<void> {
    this.stopShakeAction(state);
    // `stop` is a web extension: native has no such key, so it only needs to
    // cancel above without producing a tween.
    if (input.stop) return Promise.resolve();

    const sessionId = state.shakeSessionId;
    const durationMs = Math.max(input.durationMs, 0);
    // Random.Range(-r, r) with a negative r still spans [-|r|, |r|].
    const path = buildShakePath(durationMs, {
      fadeOut: true,
      randomness: Math.abs(input.randomness),
      vibrato: input.times,
      xStrength: input.power,
      yStrength: input.power,
    });

    return this.tween(
      durationMs,
      (progress) => {
        if (
          !this.isActiveCharacterState(state) ||
          state.shakeSessionId !== sessionId
        )
          return;
        const offset = sampleShakePath(path, progress);
        state.shakeOffsetX = offset.x;
        state.shakeOffsetY = -offset.y;
        this.updateCharacterState(state);
      },
      () => {
        if (
          !this.isActiveCharacterState(state) ||
          state.shakeSessionId !== sessionId
        )
          return;
        state.shakeOffsetX = 0;
        state.shakeOffsetY = 0;
        this.updateCharacterState(state);
      },
    );
  }

  private isActiveCharacterState(
    state: CharacterRenderState,
    transformSessionId?: number,
  ): boolean {
    if (!this.app) return false;
    const current = this.characterSlots.get(state.slot);
    if (current !== state) return false;
    if (
      transformSessionId !== undefined &&
      state.transformSessionId !== transformSessionId
    )
      return false;
    return true;
  }

  private isActiveCurtainState(
    direction: number,
    state: CurtainRenderState,
    tweenSessionId?: number,
  ): boolean {
    if (!this.app) return false;
    const current = this.curtains.get(direction);
    if (current !== state) return false;
    if (tweenSessionId !== undefined && state.tweenSessionId !== tweenSessionId)
      return false;
    return true;
  }

  private bumpStickerSessions(id: string): void {
    this.stickerTypingSessionIds.set(
      id,
      (this.stickerTypingSessionIds.get(id) ?? 0) + 1,
    );
    this.stickerFadeSessionIds.set(
      id,
      (this.stickerFadeSessionIds.get(id) ?? 0) + 1,
    );
    // Native: re-showing the same id goes through RenderSticker._ResetView
    // (0x183ed43b0), which kills the view's running sticker-tween Sequence
    // (TweenExtensions.Kill(m_seq, false)). Bumping this session makes any
    // in-flight sticker tween stop applying -- ForceCommandEnd does NOT do
    // this in native (it only touches m_currentSticker, which stickertween
    // never writes), so skip/force-end deliberately leaves the tween running.
    this.stickerTweenSessionIds.set(
      id,
      (this.stickerTweenSessionIds.get(id) ?? 0) + 1,
    );
  }

  private clearTimerInterval(): void {
    if (!this.timerStickerInterval) return;
    clearInterval(this.timerStickerInterval);
    this.timerStickerInterval = null;
  }

  private stopCameraShake(sessionId?: number): void {
    if (sessionId !== undefined && sessionId !== this.cameraShakeSessionId)
      return;

    this.cameraShakeSessionId += 1;
    this.sceneLayer.position.set(0, 0);
    this.cameraShakeWaitResolve?.();
    this.cameraShakeWaitResolve = null;
  }

  private readBlockerColor(): {
    a: number;
    b: number;
    g: number;
    r: number;
  } {
    // Native `_ExecuteBlocker` reads the current `Color` (float32) for the
    // omitted `*From` channels (2.7.61 VA 0x183e30318). Returning the raw
    // float mirror keeps full precision and preserves 0-255-scale values
    // across commands, instead of re-reading the quantized 8-bit tint.
    return { ...this.blockerColor };
  }

  private writeBlockerColor(
    blocker: Sprite,
    color: { a: number; b: number; g: number; r: number },
  ): void {
    // Native stores the raw GetFloat values straight into `Color` (float32,
    // components may exceed 1) and DOColor tweens that raw color; only the GPU
    // clamps at render time. So a 0-255 endpoint (r=255) saturates the channel
    // for essentially the whole tween -- the visible fade is the alpha ramp.
    // Mirror that: keep the raw floats, and clamp just this 8-bit tint write.
    // (The old per-value `value > 1 ? value / 255 : value` heuristic re-scaled
    // every intermediate frame, halving mid-fade brightness.)
    this.blockerColor = { a: color.a, b: color.b, g: color.g, r: color.r };
    blocker.tint =
      (tintChannel(color.r) << 16) |
      (tintChannel(color.g) << 8) |
      tintChannel(color.b);
    if (this.blockerSlideFilter) {
      // SlideMask replaces Image.alpha with the per-pixel wipe: the sprite
      // stays opaque so the filter input keeps the untinted-dimmed rgb, and
      // the raw alpha drives the shader's reveal progress directly.
      this.blockerSlideFilter.alpha = color.a;
      blocker.alpha = 1;
    } else {
      blocker.alpha = clamp01(color.a);
    }
    blocker.visible = color.a > 0;
  }

  private createOverlayTextStyle(fontSize: number, widthPx: number): TextStyle {
    return new TextStyle({
      breakWords: true,
      dropShadow: {
        alpha: 0.9,
        angle: Math.PI / 4,
        blur: 4,
        color: "#404040",
        distance: 0,
      },
      fill: "#ffffff",
      // panel_subtitle and the sticker / timer prefabs use the dialog font too.
      fontFamily: [DIALOG_FONT_FAMILY, "sans-serif"],
      fontSize,
      fontWeight: DIALOG_FONT_WEIGHT,
      whiteSpace: "pre-line",
      wordWrap: true,
      wordWrapWidth: widthPx,
    });
  }

  private ensureStickerText(id: string): Text {
    const existing = this.stickerTexts.get(id);
    if (existing) return existing;

    const sticker = new Text({
      style: this.createOverlayTextStyle(18, 675),
      text: "",
    });
    sticker.visible = false;
    this.stickerTexts.set(id, sticker);
    this.uiLayer.addChild(sticker);
    return sticker;
  }

  private ensureTimerStickerText(): Text {
    if (!this.timerStickerText) {
      this.timerStickerText = new Text({
        style: this.createOverlayTextStyle(18, 220),
        text: "",
      });
      this.timerStickerText.visible = false;
      this.uiLayer.addChild(this.timerStickerText);
    }
    return this.timerStickerText;
  }

  private formatTimer(totalSeconds: number): string {
    // `_TimerTick` formats `TimeSpan.FromMilliseconds(...)`; `TimeSpan.Hours`
    // wraps at 24, so time >= 86400 displays modulo a day (100000 -> 03:46:40).
    const hours = Math.floor(totalSeconds / 3600) % 24;
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return [hours, minutes, seconds]
      .map((value) => value.toString().padStart(2, "0"))
      .join(":");
  }

  private layoutSubtitle(
    subtitle: Text,
    baseX: number,
    widthPx: number,
    alignment: SubtitleInput["alignment"],
  ): void {
    const renderedWidth = Math.min(subtitle.width, widthPx);
    switch (alignment) {
      case "center": {
        subtitle.x = baseX + Math.max(widthPx - renderedWidth, 0) / 2;
        return;
      }
      case "right": {
        subtitle.x = baseX + Math.max(widthPx - renderedWidth, 0);
        return;
      }
      default: {
        subtitle.x = baseX;
      }
    }
  }

  private async runSubtitleTyping(
    sessionId: number,
    subtitle: Text,
    input: {
      alignment: SubtitleInput["alignment"];
      baseX: number;
      delayMs: number;
      newChars: RichChar[];
      onTypingComplete?: () => void;
      prevChars: RichChar[];
      widthPx: number;
    },
  ): Promise<void> {
    for (let index = 0; index < input.newChars.length; index += 1) {
      if (!this.app || this.subtitleTypingSessionId !== sessionId) return;

      await new Promise((resolve) => setTimeout(resolve, input.delayMs));

      if (!this.app || this.subtitleTypingSessionId !== sessionId) return;

      // The transparent tail keeps the full-text line layout fixed while the
      // visible prefix grows (native BeginText hidden-string port).
      subtitle.text = richCharsToTaggedText([
        ...input.prevChars,
        ...input.newChars.slice(0, index + 1),
        ...subtitleHiddenTail(input.newChars.slice(index + 1)),
      ]);
      this.layoutSubtitle(
        subtitle,
        input.baseX,
        input.widthPx,
        input.alignment,
      );
    }

    if (this.subtitleTypingSessionId === sessionId) {
      this.subtitleTypingTarget = null;
      // Native `SubtitlePanel._OnTypeWriterEnd` (2.7.61 VA 0x183e94e80):
      // typing that finishes on its own raises the auto click; without this,
      // auto mode would sit on the subtitle waiting for a manual click.
      input.onTypingComplete?.();
    }
  }

  private async runStickerTyping(
    id: string,
    sessionId: number,
    sticker: Text,
    input: {
      alignment: StickerInput["alignment"];
      baseX: number;
      delayMs: number;
      prevChars: RichChar[];
      newChars: RichChar[];
      onTypingComplete?: () => void;
      widthPx: number;
    },
  ): Promise<void> {
    for (let index = 0; index < input.newChars.length; index += 1) {
      if (
        !this.app ||
        (this.stickerTypingSessionIds.get(id) ?? 0) !== sessionId
      )
        return;

      await new Promise((resolve) => setTimeout(resolve, input.delayMs));

      if (
        !this.app ||
        (this.stickerTypingSessionIds.get(id) ?? 0) !== sessionId
      )
        return;

      sticker.text = richCharsToTaggedText([
        ...input.prevChars,
        ...input.newChars.slice(0, index + 1),
      ]);
      this.layoutSubtitle(sticker, input.baseX, input.widthPx, input.alignment);
    }

    if ((this.stickerTypingSessionIds.get(id) ?? 0) === sessionId) {
      this.stickerTypingTargets.delete(id);
      // Native `AVGStickerTextView._OnTypeWriterEnd` -> m_OnTypeEnd: only
      // typing that ends on its own raises the auto click here.
      input.onTypingComplete?.();
    }
  }

  /**
   * The sizeDelta `Image.SetNativeSize()` writes in the CN client:
   * `sprite.rect / ppu * 100` (canvas.referencePixelsPerUnit = 100), plus it
   * collapses the prefab's stretch anchors so sizeDelta becomes the real
   * rect. AVG background art ships a tuned per-asset ppu (80 / 100 /
   * 68.2464 / ...), so this is generally NOT the texture's pixel size --
   * e.g. bg_cher_1 (1024x576, ppu 68.2464) natively renders 1500.44x844,
   * a 17% centered overscan. Web PNGs are texture-sized with no ppu
   * metadata, so the per-key ppu comes from the `avg/background.json`
   * sidecar (context.backgroundPpuMap). For keys missing from the sidecar
   * every 16:9 background added since 2023 ships a ppu tuned to exactly the
   * 1280x720 canvas, so a 16:9 texture maps to that and anything else keeps
   * its texture size.
   */
  private nativeBackgroundRect(
    key: string,
    texture: Texture,
  ): readonly [number, number] {
    const width = Math.max(1, texture.width);
    const height = Math.max(1, texture.height);
    // Sidecar keys are the lowercase bundle container names (= web asset
    // URLs); normalize defensively in case a story references a key with
    // different casing than the bundle.
    const ppu = this.context.backgroundPpuMap?.[key.toLowerCase()];
    if (ppu !== undefined && ppu > 0) {
      return [(width / ppu) * 100, (height / ppu) * 100];
    }
    if (Math.abs(width / height - STORY_WIDTH / STORY_HEIGHT) < 0.01) {
      return [STORY_WIDTH, STORY_HEIGHT];
    }
    return [width, height];
  }

  /**
   * Native port: `AVGImagePanel._LoadImage` sizes the Image in three steps.
   * (1) `Image.SetNativeSize()` resets sizeDelta to the sprite's native
   * display rect (see nativeBackgroundRect -- confirmed called at
   * 0x183e58688 in build 2761 right after `image.sprite` is assigned).
   * (2) sizeDelta is multiplied by the `width`/`height` params
   * (GetOrDefault<float>(..., 1.0); mulss at 0x183e587b0/0x183e587b4).
   * (3) An optional SCREEN_ADAPT_FUNCTION_MAP entry maps that multiplied
   * rect against the 1280x720 reference -- the ratio checks read the
   * post-multiplier sizeDelta too. With screenadapt omitted the rect keeps
   * the multiplied native size, which still covers the canvas for ppu-tuned
   * art (most backgrounds) and reveals the backing color only around art
   * whose native rect is smaller than 1280x720 (e.g. 33_g4_srctheater).
   */
  private layoutImageForScreenAdapt(
    sprite: Sprite | TilingSprite,
    mode?: BackgroundInput["screenAdapt"],
    multipliers?: { height: number; width: number },
    nativeRect?: readonly [number, number],
  ): void {
    const nativeWidth = nativeRect?.[0] ?? Math.max(1, sprite.texture.width);
    const nativeHeight = nativeRect?.[1] ?? Math.max(1, sprite.texture.height);
    const sourceWidth = nativeWidth * (multipliers?.width ?? 1);
    const sourceHeight = nativeHeight * (multipliers?.height ?? 1);
    let width = sourceWidth;
    let height = sourceHeight;

    if (mode === "fill") {
      width = STORY_WIDTH;
      height = STORY_HEIGHT;
    } else if (
      mode === "width" ||
      (mode === "showall" &&
        sourceWidth / sourceHeight > STORY_WIDTH / STORY_HEIGHT) ||
      (mode === "coverall" &&
        sourceWidth / sourceHeight < STORY_WIDTH / STORY_HEIGHT)
    ) {
      width = STORY_WIDTH;
      height = (sourceHeight * STORY_WIDTH) / sourceWidth;
    } else if (mode === "height" || mode === "showall" || mode === "coverall") {
      height = STORY_HEIGHT;
      width = (sourceWidth * STORY_HEIGHT) / sourceHeight;
    }

    sprite.width = width;
    sprite.height = height;
    sprite.position.set(0, 0);
  }

  /**
   * `Image.GenerateTiledSprite` lays tiles out from the rect's lower-left
   * corner and clips the last row at the top edge, while a TilingSprite starts
   * at its top-left corner. Shifting the tile origin by the leftover height
   * puts a tile boundary on the bottom edge like Unity. A no-op whenever the
   * rect is a whole number of tiles high, which covers every tiled story line
   * today (none passes screenadapt/width/height, so the rect is one tile).
   */
  private alignTilesToLowerLeft(sprite: TilingSprite): void {
    const tileHeight = sprite.texture.height * sprite.tileScale.y;
    sprite.tilePosition.set(0, tileHeight > 0 ? sprite.height % tileHeight : 0);
  }

  private readCenteredTransform(root: Container): {
    scaleX: number;
    scaleY: number;
    x: number;
    y: number;
  } {
    return readRootTransform(root);
  }

  private applyCenteredTransform(
    root: Container,
    transform: { scaleX: number; scaleY: number; x: number; y: number },
  ): void {
    applyCenteredTransformToRoot(root, transform);
  }

  private isActiveBackground(root: Container, sessionId: number): boolean {
    return Boolean(
      this.app &&
      this.backgroundRoot === root &&
      this.backgroundTweenSessionId === sessionId &&
      root.parent === this.backgroundLayer,
    );
  }

  private isActiveLargeBackground(root: Container, sessionId: number): boolean {
    return Boolean(
      this.app &&
      this.largeBackgroundRoot === root &&
      this.largeBackgroundTweenSessionId === sessionId &&
      root.parent === this.gridBackgroundLayer,
    );
  }

  private isActiveLargeImage(root: Container, sessionId: number): boolean {
    return Boolean(
      this.app &&
      this.largeImageRoot === root &&
      this.largeImageTweenSessionId === sessionId &&
      root.parent === this.imageLayer,
    );
  }

  private buildGridBackgroundRoot(
    input: GridBackgroundInput,
    textures: Texture[],
  ): Container {
    return buildGridRoot(input, textures);
  }

  private async textureForImageKey(
    key: string,
    kind: StoryAssetFamily,
  ): Promise<Texture | null> {
    const rawUrl = resolveStoryAssetByKey(key, kind);
    if (!rawUrl) {
      this.onWarning?.(`missing ${kind}: ${key}`);
      return null;
    }

    return this.textureForUrl(rawUrl, kind, key);
  }

  private async textureForCharacterKey(key: string): Promise<Texture | null> {
    const rawUrl = resolveStoryCharacterAssetByKey(key);
    if (!rawUrl) {
      this.onWarning?.(`missing character: ${key}`);
      return null;
    }

    return this.textureForUrl(rawUrl, "character", key);
  }

  private async textureForInterlude(
    input: InterludeInput,
  ): Promise<Texture | null> {
    if (input.type !== 3) {
      // CutinController routes type 1 (uichar) through Cutin/Characters and
      // type 2 (bg) through AVG/Backgrounds; only type 3 reuses AVG characters.
      let kind: StoryAssetFamily = "image";
      if (input.type === 2) kind = "background";
      else if (input.type === 1) kind = "cutin";
      return this.textureForImageKey(input.name, kind);
    }

    const base = input.avatarCharacterKey;
    const expression = input.avatarExpression;
    if (!base || !expression) return null;
    const link = this.context.linkMap[base];
    const item = link?.array.find((entry) => entry.name === expression);
    if (!item) return null;
    if (item.group === -1 && item.image)
      return this.textureForCharacterKey(item.image);
    const group = item.group >= 0 ? link.groups[item.group] : undefined;
    if (group?.mode === "face_overlay")
      return this.textureForCharacterKey(group.base);
    return null;
  }

  private async textureForUrl(
    rawUrl: string,
    kind: string,
    key: string,
  ): Promise<Texture | null> {
    const url = resolveAssetUrl(rawUrl);
    try {
      const texture = await Assets.load<Texture>(url);
      return texture;
    } catch {
      this.onWarning?.(`failed ${kind}: ${key}`);
      return null;
    }
  }

  private tween(
    durationMs: number,
    step: (progress: number) => void,
    done?: () => void,
    options?: TweenRunOptions,
  ): Promise<void> {
    return this.tweenRunner.run(durationMs, step, done, options);
  }
}

export { DIALOG_FRAME_URL } from "../../assets";
